import React, { useEffect, useMemo, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import * as api from "./api";
import { Login, LoginLoading, SigninLinkRedeem, signinLinkTokenFromHash } from "./Login";
import logoUrl from "./assets/logo.png";
import { filterNav, resolveActiveSection, type NavItem } from "./sidebarPermissions";
import {
  interpretActionResult,
  type ActionKind,
  type ActionOutcome,
  type ApprovalEnvelope,
} from "./tenantActions";
import { filterTenants, filterAdminUsers } from "./tenantSearch";
import { logoFor, logoUrl as providerLogoUrl } from "./providerLogos";
import { rollupTone, rollupLabel, formatRate, formatUptime, sortRollup } from "./providerHealth";
import { splitLiveChats, eligibleReassignTargets } from "./liveChatFilters";
import {
  classifyFields,
  fieldsFromResult,
  visibilityTone,
  type ClassifiedField,
} from "./policySimulator";
import type {
  AIRun,
  AiCostRow,
  AiCostGroupBy,
  AdminAuth,
  AdminSettings,
  AuditEntry,
  BillingMetrics,
  ChatMessage,
  CustomerContext,
  EnvironmentInfo,
  HealthMetrics,
  HealthTimelinePoint,
  Integration,
  PlatformAlert,
  PlatformMetrics,
  QueueStat,
  ServiceHealth,
  Tenant,
  Ticket
} from "./types";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/*
  Ivorycom Admin Portal — production scaffold.
  All data is fetched from the backend identified by API_URL.
  Inline business data is intentionally absent — empty/loading/error states
  cover every section until the API is wired up.
*/

function Icon({ name, className = "", size = 18 }) {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, className };
  const icons = {
    menu: <svg {...common}><path d="M4 7h16"/><path d="M4 12h16"/><path d="M4 17h16"/></svg>,
    x: <svg {...common}><path d="m18 6-12 12"/><path d="m6 6 12 12"/></svg>,
    dashboard: <svg {...common}><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/></svg>,
    building: <svg {...common}><path d="M4 21V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v16"/><path d="M9 21v-4h3v4"/><path d="M8 7h1"/><path d="M12 7h1"/><path d="M8 11h1"/><path d="M12 11h1"/><path d="M3 21h18"/></svg>,
    users: <svg {...common}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/></svg>,
    card: <svg {...common}><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>,
    plug: <svg {...common}><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M6 8h12v3a6 6 0 0 1-12 0z"/></svg>,
    support: <svg {...common}><circle cx="12" cy="12" r="10"/><path d="M8 15a4 4 0 0 0 8 0"/><path d="M9 9h.01"/><path d="M15 9h.01"/></svg>,
    bot: <svg {...common}><rect x="5" y="8" width="14" height="10" rx="2"/><path d="M12 8V4"/><circle cx="9" cy="13" r="1"/><circle cx="15" cy="13" r="1"/><path d="M9 18v2"/><path d="M15 18v2"/></svg>,
    shield: <svg {...common}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/><path d="m9 12 2 2 4-4"/></svg>,
    activity: <svg {...common}><path d="M22 12h-4l-3 8L9 4l-3 8H2"/></svg>,
    megaphone: <svg {...common}><path d="m3 11 18-5v12L3 13z"/><path d="M11 14v5a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-6"/></svg>,
    settings: <svg {...common}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 .6 1.65 1.65 0 0 0-.38 1.05V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8.6 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-.6-1 1.65 1.65 0 0 0-1.05-.38H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 8.6a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-.6 1.65 1.65 0 0 0 .38-1.05V3a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15.4 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06A2 2 0 1 1 20.1 7.1l-.06.06A1.65 1.65 0 0 0 19.4 9c.14.33.36.62.6 1 .24.38.62.6 1.05.6H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51.4Z"/></svg>,
    search: <svg {...common}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
    bell: <svg {...common}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
    dots: <svg {...common}><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>,
    warning: <svg {...common}><path d="m10.29 3.86-8.5 14A2 2 0 0 0 3.5 21h17a2 2 0 0 0 1.71-3.14l-8.5-14a2 2 0 0 0-3.42 0Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>,
    check: <svg {...common}><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>,
    clock: <svg {...common}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
    dollar: <svg {...common}><path d="M12 2v20"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6"/></svg>,
    server: <svg {...common}><rect x="3" y="4" width="18" height="8" rx="2"/><rect x="3" y="12" width="18" height="8" rx="2"/><path d="M7 8h.01"/><path d="M7 16h.01"/></svg>,
    message: <svg {...common}><path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/></svg>,
    send: <svg {...common}><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>,
    lock: <svg {...common}><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
    flag: <svg {...common}><path d="M4 22V4"/><path d="M4 4h13l-1 5 1 5H4"/></svg>,
    mail: <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>,
    key: <svg {...common}><circle cx="7" cy="17" r="3"/><path d="M10 17h11l-3-3 3-3"/></svg>,
    database: <svg {...common}><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.7 4 3 9 3s9-1.3 9-3V5"/><path d="M3 12c0 1.7 4 3 9 3s9-1.3 9-3"/></svg>,
    tag: <svg {...common}><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"/></svg>
  };
  return icons[name] || icons.dashboard;
}

const nav: NavItem[] = [
  { name: "Command Center", icon: "dashboard" },
  { name: "Tenants", icon: "building" },
  { name: "Users & Teams", icon: "users" },
  { name: "My Team", icon: "users" },
  { name: "Billing", icon: "card" },
  { name: "Coupons", icon: "tag" },
  { name: "Integrations", icon: "plug" },
  { name: "Support Inbox", icon: "support" },
  { name: "Live Chat", icon: "message" },
  { name: "AI Agent Ops", icon: "bot" },
  { name: "Approvals", icon: "shield" },
  { name: "Guardrail Approvals", icon: "shield" },
  { name: "Security", icon: "shield" },
  { name: "Policy Simulator", icon: "shield" },
  { name: "System Health", icon: "activity" },
  { name: "Logs & Errors", icon: "warning" },
  { name: "Feature Flags", icon: "shield" },
  { name: "Announcements", icon: "megaphone" },
  { name: "Settings", icon: "settings" },
  // Day 14 Slice F — role-scoped dashboards. Visibility filtered by
  // sidebarPermissions.ts → `dashboard:*` perms (admin-bff migration 0006).
  { name: "Legal", icon: "shield" },
  { name: "Tech Support", icon: "clock" },
  { name: "Live Agent", icon: "message" },
  { name: "Billing Ops", icon: "card" },
  { name: "Technical", icon: "server" },
  { name: "App Review", icon: "plug" },
  { name: "People", icon: "users" }
];

const adminRoles = [
  { name: "Owner", description: "Full operational access" },
  { name: "Admin", description: "Full operational access" },
  { name: "Support Agent", description: "Support inbox + customer profile" },
  { name: "Billing Manager", description: "Limited module-specific access" },
  { name: "Engineer", description: "Limited module-specific access" },
  { name: "Read Only", description: "Limited module-specific access" }
];

const supportInboxTabs = ["All", "Pending", "Escalated", "Closed"] as const;

// Sections where the topbar search bar is meaningful. Everywhere else
// the input is hidden — the section either has no list to filter or
// owns its own search UI (e.g. Logs & Errors has actor/action filters).
const SEARCHABLE_SECTIONS = new Set(["Tenants", "Users & Teams", "Support Inbox"]);

const sectionHashMap: Record<string, string> = {
  "Command Center": "command-center",
  "Tenants": "tenants",
  "Users & Teams": "users-teams",
  "My Team": "my-team",
  "Billing": "billing",
  "Integrations": "integrations",
  "Support Inbox": "support-inbox",
  "Live Chat": "live-chat",
  "AI Agent Ops": "ai-agent-ops",
  "Approvals": "approvals",
  "Guardrail Approvals": "guardrail-approvals",
  "Security": "security",
  "Policy Simulator": "policy-simulator",
  "System Health": "system-health",
  "Logs & Errors": "logs-errors",
  "Feature Flags": "feature-flags",
  "Announcements": "announcements",
  "Settings": "settings",
  "Legal": "legal",
  "App Review": "app-review",
  "People": "people",
  "Tech Support": "tech-support",
  "Live Agent": "live-agent",
  "Billing Ops": "billing-ops",
  "Technical": "technical"
};

const sectionFromHashMap = Object.fromEntries(
  Object.entries(sectionHashMap).map(([section, hash]) => [hash, section])
) as Record<string, string>;

function useResource<T>(fetchFn: () => Promise<T>, initial: T, deps: ReadonlyArray<unknown> = []) {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchFn()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.message ?? String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadTick]);
  const refetch = () => setReloadTick((n) => n + 1);
  return { data, loading, error, refetch };
}

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl bg-white border border-zinc-200 shadow-sm ${className}`}>{children}</div>;
}
function Button({ children, className = "", variant = "solid", ...rest }: any) {
  const base = "inline-flex items-center justify-center rounded-2xl px-4 py-2.5 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed";
  const style = variant === "outline" ? "border border-zinc-200 bg-white text-zinc-900 hover:bg-zinc-50" : "bg-zinc-950 text-white hover:bg-zinc-800";
  return <button type="button" {...rest} className={`${base} ${style} ${className}`}>{children}</button>;
}
function Badge({ children, tone = "default" }) {
  const colors = { good: "bg-emerald-50 text-emerald-700", warn: "bg-amber-50 text-amber-700", bad: "bg-red-50 text-red-700", info: "bg-blue-50 text-blue-700", default: "bg-zinc-100 text-zinc-700" };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${colors[tone] || colors.default}`}>{children}</span>;
}
function StatCard({ title, value, sub, icon }) {
  return <Card className="p-5"><div className="flex items-center justify-between gap-4"><div><p className="text-sm text-zinc-500">{title}</p><h3 className="text-2xl font-bold text-zinc-950 mt-1">{value ?? "—"}</h3><p className="text-xs text-zinc-500 mt-1">{sub ?? "—"}</p></div><div className="h-11 w-11 rounded-2xl bg-zinc-950 text-white flex items-center justify-center shrink-0"><Icon name={icon} size={20}/></div></div></Card>;
}
function SectionHeader({ title, subtitle, action = null }) {
  return <div className="flex items-center justify-between gap-4 mb-4"><div><h3 className="text-lg font-bold">{title}</h3><p className="text-sm text-zinc-500">{subtitle}</p></div>{action}</div>;
}

function ResourceState({ loading, error, empty, emptyTitle, emptyBody }: { loading: boolean; error: string | null; empty: boolean; emptyTitle?: string; emptyBody?: string }) {
  if (loading) return <div className="rounded-2xl border border-zinc-200 p-6 text-sm text-zinc-500">Loading…</div>;
  if (error) return <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"><span className="font-semibold">Failed to load:</span> {error}</div>;
  if (empty) return <div className="rounded-2xl border border-dashed border-zinc-200 p-6 text-center"><p className="font-medium text-zinc-700">{emptyTitle ?? "No data yet"}</p><p className="text-sm text-zinc-500 mt-1">{emptyBody ?? "Connect a backend to populate this view."}</p></div>;
  return null;
}

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(amount);
}

function CompanyRollupTile() {
  const [window, setWindow] = useState<PerfWindow>("7d");
  const rollup = useResource<api.CompanyRollup | null>(() => api.getCompanyRollup(window), null, [window]);
  const r = rollup.data;
  const activeTotal = r ? r.headcountByDepartment.reduce((s, d) => s + Number(d.active), 0) : null;
  return (
    <Card className="p-5 mb-6">
      <SectionHeader
        title="Company rollup"
        subtitle="Cross-team headcount, activity, and pending approvals. Executive view."
        action={<WindowToggle value={window} onChange={setWindow} />}
      />
      <ResourceState loading={rollup.loading} error={rollup.error} empty={false} />
      {r && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <StatCard title="Active employees" value={activeTotal} sub={`${r.headcountByDepartment.length} departments`} icon="users" />
            <StatCard title={`Actions (${window})`} value={r.totalActionsInWindow} sub="Audited privileged actions" icon="activity" />
            <StatCard title="Pending approvals" value={r.pendingApprovals} sub="Across all teams" icon="check" />
          </div>
          <div className="overflow-x-auto rounded-2xl border border-zinc-200">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-500"><tr><th className="text-left p-3">Department</th><th className="text-right p-3">Active</th><th className="text-right p-3">Total</th></tr></thead>
              <tbody>
                {r.headcountByDepartment.map((d) => (
                  <tr key={d.department} className="border-t border-zinc-100">
                    <td className="p-3 font-medium">{d.department}</td>
                    <td className="p-3 text-right tabular-nums">{d.active}</td>
                    <td className="p-3 text-right tabular-nums text-zinc-500">{d.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

function CommandCenter({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const metrics = useResource<PlatformMetrics | null>(api.getPlatformMetrics, null);
  const tenants = useResource<Tenant[]>(api.listTenants, []);
  const alerts = useResource<PlatformAlert[]>(api.listPlatformAlerts, []);
  const m = metrics.data;
  const canSeeExec = currentUser?.permissions?.includes("dashboard:exec") ?? false;

  return <>
    {canSeeExec && <CompanyRollupTile />}
    <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
      <StatCard title="Monthly Recurring Revenue" value={m ? formatMoney(m.mrr) : null} sub={m?.mrrTrend ?? null} icon="dollar" />
      <StatCard title="Active Tenants" value={m?.activeTenants ?? null} sub={m ? `${m.trialsConvertingSoon} trials converting soon` : null} icon="building" />
      <StatCard title="Open Support Tickets" value={m?.openTickets ?? null} sub={m ? `${m.highPriorityTickets} high priority` : null} icon="support" />
      <StatCard title="System Health" value={m ? `${m.systemUptimePercent}%` : null} sub={m ? "Live uptime metric" : null} icon="server" />
    </section>
    <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <Card className="xl:col-span-2 p-5">
        <SectionHeader title="Tenant Operations" subtitle="Customer accounts that need attention." action={<Button variant="outline">View All</Button>} />
        <ResourceState loading={tenants.loading} error={tenants.error} empty={tenants.data.length === 0} emptyTitle="No tenants yet" emptyBody="Tenants returned by GET /tenants will appear here." />
        {!tenants.loading && !tenants.error && tenants.data.length > 0 && <TenantTable tenants={tenants.data} />}
      </Card>
      <AlertsCard alerts={alerts.data} loading={alerts.loading} error={alerts.error} />
    </section>
  </>;
}

function TenantTable({
  tenants,
  bulkMode = false,
  bulkSelected,
  onToggleBulk,
}: {
  tenants: Tenant[];
  bulkMode?: boolean;
  bulkSelected?: Set<string>;
  onToggleBulk?: (id: string) => void;
}) {
  // Rows navigate to the Tenant 360° detail page via the hash router.
  // Hash-driven (not setState) so the URL is the source of truth and
  // back/forward buttons work without extra plumbing.
  const openDetail = (id: string) => {
    if (typeof window !== "undefined") {
      window.location.hash = `tenant/${encodeURIComponent(id)}`;
    }
  };
  return (
    <div className="overflow-x-auto rounded-2xl border border-zinc-200">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-zinc-50 text-zinc-500">
          <tr>
            {bulkMode && <th className="p-3 w-10"></th>}
            <th className="text-left p-3">Tenant</th>
            <th className="text-left p-3">Owner</th>
            <th className="text-left p-3">Plan</th>
            <th className="text-left p-3">Health</th>
            <th className="text-left p-3">MRR</th>
            <th className="text-left p-3">Users</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((tenant) => {
            const checked = bulkSelected?.has(tenant.id) ?? false;
            const handleRowClick = () => {
              if (bulkMode && onToggleBulk) {
                onToggleBulk(tenant.id);
              } else {
                openDetail(tenant.id);
              }
            };
            return (
              <tr
                key={tenant.id}
                onClick={handleRowClick}
                className={`border-t border-zinc-100 cursor-pointer hover:bg-zinc-50 ${checked ? "bg-amber-50" : ""}`}
              >
                {bulkMode && (
                  <td className="p-3" onClick={(e) => { e.stopPropagation(); onToggleBulk?.(tenant.id); }}>
                    <input type="checkbox" checked={checked} readOnly className="h-4 w-4" />
                  </td>
                )}
                <td className="p-3 font-medium">{tenant.name}</td>
                <td className="p-3 text-zinc-600">{tenant.owner}</td>
                <td className="p-3">{tenant.plan}</td>
                <td className="p-3"><Badge tone={tenant.health === "Healthy" ? "good" : tenant.health === "Critical" ? "bad" : "warn"}>{tenant.health}</Badge></td>
                <td className="p-3">{tenant.mrr}</td>
                <td className="p-3">{tenant.users}</td>
                <td className="p-3 text-right"><Icon name="dots" size={18}/></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AlertsCard({ alerts, loading, error }: { alerts: PlatformAlert[]; loading: boolean; error: string | null }) {
  return <Card className="p-5"><h3 className="text-lg font-bold">Platform Alerts</h3><div className="mt-4"><ResourceState loading={loading} error={error} empty={alerts.length === 0} emptyTitle="No active alerts" emptyBody="Platform alerts surfaced by your backend will appear here." />{!loading && !error && alerts.length > 0 && <div className="space-y-4">{alerts.map((a) => <Alert key={a.id} icon={a.icon} tone={a.tone} title={a.title} body={a.body} />)}</div>}</div></Card>;
}
function Alert({ icon, tone, title, body }) { return <div className="flex gap-3"><Icon name={icon} className={tone} size={19}/><div><p className="text-sm font-medium">{title}</p><p className="text-xs text-zinc-500">{body}</p></div></div>; }

function TenantsSection({
  currentUser,
  searchQuery,
}: {
  currentUser: api.CurrentUser | null;
  searchQuery: string;
}) {
  const tenants = useResource<Tenant[]>(api.listTenants, []);
  const [selectedId, setSelectedId] = useState<string>("");
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkReason, setBulkReason] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkFeedback, setBulkFeedback] = useState<{ tone: "good" | "warn" | "bad"; text: string } | null>(null);

  const canSuspend = hasPerm(currentUser, "tenant:suspend");
  // Terminated (Cancelled) tenants are hidden by default so the dashboard
  // stays presentable — the rows still exist for audit and can be shown.
  const [showTerminated, setShowTerminated] = useState(false);

  const filtered = useMemo(() => {
    const base = showTerminated
      ? tenants.data
      : tenants.data.filter((t) => t.status !== "Cancelled");
    return filterTenants(base, searchQuery);
  }, [tenants.data, searchQuery, showTerminated]);
  const terminatedCount = useMemo(
    () => tenants.data.filter((t) => t.status === "Cancelled").length,
    [tenants.data],
  );

  const selected = tenants.data.find((t) => t.id === selectedId) ?? null;

  const toggleBulk = (id: string) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setBulkSelected(new Set(filtered.map((t) => t.id)));
  const clearSelection = () => setBulkSelected(new Set());

  async function runBulkSuspend() {
    if (bulkSelected.size === 0 || !bulkReason.trim() || bulkBusy) return;
    if (!confirm(`Suspend ${bulkSelected.size} tenant(s)? This routes through approval if 5+ tenants are selected.`)) return;
    setBulkBusy(true);
    setBulkFeedback(null);
    try {
      const res = await api.bulkSuspendTenants(Array.from(bulkSelected), bulkReason.trim()) as { status?: string; approvalId?: string; count?: number };
      if (res?.status === "pending_approval") {
        setBulkFeedback({ tone: "warn", text: `Approval required (id: ${res.approvalId}). Have a CFO+OWNER approve in the Approvals page.` });
      } else {
        setBulkFeedback({ tone: "good", text: `Suspended ${bulkSelected.size} tenant(s).` });
        clearSelection();
        tenants.refetch();
      }
    } catch (err: any) {
      setBulkFeedback({ tone: "bad", text: err?.message ?? "Bulk suspend failed." });
    } finally {
      setBulkBusy(false);
    }
  }

  return <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
    <Card className="xl:col-span-2 p-5">
      <SectionHeader
        title="Customer Tenants"
        subtitle={searchQuery ? `Filtered by "${searchQuery}" (${filtered.length} of ${tenants.data.length})` : "Manage accounts, plans, user limits, trials, health, and safe impersonation."}
        action={
          <div className="flex gap-2 items-center">
            {terminatedCount > 0 && (
              <button
                type="button"
                onClick={() => setShowTerminated((v) => !v)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${showTerminated ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}
              >
                {showTerminated ? `Hide terminated (${terminatedCount})` : `Show terminated (${terminatedCount})`}
              </button>
            )}
            {canSuspend && (
              <Button variant="outline" onClick={() => { setBulkMode(!bulkMode); clearSelection(); setBulkFeedback(null); }}>
                {bulkMode ? "Exit bulk" : "Bulk actions"}
              </Button>
            )}
            <Button>Create Tenant</Button>
          </div>
        }
      />
      {bulkMode && canSuspend && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-semibold">{bulkSelected.size} selected</span>
            <button type="button" onClick={selectAll} className="text-xs px-2 py-1 rounded-md bg-white border border-zinc-200">Select all visible</button>
            <button type="button" onClick={clearSelection} className="text-xs px-2 py-1 rounded-md bg-white border border-zinc-200">Clear</button>
            <input
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
              placeholder="Reason (required)…"
              className="flex-1 rounded-xl border border-zinc-200 px-3 py-2 text-sm"
            />
            <Button
              onClick={runBulkSuspend}
              disabled={bulkBusy || bulkSelected.size === 0 || !bulkReason.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {bulkBusy ? "Suspending…" : `Suspend ${bulkSelected.size}`}
            </Button>
          </div>
          {bulkFeedback && (
            <div className={`mt-2 text-xs ${
              bulkFeedback.tone === "good" ? "text-emerald-700" : bulkFeedback.tone === "warn" ? "text-amber-700" : "text-red-700"
            }`}>{bulkFeedback.text}</div>
          )}
          <p className="mt-2 text-xs text-zinc-500">Tip: 5+ tenants requires CFO+OWNER two-person approval before execution.</p>
        </div>
      )}
      <ResourceState loading={tenants.loading} error={tenants.error} empty={tenants.data.length === 0} emptyTitle="No tenants yet" emptyBody="Create a tenant or wait for your backend to report them." />
      {!tenants.loading && !tenants.error && tenants.data.length > 0 && filtered.length === 0 && !searchQuery && (
        <p className="text-sm text-zinc-500">Every tenant is terminated and hidden — use “Show terminated” to see them.</p>
      )}
      {!tenants.loading && !tenants.error && tenants.data.length > 0 && (
        filtered.length > 0
          ? <TenantTable tenants={filtered} bulkMode={bulkMode} bulkSelected={bulkSelected} onToggleBulk={toggleBulk} />
          : <p className="text-sm text-zinc-500">No tenants match "{searchQuery}".</p>
      )}
    </Card>
    <Card className="p-5">
      <h3 className="text-lg font-bold">Tenant 360 Actions</h3>

      {/* Tenant picker — actions below operate on this row. */}
      <label className="mt-4 block text-xs font-semibold text-zinc-500">Selected tenant</label>
      <select
        className="mt-1 w-full rounded-2xl border border-zinc-200 bg-white px-3 py-2 text-sm outline-none focus:border-zinc-500"
        value={selectedId}
        onChange={(e) => { setSelectedId(e.target.value); }}
      >
        <option value="">{tenants.data.length ? "Pick a tenant…" : "No tenants available"}</option>
        {tenants.data.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name} · {t.plan} · {t.status}
          </option>
        ))}
      </select>

      <TenantActionPanel
        tenant={selected}
        currentUser={currentUser}
        onChanged={() => tenants.refetch()}
      />
    </Card>
  </section>;
}

// Permission gate — every high-risk action button checks the actor's
// permissions array (sourced from /auth/me) so support-tier admins
// can't see buttons they can't operate. The actual server-side check
// is in tenants.controller.ts via @RequirePermissions; this is just UX.
function hasPerm(currentUser: api.CurrentUser | null, perm: string): boolean {
  return !!currentUser?.permissions?.includes(perm);
}

// TenantActionPanel — the action shelf that drives every high-risk
// tenant operation. Reused on the Tenants page action card and on the
// Tenant 360° detail page so support agents have a consistent surface.
//
// Each action runs through admin-bff /tenants/:id/<action>, which
// either auto-executes (200) or routes to the approval workflow (202).
// The interpretActionResult helper produces operator-facing copy from
// the response envelope so the UI doesn't reimplement that logic.
function TenantActionPanel({
  tenant,
  currentUser,
  onChanged,
}: {
  tenant: Tenant | null;
  currentUser: api.CurrentUser | null;
  onChanged: () => void;
}) {
  const [openModal, setOpenModal] = useState<ActionKind | "resume" | "impersonate" | null>(null);
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [graceDays, setGraceDays] = useState(0);
  // Plan-override-specific state (Slice D). overridePlan is one of the
  // seeded tiers; overrideExpiresAt is an empty string when the override
  // should never auto-expire (UI surfaces that as "no expiry").
  const [overridePlan, setOverridePlan] = useState<
    "starter" | "growth" | "business" | "enterprise"
  >("enterprise");
  const [overrideExpiresAt, setOverrideExpiresAt] = useState("");
  // Impersonation-specific state. tenantUsers is fetched lazily when
  // the modal opens so we don't pre-load every tenant's user list.
  const [tenantUsers, setTenantUsers] = useState<api.TenantUserRow[] | null>(null);
  const [tenantUsersLoading, setTenantUsersLoading] = useState(false);
  const [impersonateUserId, setImpersonateUserId] = useState("");
  const [impersonateMinutes, setImpersonateMinutes] = useState(15);
  const [impersonateResult, setImpersonateResult] = useState<api.ImpersonationStartResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: "good" | "warn" | "bad"; text: string } | null>(null);

  const closeModal = () => {
    setOpenModal(null);
    setReason("");
    setAmount("");
    setGraceDays(0);
    setOverridePlan("enterprise");
    setOverrideExpiresAt("");
    setTenantUsers(null);
    setImpersonateUserId("");
    setImpersonateMinutes(15);
    setImpersonateResult(null);
  };

  const beginAction = (k: ActionKind | "resume" | "impersonate") => {
    setOpenModal(k);
    setReason("");
    setAmount("");
    setGraceDays(0);
    setOverridePlan("enterprise");
    setOverrideExpiresAt("");
    setImpersonateUserId("");
    setImpersonateMinutes(15);
    setImpersonateResult(null);
    setFeedback(null);
    // Lazy-load the tenant's user list when opening the impersonate modal.
    if (k === "impersonate" && tenant) {
      setTenantUsersLoading(true);
      setTenantUsers(null);
      api
        .listTenantUsers(tenant.id)
        .then((users) => setTenantUsers(users))
        .catch(() => setTenantUsers([]))
        .finally(() => setTenantUsersLoading(false));
    }
  };

  const onConfirmImpersonate = async () => {
    if (!tenant || !impersonateUserId || reason.trim().length < 3) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const res = await api.startImpersonation({
        tenantId: tenant.id,
        targetUserId: impersonateUserId,
        reason: reason.trim(),
        durationSeconds: Math.max(60, impersonateMinutes * 60),
      });
      setImpersonateResult(res);
      // Auto-open the consume URL when present so the operator lands
      // straight in the customer app session. They can copy the token
      // manually for any other endpoint.
      if (res.consumeUrl && typeof window !== "undefined") {
        window.open(res.consumeUrl, "_blank", "noopener,noreferrer");
      }
    } catch (e: any) {
      setFeedback({ tone: "bad", text: e?.message ?? "Impersonation failed." });
    } finally {
      setSubmitting(false);
    }
  };

  const runWith = async (action: ActionKind, fn: () => Promise<unknown>) => {
    setSubmitting(true);
    setFeedback(null);
    try {
      const res = (await fn()) as ApprovalEnvelope;
      const outcome: ActionOutcome = interpretActionResult(action, res ?? {});
      const tone = outcome.kind === "pending" ? "warn" : outcome.kind === "failed" ? "bad" : "good";
      setFeedback({ tone, text: outcome.message });
      if (outcome.refetch) onChanged();
      closeModal();
    } catch (e: any) {
      setFeedback({ tone: "bad", text: e?.message ?? `${action} failed.` });
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirmSuspend = () => {
    if (!tenant || reason.trim().length < 3) return;
    return runWith("suspend", () => api.suspendTenant(tenant.id, reason.trim()));
  };

  const onConfirmPlanOverride = () => {
    if (!tenant || reason.trim().length < 3) return;
    // Empty expiry → null (override never auto-expires). Non-empty
    // values arrive from <input type="datetime-local"> as
    // "YYYY-MM-DDTHH:mm" without a timezone; treat them as the
    // operator's local time and convert to ISO.
    const expiresAt =
      overrideExpiresAt.trim() === ""
        ? null
        : new Date(overrideExpiresAt).toISOString();
    return runWith("plan_override", () =>
      api.setTenantPlanOverride(tenant.id, {
        plan: overridePlan,
        reason: reason.trim(),
        expiresAt,
      }),
    );
  };

  const onConfirmClearPlanOverride = () => {
    if (!tenant) return;
    return runWith("clear_plan_override", () =>
      api.clearTenantPlanOverride(tenant.id),
    );
  };

  const onConfirmResume = async () => {
    if (!tenant) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      await api.resumeTenant(tenant.id);
      setFeedback({ tone: "good", text: "Tenant resumed." });
      onChanged();
      closeModal();
    } catch (e: any) {
      setFeedback({ tone: "bad", text: e?.message ?? "Resume failed." });
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirmRefund = () => {
    if (!tenant) return;
    const cents = Math.round(parseFloat(amount) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setFeedback({ tone: "bad", text: "Amount must be a positive number." });
      return;
    }
    if (reason.trim().length < 3) return;
    return runWith("refund", () => api.refundTenant(tenant.id, cents, reason.trim()));
  };

  const onConfirmCredit = () => {
    if (!tenant) return;
    const cents = Math.round(parseFloat(amount) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setFeedback({ tone: "bad", text: "Amount must be a positive number." });
      return;
    }
    if (reason.trim().length < 3) return;
    return runWith("credit", () => api.creditTenant(tenant.id, cents, reason.trim()));
  };

  const onConfirmTerminate = () => {
    if (!tenant) return;
    if (reason.trim().length < 3) return;
    return runWith("terminate", () => api.terminateTenant(tenant.id, reason.trim(), graceDays));
  };

  const isSuspended = tenant?.status === "Suspended";
  const isCancelled = tenant?.status === "Cancelled";

  return (
    <>
      <div className="grid gap-3 mt-4">
        {hasPerm(currentUser, "user:impersonate") && (
          <Button
            variant="outline"
            disabled={!tenant || isSuspended || isCancelled}
            onClick={() => beginAction("impersonate")}
          >
            Safe Impersonation{tenant ? ` — ${tenant.name}` : ""}
          </Button>
        )}
        {hasPerm(currentUser, "tenant:plan_override") ? (
          <Button
            variant="outline"
            disabled={!tenant || isCancelled}
            onClick={() => beginAction("plan_override")}
          >
            Override Plan{tenant ? ` — ${tenant.name}` : ""}
          </Button>
        ) : (
          <Button variant="outline" disabled>Change Plan</Button>
        )}

        {hasPerm(currentUser, "tenant:suspend") && !isSuspended && (
          <Button
            variant="outline"
            disabled={!tenant || isCancelled}
            onClick={() => beginAction("suspend")}
          >
            Suspend Account{tenant ? ` — ${tenant.name}` : ""}
          </Button>
        )}

        {hasPerm(currentUser, "tenant:suspend") && isSuspended && (
          <Button variant="outline" onClick={() => beginAction("resume")}>
            Resume Account{tenant ? ` — ${tenant.name}` : ""}
          </Button>
        )}

        {hasPerm(currentUser, "billing:refund") && (
          <Button
            variant="outline"
            disabled={!tenant}
            onClick={() => beginAction("refund")}
          >
            Issue Refund{tenant ? ` — ${tenant.name}` : ""}
          </Button>
        )}

        {hasPerm(currentUser, "billing:credit") && (
          <Button
            variant="outline"
            disabled={!tenant}
            onClick={() => beginAction("credit")}
          >
            Issue Credit{tenant ? ` — ${tenant.name}` : ""}
          </Button>
        )}

        {hasPerm(currentUser, "tenant:terminate") && (
          <Button
            variant="outline"
            disabled={!tenant || isCancelled}
            onClick={() => beginAction("terminate")}
          >
            Terminate Account{tenant ? ` — ${tenant.name}` : ""}
          </Button>
        )}
      </div>

      {feedback && (
        <div className={`mt-4 rounded-2xl border p-3 text-sm ${
          feedback.tone === "good" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : feedback.tone === "warn" ? "border-amber-200 bg-amber-50 text-amber-900"
          : "border-red-200 bg-red-50 text-red-800"
        }`}>{feedback.text}</div>
      )}

      {openModal === "suspend" && tenant && (
        <ActionModal
          title={`Suspend ${tenant.name}?`}
          warning="High-risk action. Below SUPPORT_LEAD this will be held for approval."
          submitLabel={submitting ? "..." : "Confirm suspend"}
          submitDisabled={submitting || reason.trim().length < 3}
          onSubmit={onConfirmSuspend}
          onCancel={closeModal}
        >
          <ReasonField value={reason} onChange={setReason} placeholder="e.g. fraudulent payment dispute" />
        </ActionModal>
      )}

      {openModal === "resume" && tenant && (
        <ActionModal
          title={`Resume ${tenant.name}?`}
          warning="Reverses suspension. Tenant data and billing resume immediately."
          submitLabel={submitting ? "..." : "Confirm resume"}
          submitDisabled={submitting}
          onSubmit={onConfirmResume}
          onCancel={closeModal}
        >
          <p className="text-xs text-zinc-600">
            No reason required — resume is a low-risk reverse of suspend.
          </p>
        </ActionModal>
      )}

      {openModal === "refund" && tenant && (
        <ActionModal
          title={`Issue refund to ${tenant.name}?`}
          warning="Above your tier (per approval policy) this routes to Approvals before any Stripe call."
          submitLabel={submitting ? "..." : "Confirm refund"}
          submitDisabled={submitting || reason.trim().length < 3 || !amount}
          onSubmit={onConfirmRefund}
          onCancel={closeModal}
        >
          <AmountField value={amount} onChange={setAmount} />
          <ReasonField value={reason} onChange={setReason} placeholder="e.g. service outage credit per support#1842" />
          <p className="text-[11px] text-zinc-500 mt-2">
            Refunds against the tenant's most recent successful Stripe charge.
          </p>
        </ActionModal>
      )}

      {openModal === "credit" && tenant && (
        <ActionModal
          title={`Apply account credit to ${tenant.name}?`}
          warning="Negative customer balance — applied to next invoice."
          submitLabel={submitting ? "..." : "Confirm credit"}
          submitDisabled={submitting || reason.trim().length < 3 || !amount}
          onSubmit={onConfirmCredit}
          onCancel={closeModal}
        >
          <AmountField value={amount} onChange={setAmount} />
          <ReasonField value={reason} onChange={setReason} placeholder="e.g. retention offer Q2" />
        </ActionModal>
      )}

      {openModal === "terminate" && tenant && (
        <ActionModal
          title={`Terminate ${tenant.name}?`}
          warning="Irreversible. As OWNER this executes immediately; other roles create a pending approval an OWNER must approve. Data purges after grace expires."
          submitLabel={submitting ? "..." : "Confirm terminate"}
          submitDisabled={submitting || reason.trim().length < 3}
          onSubmit={onConfirmTerminate}
          onCancel={closeModal}
        >
          <ReasonField value={reason} onChange={setReason} placeholder="e.g. customer requested closure under DPA" />
          <label className="mt-3 block text-xs font-semibold text-zinc-700">Grace period (days)</label>
          <input
            type="number"
            min={0}
            max={180}
            value={graceDays}
            onChange={(e) => setGraceDays(Math.max(0, Math.min(180, parseInt(e.target.value || "0", 10))))}
            className="w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm outline-none focus:border-zinc-500"
          />
          <p className="text-[11px] text-zinc-500 mt-1">
            0 = immediate termination. After grace expires, the purge worker reaps tenant data.
          </p>
        </ActionModal>
      )}

      {openModal === "plan_override" && tenant && (
        <ActionModal
          title={`Override plan for ${tenant.name}?`}
          warning="Changes the effective plan for feature gating only — billing/subscriptions are unchanged. Below CFO this is held for approval."
          submitLabel={submitting ? "..." : "Apply override"}
          submitDisabled={submitting || reason.trim().length < 3 || !overridePlan}
          onSubmit={onConfirmPlanOverride}
          onCancel={closeModal}
        >
          <label className="block text-xs font-semibold text-zinc-700">Target plan</label>
          <select
            value={overridePlan}
            onChange={(e) =>
              setOverridePlan(
                e.target.value as "starter" | "growth" | "business" | "enterprise",
              )
            }
            className="mt-1 w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm outline-none focus:border-zinc-500"
          >
            <option value="starter">starter</option>
            <option value="growth">growth</option>
            <option value="business">business</option>
            <option value="enterprise">enterprise</option>
          </select>
          <ReasonField
            value={reason}
            onChange={setReason}
            placeholder="e.g. paid eval grant #1842 — replace lost trial week"
          />
          <label className="mt-3 block text-xs font-semibold text-zinc-700">
            Expires (optional)
          </label>
          <input
            type="datetime-local"
            value={overrideExpiresAt}
            onChange={(e) => setOverrideExpiresAt(e.target.value)}
            className="mt-1 w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm outline-none focus:border-zinc-500"
          />
          <p className="text-[11px] text-zinc-500 mt-1">
            Empty = never auto-expires (manual revocation only). Existing
            overrides are replaced — to revert, use "Clear override".
          </p>
        </ActionModal>
      )}

      {openModal === "clear_plan_override" && tenant && (
        <ActionModal
          title={`Clear plan override for ${tenant.name}?`}
          warning="Reverts the tenant to their raw subscription plan. Low-risk and auto-approved."
          submitLabel={submitting ? "..." : "Confirm clear"}
          submitDisabled={submitting}
          onSubmit={onConfirmClearPlanOverride}
          onCancel={closeModal}
        >
          <p className="text-xs text-zinc-600">
            Feature gating reverts on the tenant's next login (token TTL).
            The audit log captures the actor + timestamp automatically.
          </p>
        </ActionModal>
      )}

      {openModal === "impersonate" && tenant && (
        <ActionModal
          title={`Impersonate a user in ${tenant.name}`}
          warning="Every impersonation is audited. Hard-capped at 30 minutes per session."
          submitLabel={
            impersonateResult ? "Done" :
            submitting ? "..." : "Mint impersonation token"
          }
          submitDisabled={
            impersonateResult ? false :
            submitting || !impersonateUserId || reason.trim().length < 3
          }
          onSubmit={impersonateResult ? closeModal : onConfirmImpersonate}
          onCancel={closeModal}
        >
          {!impersonateResult && (
            <>
              <label className="block text-xs font-semibold text-zinc-700">Target user</label>
              {tenantUsersLoading ? (
                <p className="mt-1 text-xs text-zinc-500">Loading users…</p>
              ) : (
                <select
                  value={impersonateUserId}
                  onChange={(e) => setImpersonateUserId(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm outline-none focus:border-zinc-500"
                >
                  <option value="">{tenantUsers && tenantUsers.length > 0 ? "Pick a user…" : "No users on this tenant"}</option>
                  {(tenantUsers ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.fullName || u.email} · {u.role}
                    </option>
                  ))}
                </select>
              )}
              <label className="mt-3 block text-xs font-semibold text-zinc-700">Duration (minutes, max 30)</label>
              <input
                type="number"
                min={1}
                max={30}
                value={impersonateMinutes}
                onChange={(e) => setImpersonateMinutes(Math.max(1, Math.min(30, parseInt(e.target.value || "1", 10))))}
                className="w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm outline-none focus:border-zinc-500"
              />
              <ReasonField value={reason} onChange={setReason} placeholder="e.g. debugging support#4521 — customer reports stuck checkout" />
            </>
          )}
          {impersonateResult && (
            <div className="space-y-3">
              <p className="text-sm text-emerald-800 font-semibold">
                Token minted — opened in a new tab.
              </p>
              <p className="text-xs text-zinc-600">
                Expires {new Date(impersonateResult.expiresAt).toLocaleString()}.
              </p>
              {impersonateResult.consumeUrl && (
                <p className="text-[11px] text-zinc-500 break-all">
                  Link: <a href={impersonateResult.consumeUrl} target="_blank" rel="noopener noreferrer" className="underline">{impersonateResult.consumeUrl}</a>
                </p>
              )}
              <button
                type="button"
                onClick={() => {
                  if (typeof navigator !== "undefined" && navigator.clipboard) {
                    navigator.clipboard.writeText(impersonateResult.token);
                  }
                }}
                className="text-xs underline text-zinc-700 hover:text-zinc-950"
              >
                Copy raw token
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.revokeImpersonation(impersonateResult.id);
                    setFeedback({ tone: "warn", text: "Impersonation token revoked." });
                    closeModal();
                  } catch (e: any) {
                    setFeedback({ tone: "bad", text: e?.message ?? "Revoke failed." });
                  }
                }}
                className="text-xs underline text-red-700 hover:text-red-900 ml-3"
              >
                Revoke now
              </button>
            </div>
          )}
        </ActionModal>
      )}
    </>
  );
}

function ActionModal({
  title,
  warning,
  submitLabel,
  submitDisabled,
  onSubmit,
  onCancel,
  children,
}: {
  title: string;
  warning?: string;
  submitLabel: string;
  submitDisabled: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4 rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
      <p className="text-sm font-semibold text-zinc-900">{title}</p>
      {warning && <p className="mt-1 text-xs text-zinc-600">{warning}</p>}
      <div className="mt-3">{children}</div>
      <div className="mt-3 flex gap-2">
        <Button onClick={onSubmit} disabled={submitDisabled}>{submitLabel}</Button>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function ReasonField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <>
      <label className="mt-1 block text-xs font-semibold text-zinc-700">Reason (min 3 chars)</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm outline-none focus:border-zinc-500"
        placeholder={placeholder}
      />
    </>
  );
}

function AmountField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <>
      <label className="block text-xs font-semibold text-zinc-700">Amount (USD)</label>
      <input
        type="number"
        step="0.01"
        min="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm outline-none focus:border-zinc-500"
        placeholder="49.99"
      />
    </>
  );
}

// TenantDetailSection — Tenant 360° drill-in. Aggregates everything an
// admin needs to triage a customer in one place: identity, plan +
// billing, users, recent admin actions on the account, and a
// per-tenant audit trail. The action shelf reuses TenantActionPanel
// so suspend/resume/refund/credit/terminate behave identically here
// and on the Tenants list.
//
// Each panel fetches independently — a single failing endpoint shows
// "Failed to load" in its panel rather than blanking the page.
function TenantDetailSection({
  tenantId,
  currentUser,
}: {
  tenantId: string;
  currentUser: api.CurrentUser | null;
}) {
  const detailFetcher = React.useCallback(() => api.getTenantDetail(tenantId), [tenantId]);
  const usersFetcher = React.useCallback(() => api.listTenantUsers(tenantId), [tenantId]);
  const auditFetcher = React.useCallback(() => api.listTenantAudit(tenantId), [tenantId]);
  const billingFetcher = React.useCallback(() => api.getTenantBillingActions(tenantId), [tenantId]);

  const detail = useResource<api.TenantDetail | null>(detailFetcher, null, [tenantId]);
  const users = useResource<api.TenantUserRow[]>(usersFetcher, [], [tenantId]);
  const audit = useResource<api.TenantAuditRow[]>(auditFetcher, [], [tenantId]);
  const billing = useResource<{ refunds: api.TenantBillingActionRow[]; credits: api.TenantBillingActionRow[] }>(
    billingFetcher,
    { refunds: [], credits: [] },
    [tenantId],
  );

  const refetchAll = () => {
    detail.refetch();
    audit.refetch();
    billing.refetch();
  };

  // Adapt the AdminTenant-shape detail into the Tenant-shape that
  // TenantActionPanel expects. Both come from auth-service originally
  // but the column-renamed table shape used in TenantsSection differs.
  const tenantForActions = detail.data
    ? {
        id: detail.data.id,
        name: detail.data.name,
        owner: detail.data.ownerName || detail.data.ownerEmail || '—',
        plan: detail.data.plan,
        health: capitalizeStatus(detail.data.health) as 'Healthy' | 'Attention' | 'Critical',
        mrr: '—',
        users: detail.data.userCount,
        status: capitalizeStatus(detail.data.status) as 'Active' | 'Trial' | 'Suspended' | 'Cancelled',
      }
    : null;

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            type="button"
            onClick={() => { window.location.hash = "tenants"; }}
            className="text-sm text-zinc-500 hover:text-zinc-900"
          >
            ← Back to Tenants
          </button>
          <h2 className="mt-1 text-2xl font-bold">{detail.data?.name ?? "Loading…"}</h2>
          {detail.data && (
            <p className="text-sm text-zinc-500">
              {detail.data.slug} · {detail.data.plan} · <Badge tone={tenantStatusTone(detail.data.status)}>{capitalizeStatus(detail.data.status)}</Badge>
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-2 p-5">
          <h3 className="text-lg font-bold">Tenant summary</h3>
          <ResourceState
            loading={detail.loading}
            error={detail.error}
            empty={!detail.data}
            emptyTitle="Tenant not found"
            emptyBody="The id in the URL didn't match a tenant. Check the link or go back."
          />
          {detail.data && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Info label="Owner" value={detail.data.ownerName || detail.data.ownerEmail || "—"} />
              <Info label="Owner email" value={detail.data.ownerEmail || "—"} />
              <Info label="Plan" value={detail.data.plan} />
              <Info label="Status" value={capitalizeStatus(detail.data.status)} />
              <Info label="Users" value={String(detail.data.userCount)} />
              <Info label="Sector" value={detail.data.activitySector || "—"} />
              <Info label="Business size" value={detail.data.businessSize || "—"} />
              <Info label="Created" value={formatDate(detail.data.createdAt)} />
              <Info label="Last activity" value={detail.data.lastActivityAt ? formatDate(detail.data.lastActivityAt) : "—"} />
              {detail.data.suspendedAt && (
                <>
                  <Info label="Suspended at" value={formatDate(detail.data.suspendedAt)} />
                  <Info label="Suspend reason" value={detail.data.suspendedReason || "—"} />
                </>
              )}
              {detail.data.terminatedAt && (
                <>
                  <Info label="Terminated at" value={formatDate(detail.data.terminatedAt)} />
                  <Info label="Terminate reason" value={detail.data.terminatedReason || "—"} />
                  <Info
                    label="Grace until"
                    value={detail.data.terminatedGraceUntil ? formatDate(detail.data.terminatedGraceUntil) : "Immediate"}
                  />
                </>
              )}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="text-lg font-bold">Actions</h3>
          {!tenantForActions ? (
            <p className="mt-3 text-sm text-zinc-500">Load the tenant to enable actions.</p>
          ) : (
            <TenantActionPanel
              tenant={tenantForActions}
              currentUser={currentUser}
              onChanged={refetchAll}
            />
          )}
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="text-lg font-bold">Users on this tenant</h3>
        <ResourceState
          loading={users.loading}
          error={users.error}
          empty={!users.loading && users.data.length === 0}
          emptyTitle="No users"
          emptyBody="Auth-service returned no users for this tenant."
        />
        {users.data.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-500"><tr>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Role</th>
                <th className="text-left p-2">MFA</th>
                <th className="text-left p-2">Last login</th>
                <th className="text-left p-2">Active</th>
              </tr></thead>
              <tbody>
                {users.data.map((u) => (
                  <tr key={u.id} className="border-t border-zinc-100">
                    <td className="p-2 font-medium">{u.fullName || "—"}</td>
                    <td className="p-2 text-zinc-600">{u.email}</td>
                    <td className="p-2">{u.role}</td>
                    <td className="p-2">{u.mfaEnabled ? "Yes" : "No"}</td>
                    <td className="p-2">{u.lastLoginAt ? formatDate(u.lastLoginAt) : "—"}</td>
                    <td className="p-2">
                      <Badge tone={u.active ? "good" : "bad"}>{u.active ? "Active" : "Inactive"}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="text-lg font-bold">Recent refunds</h3>
          <ResourceState
            loading={billing.loading}
            error={billing.error}
            empty={!billing.loading && billing.data.refunds.length === 0}
            emptyTitle="No refunds yet"
            emptyBody="Issue one from the action panel above."
          />
          {billing.data.refunds.length > 0 && (
            <BillingActionList rows={billing.data.refunds} kind="refund" />
          )}
        </Card>
        <Card className="p-5">
          <h3 className="text-lg font-bold">Recent credits</h3>
          <ResourceState
            loading={billing.loading}
            error={billing.error}
            empty={!billing.loading && billing.data.credits.length === 0}
            emptyTitle="No credits yet"
            emptyBody="Issue one from the action panel above."
          />
          {billing.data.credits.length > 0 && (
            <BillingActionList rows={billing.data.credits} kind="credit" />
          )}
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="text-lg font-bold">Audit trail</h3>
        <ResourceState
          loading={audit.loading}
          error={audit.error}
          empty={!audit.loading && audit.data.length === 0}
          emptyTitle="No audit entries"
          emptyBody="Admin actions on this tenant will land here."
        />
        {audit.data.length > 0 && (
          <ul className="mt-4 space-y-2 text-sm">
            {audit.data.map((row) => (
              <li key={row.id} className="flex items-start gap-3 border-b border-zinc-100 pb-2">
                <Badge tone={row.result === "success" ? "good" : row.result === "denied" ? "warn" : "bad"}>
                  {row.result}
                </Badge>
                <div className="flex-1">
                  <p className="font-medium">{row.action}</p>
                  <p className="text-xs text-zinc-500">{row.actor} · {formatDate(row.occurredAt)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}

function BillingActionList({
  rows,
  kind,
}: {
  rows: api.TenantBillingActionRow[];
  kind: 'refund' | 'credit';
}) {
  return (
    <ul className="mt-4 space-y-2 text-sm">
      {rows.map((r) => (
        <li key={r.id} className="rounded-xl border border-zinc-200 p-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-semibold">{formatCents(r.amountCents)}</p>
              <p className="text-xs text-zinc-500">{r.reason}</p>
              <p className="text-xs text-zinc-400 mt-1">
                by {r.actorEmail || "system"} · {formatDate(r.createdAt)}
              </p>
            </div>
            <Badge tone={r.status === "completed" ? "good" : r.status === "failed" ? "bad" : "warn"}>
              {r.status}
            </Badge>
          </div>
          {r.errorMessage && (
            <p className="mt-2 text-xs text-red-700">{r.errorMessage}</p>
          )}
          {kind === 'refund' && r.stripeRefundId && (
            <p className="mt-2 text-[11px] text-zinc-400">Stripe: {r.stripeRefundId}</p>
          )}
          {kind === 'credit' && r.stripeBalanceTransactionId && (
            <p className="mt-2 text-[11px] text-zinc-400">Stripe: {r.stripeBalanceTransactionId}</p>
          )}
        </li>
      ))}
    </ul>
  );
}

function capitalizeStatus(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function tenantStatusTone(status: string): 'good' | 'warn' | 'bad' | 'info' | 'default' {
  switch (status) {
    case 'active': return 'good';
    case 'trial': return 'info';
    case 'suspended': return 'warn';
    case 'cancelled': return 'bad';
    default: return 'default';
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

// ----- Pillar 2: team performance shared UI -----

const PERF_WINDOWS = ["7d", "30d"] as const;
type PerfWindow = (typeof PERF_WINDOWS)[number];

function WindowToggle({ value, onChange }: { value: PerfWindow; onChange: (w: PerfWindow) => void }) {
  return (
    <div className="inline-flex rounded-xl border border-zinc-200 bg-white p-0.5 text-xs font-medium">
      {PERF_WINDOWS.map((w) => (
        <button
          key={w}
          type="button"
          onClick={() => onChange(w)}
          className={`px-3 py-1.5 rounded-lg transition ${value === w ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
        >
          {w === "7d" ? "7 days" : "30 days"}
        </button>
      ))}
    </div>
  );
}

const fmtPct = (r: number | null) => (r == null ? "—" : `${Math.round(r * 100)}%`);
const fmtMins = (m: number | null) => (m == null ? "—" : m >= 60 ? `${Math.round((m / 60) * 10) / 10}h` : `${m}m`);

// Column registry — the role-dashboard overlays reuse this table with a subset.
const KPI_COLUMNS = {
  actions: { label: "Actions", render: (k: api.EmployeeKpis) => `${k.actionsTotal}` , sub: (k: api.EmployeeKpis) => k.actionsTotal ? `${fmtPct(k.successRate)} ok` : "—" },
  approvals: { label: "Approvals", render: (k: api.EmployeeKpis) => `${k.approvalsActioned}`, sub: (k: api.EmployeeKpis) => `${k.approvalsRequested} req` },
  pending: { label: "Pending", render: (k: api.EmployeeKpis) => `${k.approvalsPending}`, sub: () => "in queue" },
  tickets: { label: "Open tickets", render: (k: api.EmployeeKpis) => k.opsAvailable ? `${k.ticketsOpen}` : "—", sub: () => "now" },
  live: { label: "Live", render: (k: api.EmployeeKpis) => k.opsAvailable ? `${k.liveChatActive}` : "—", sub: () => "chats" },
  resolved: { label: "Resolved", render: (k: api.EmployeeKpis) => k.opsAvailable ? `${k.ticketsResolved}` : "—", sub: (k: api.EmployeeKpis) => `avg ${fmtMins(k.avgResolutionMinutes)}` },
  lastActive: { label: "Last active", render: (k: api.EmployeeKpis) => k.lastActionAt ? formatDate(k.lastActionAt) : "—", sub: () => "" },
} as const;
type KpiColumnKey = keyof typeof KPI_COLUMNS;

function TeamMemberKpiTable({
  team,
  columns,
  onSelect,
}: {
  team: api.TeamMemberPerf[];
  columns: KpiColumnKey[];
  onSelect?: (id: string) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-zinc-200">
      <table className="w-full text-sm min-w-[680px]">
        <thead className="bg-zinc-50 text-zinc-500">
          <tr>
            <th className="text-left p-3">Name</th>
            {columns.map((c) => (
              <th key={c} className="text-right p-3">{KPI_COLUMNS[c].label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {team.map((m) => (
            <tr key={m.id} className="border-t border-zinc-100">
              <td className="p-3 font-medium">
                {onSelect ? (
                  <button type="button" className="text-zinc-900 hover:underline" onClick={() => onSelect(m.id)}>
                    {m.fullName || m.email}
                  </button>
                ) : (
                  m.fullName || m.email
                )}
                <div className="text-xs text-zinc-500">{m.title || m.department || m.email}</div>
              </td>
              {columns.map((c) => (
                <td key={c} className="p-3 text-right tabular-nums">
                  <div className="font-semibold text-zinc-900">{KPI_COLUMNS[c].render(m.kpis)}</div>
                  <div className="text-xs text-zinc-400">{KPI_COLUMNS[c].sub(m.kpis)}</div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Manager-variant overlay reused on each role dashboard. Renders nothing for non-managers. */
function TeamMemberOverlay({ title, columns }: { title: string; columns: KpiColumnKey[] }) {
  const [window, setWindow] = useState<PerfWindow>("7d");
  const perf = useResource<api.MyTeamPerformance | null>(() => api.getMyTeamPerformance(window), null, [window]);
  const team = perf.data?.team ?? [];
  if (!perf.loading && team.length === 0) return null; // non-managers see nothing
  return (
    <Card className="xl:col-span-4 p-5 mt-2">
      <SectionHeader
        title={title}
        subtitle="Your direct reports' contribution. Manager view."
        action={<WindowToggle value={window} onChange={setWindow} />}
      />
      <ResourceState loading={perf.loading} error={perf.error} empty={false} />
      {team.length > 0 && <TeamMemberKpiTable team={team} columns={columns} />}
    </Card>
  );
}

function EmployeePerformanceSection({ userId, onBack }: { userId: string; onBack: () => void }) {
  const [window, setWindow] = useState<PerfWindow>("7d");
  const detail = useResource<api.EmployeePerformanceDetail | null>(
    () => api.getEmployeePerformance(userId, window),
    null,
    [userId, window],
  );
  const d = detail.data;
  const k = d?.kpis;
  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <button type="button" onClick={onBack} className="text-sm text-zinc-600 hover:underline">← Back to My Team</button>
        <WindowToggle value={window} onChange={setWindow} />
      </div>
      <Card className="p-5">
        <h3 className="text-xl font-bold">{d?.user.fullName || "—"}</h3>
        <p className="text-sm text-zinc-500">{[d?.user.title, d?.user.department].filter(Boolean).join(" · ") || d?.user.email}</p>
      </Card>
      <ResourceState loading={detail.loading} error={detail.error} empty={false} />
      {k && (
        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard title="Actions" value={k.actionsTotal} sub={k.actionsTotal ? `${fmtPct(k.successRate)} success` : "none in window"} icon="activity" />
          <StatCard title="Approvals actioned" value={k.approvalsActioned} sub={`${k.approvalsRequested} requested · ${k.approvalsPending} pending`} icon="check" />
          <StatCard title="Tickets resolved" value={k.opsAvailable ? k.ticketsResolved : "—"} sub={k.opsAvailable ? `avg ${fmtMins(k.avgResolutionMinutes)} · ${k.ticketsOpen} open` : "ops metrics unavailable"} icon="support" />
          <StatCard title="Live chats" value={k.opsAvailable ? k.liveChatActive : "—"} sub={k.lastActionAt ? `last active ${formatDate(k.lastActionAt)}` : "—"} icon="message" />
        </section>
      )}
      {d && (
        <Card className="p-5">
          <SectionHeader title="Recent activity" subtitle={`Last ${d.timeline.length} privileged actions in the window.`} />
          <ResourceState loading={false} error={null} empty={d.timeline.length === 0} emptyTitle="No activity" emptyBody="No audited actions in this window." />
          {d.timeline.length > 0 && (
            <ul className="space-y-2">
              {d.timeline.map((e, i) => (
                <li key={i} className="flex items-center justify-between gap-3 rounded-2xl border border-zinc-200 p-3 text-sm">
                  <div>
                    <span className="font-medium">{e.action}</span>
                    {e.resource_type && <span className="text-zinc-500"> · {e.resource_type}{e.resource_id ? ` ${e.resource_id}` : ""}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge tone={e.result === "success" ? "good" : e.result === "denied" ? "warn" : "bad"}>{e.result}</Badge>
                    <span className="text-xs text-zinc-500">{formatDate(e.occurred_at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </section>
  );
}

function MyTeamSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const [window, setWindow] = useState<PerfWindow>("7d");
  const [selected, setSelected] = useState<string | null>(null);
  const perf = useResource<api.MyTeamPerformance | null>(() => api.getMyTeamPerformance(window), null, [window]);
  const dept = currentUser?.department?.name ?? null;
  const mgr = currentUser?.manager?.fullName ?? null;
  const team = perf.data?.team ?? [];
  const summary = perf.data?.summary;
  const opsDown = team.some((m) => !m.kpis.opsAvailable);

  if (selected) return <EmployeePerformanceSection userId={selected} onBack={() => setSelected(null)} />;

  return <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
    <div className="xl:col-span-2 space-y-6">
      <Card className="p-5">
        <SectionHeader
          title="My Team"
          subtitle="Your direct reports' performance — activity, approvals, and support load."
          action={<WindowToggle value={window} onChange={setWindow} />}
        />
        <ResourceState
          loading={perf.loading}
          error={perf.error}
          empty={!perf.loading && !perf.error && team.length === 0}
          emptyTitle="No direct reports"
          emptyBody="You don't have anyone reporting to you yet. A manager assignment is set on the Users & Teams page."
        />
        {team.length > 0 && (
          <>
            <TeamMemberKpiTable
              team={team}
              columns={["actions", "approvals", "pending", "tickets", "live", "lastActive"]}
              onSelect={setSelected}
            />
            {opsDown && (
              <p className="mt-2 text-xs text-amber-600">Support ops metrics are temporarily unavailable; activity and approvals are still current.</p>
            )}
          </>
        )}
      </Card>
    </div>
    <div className="space-y-6">
      <Card className="p-5">
        <h3 className="text-lg font-bold">Team summary</h3>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="rounded-2xl border border-zinc-200 p-3"><div className="text-xs text-zinc-500">Reports</div><div className="text-2xl font-bold">{summary?.reports ?? (perf.loading ? "…" : 0)}</div></div>
          <div className="rounded-2xl border border-zinc-200 p-3"><div className="text-xs text-zinc-500">Actions ({window})</div><div className="text-2xl font-bold">{summary?.actionsTotal ?? "—"}</div></div>
          <div className="rounded-2xl border border-zinc-200 p-3"><div className="text-xs text-zinc-500">Approvals actioned</div><div className="text-2xl font-bold">{summary?.approvalsActioned ?? "—"}</div></div>
          <div className="rounded-2xl border border-zinc-200 p-3"><div className="text-xs text-zinc-500">Tickets open / resolved</div><div className="text-2xl font-bold">{summary ? `${summary.ticketsOpen}/${summary.ticketsResolved}` : "—"}</div></div>
        </div>
      </Card>
      <Card className="p-5">
        <h3 className="text-lg font-bold">Where you sit</h3>
        <div className="space-y-3 mt-4 text-sm text-zinc-600">
          <p><span className="text-zinc-400">Your title:</span> {currentUser?.title || "—"}</p>
          <p><span className="text-zinc-400">Department:</span> {dept || "—"}</p>
          <p><span className="text-zinc-400">Reports to:</span> {mgr || "—"}</p>
          <p><span className="text-zinc-400">Direct reports:</span> {perf.loading ? "…" : team.length}</p>
        </div>
      </Card>
    </div>
  </section>;
}

function UsersSection({
  currentUser,
  searchQuery,
}: {
  currentUser: api.CurrentUser | null;
  searchQuery: string;
}) {
  const users = useResource<api.AdminTeamMember[]>(api.listAdminUsers, []);
  const filteredUsers = useMemo(
    () => filterAdminUsers(users.data, searchQuery),
    [users.data, searchQuery],
  );
  const rolesRes = useResource<api.AdminRole[]>(api.listAdminRoles, []);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [orgEditId, setOrgEditId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ tone: "good" | "warn" | "bad"; text: string } | null>(null);

  const canInvite = hasPerm(currentUser, "user:invite");
  const canRemove = hasPerm(currentUser, "user:remove");
  const canAssignOrg = hasPerm(currentUser, "org:manage");
  // Departments only need loading when the operator can actually assign them.
  const deptRes = useResource<api.Department[]>(
    canAssignOrg ? api.listDepartments : async () => [],
    [],
    [canAssignOrg],
  );

  return <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
    <Card className="xl:col-span-2 p-5">
      <SectionHeader
        title="Admin Users & Team Roles"
        subtitle="Control who can access billing, support, engineering logs, AI tools, and sensitive customer data."
        action={
          canInvite ? (
            <Button onClick={() => { setInviteOpen(true); setFeedback(null); }}>Invite Admin</Button>
          ) : null
        }
      />
      <ResourceState
        loading={users.loading}
        error={users.error}
        empty={!users.loading && users.data.length === 0}
        emptyTitle="No admin users yet"
        emptyBody="Invite the first teammate."
      />
      {searchQuery && users.data.length > 0 && (
        <p className="mb-3 text-xs text-zinc-500">
          Filtered by "{searchQuery}" ({filteredUsers.length} of {users.data.length})
        </p>
      )}
      {users.data.length > 0 && filteredUsers.length === 0 && (
        <p className="text-sm text-zinc-500">No admins match "{searchQuery}".</p>
      )}
      {filteredUsers.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-zinc-50 text-zinc-500">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">Department</th>
                <th className="text-left p-3">Manager</th>
                <th className="text-left p-3">Roles</th>
                <th className="text-left p-3">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id} className="border-t border-zinc-100">
                  <td className="p-3 font-medium">{u.fullName || "—"}</td>
                  <td className="p-3 text-zinc-600">{u.email}</td>
                  <td className="p-3 text-zinc-700">{u.title || "—"}</td>
                  <td className="p-3 text-zinc-700">{u.department?.name ?? "—"}</td>
                  <td className="p-3 text-zinc-700">{u.manager?.fullName ?? "—"}</td>
                  <td className="p-3 text-xs text-zinc-700">{(u.roleKeys ?? []).join(", ") || "—"}</td>
                  <td className="p-3"><Badge tone={u.active ? "good" : "warn"}>{u.active ? "Active" : "Inactive"}</Badge></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {canAssignOrg && (
                      <button
                        type="button"
                        onClick={() => { setOrgEditId(u.id); setFeedback(null); }}
                        className="text-xs text-zinc-700 hover:text-zinc-950 mr-3"
                      >
                        Org
                      </button>
                    )}
                    {canInvite && (
                      <button
                        type="button"
                        onClick={() => { setEditId(u.id); setFeedback(null); }}
                        className="text-xs text-zinc-700 hover:text-zinc-950 mr-3"
                      >
                        Edit
                      </button>
                    )}
                    {canRemove && u.active && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!confirm(`Deactivate ${u.email}?`)) return;
                          try {
                            await api.deactivateAdminUser(u.id);
                            setFeedback({ tone: "good", text: `${u.email} deactivated.` });
                            users.refetch();
                          } catch (e: any) {
                            setFeedback({ tone: "bad", text: e?.message ?? "Deactivate failed." });
                          }
                        }}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {feedback && (
        <div className={`mt-4 rounded-2xl border p-3 text-sm ${
          feedback.tone === "good" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : feedback.tone === "warn" ? "border-amber-200 bg-amber-50 text-amber-900"
          : "border-red-200 bg-red-50 text-red-800"
        }`}>{feedback.text}</div>
      )}
    </Card>
    <Card className="p-5">
      <h3 className="text-lg font-bold">Permission Rules</h3>
      <div className="space-y-3 mt-4 text-sm text-zinc-600">
        <p>• Require MFA for every admin.</p>
        <p>• Block raw token visibility.</p>
        <p>• Log every tenant access.</p>
        <p>• Approval required for account suspension.</p>
      </div>
    </Card>

    {inviteOpen && (
      <InviteAdminModal
        roles={rolesRes.data}
        onClose={() => setInviteOpen(false)}
        onSuccess={(email) => {
          setInviteOpen(false);
          setFeedback({ tone: "good", text: `Invited ${email}.` });
          users.refetch();
        }}
        onError={(msg) => setFeedback({ tone: "bad", text: msg })}
      />
    )}

    {editId && (
      <EditAdminModal
        user={users.data.find((u) => u.id === editId)!}
        roles={rolesRes.data}
        onClose={() => setEditId(null)}
        onSuccess={() => {
          setEditId(null);
          setFeedback({ tone: "good", text: "Admin updated." });
          users.refetch();
        }}
        onError={(msg) => setFeedback({ tone: "bad", text: msg })}
      />
    )}

    {orgEditId && (
      <AssignOrgModal
        user={users.data.find((u) => u.id === orgEditId)!}
        departments={deptRes.data}
        allUsers={users.data}
        onClose={() => setOrgEditId(null)}
        onSuccess={() => {
          setOrgEditId(null);
          setFeedback({ tone: "good", text: "Org assignment updated." });
          users.refetch();
        }}
        onError={(msg) => setFeedback({ tone: "bad", text: msg })}
      />
    )}
  </section>;
}

// Modals are rendered as inline overlays via fixed positioning so they
// sit above the rest of the section without depending on a portal lib.
function InviteAdminModal({
  roles,
  onClose,
  onSuccess,
  onError,
}: {
  roles: api.AdminRole[];
  onClose: () => void;
  onSuccess: (email: string) => void;
  onError: (msg: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = email.includes("@") && fullName.trim().length > 1 && selectedRoles.length > 0;
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.inviteAdminUser({ email: email.trim(), fullName: fullName.trim(), roleKeys: selectedRoles });
      onSuccess(email.trim());
    } catch (e: any) {
      onError(e?.message ?? "Invite failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <h3 className="text-lg font-bold">Invite Admin</h3>
      <p className="mt-1 text-sm text-zinc-500">
        New admins receive an invitation email. They must enrol MFA before first login.
      </p>
      <div className="mt-4 space-y-3">
        <div>
          <label className="block text-xs font-semibold text-zinc-700">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm outline-none focus:border-zinc-500"
            placeholder="teammate@ivorycom.com"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-zinc-700">Full name</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm outline-none focus:border-zinc-500"
          />
        </div>
        <RoleMultiSelect roles={roles} selected={selectedRoles} onChange={setSelectedRoles} />
      </div>
      <div className="mt-5 flex gap-2">
        <Button onClick={submit} disabled={!canSubmit || submitting}>
          {submitting ? "..." : "Send invite"}
        </Button>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
      </div>
    </ModalOverlay>
  );
}

function EditAdminModal({
  user,
  roles,
  onClose,
  onSuccess,
  onError,
}: {
  user: api.AdminTeamMember;
  roles: api.AdminRole[];
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [selectedRoles, setSelectedRoles] = useState<string[]>(user.roleKeys ?? []);
  const [active, setActive] = useState<boolean>(user.active);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.updateAdminUser(user.id, { roleKeys: selectedRoles, active });
      onSuccess();
    } catch (e: any) {
      onError(e?.message ?? "Update failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <h3 className="text-lg font-bold">Edit {user.fullName || user.email}</h3>
      <p className="mt-1 text-sm text-zinc-500">{user.email}</p>
      <div className="mt-4 space-y-3">
        <RoleMultiSelect roles={roles} selected={selectedRoles} onChange={setSelectedRoles} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>
      </div>
      <div className="mt-5 flex gap-2">
        <Button onClick={submit} disabled={selectedRoles.length === 0 || submitting}>
          {submitting ? "..." : "Save changes"}
        </Button>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
      </div>
    </ModalOverlay>
  );
}

function AssignOrgModal({
  user,
  departments,
  allUsers,
  onClose,
  onSuccess,
  onError,
}: {
  user: api.AdminTeamMember;
  departments: api.Department[];
  allUsers: api.AdminTeamMember[];
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [title, setTitle] = useState<string>(user.title ?? "");
  const [departmentId, setDepartmentId] = useState<string>(user.department?.id ?? "");
  const [managerId, setManagerId] = useState<string>(user.manager?.id ?? "");
  const [submitting, setSubmitting] = useState(false);

  // A user can't be their own manager; offer only active others.
  const managerChoices = allUsers.filter((u) => u.id !== user.id && u.active);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.assignUserOrg(user.id, {
        title: title.trim() === "" ? null : title.trim(),
        departmentId: departmentId === "" ? null : departmentId,
        managerId: managerId === "" ? null : managerId,
      });
      onSuccess();
    } catch (e: any) {
      onError(e?.message ?? "Assignment failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectCls =
    "w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm bg-white";

  return (
    <ModalOverlay onClose={onClose}>
      <h3 className="text-lg font-bold">Org assignment — {user.fullName || user.email}</h3>
      <p className="mt-1 text-sm text-zinc-500">{user.email}</p>
      <div className="mt-4 space-y-3">
        <label className="block text-sm">
          <span className="text-zinc-500">Title</span>
          <input
            className={selectCls}
            value={title}
            placeholder="e.g. VP Sales"
            maxLength={255}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="text-zinc-500">Department</span>
          <select className={selectCls} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="">— None —</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-zinc-500">Manager</span>
          <select className={selectCls} value={managerId} onChange={(e) => setManagerId(e.target.value)}>
            <option value="">— None —</option>
            {managerChoices.map((m) => (
              <option key={m.id} value={m.id}>{m.fullName || m.email}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-5 flex gap-2">
        <Button onClick={submit} disabled={submitting}>
          {submitting ? "..." : "Save assignment"}
        </Button>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
      </div>
    </ModalOverlay>
  );
}

function RoleMultiSelect({
  roles,
  selected,
  onChange,
}: {
  roles: api.AdminRole[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (key: string) => {
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };
  return (
    <div>
      <label className="block text-xs font-semibold text-zinc-700">Roles</label>
      {roles.length === 0 ? (
        <p className="mt-1 text-xs text-zinc-500">Loading roles…</p>
      ) : (
        <div className="mt-1 grid grid-cols-2 gap-2 max-h-56 overflow-auto rounded-xl border border-zinc-200 p-2">
          {roles.map((r) => (
            <label key={r.key} className="flex items-start gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(r.key)}
                onChange={() => toggle(r.key)}
                className="mt-0.5"
              />
              <span>
                <span className="font-semibold block">{r.name}</span>
                <span className="text-zinc-500">{r.description}</span>
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white border border-zinc-200 shadow-xl p-5 max-h-[90vh] overflow-auto">
        {children}
      </div>
    </div>
  );
}

function BillingSection() {
  const billing = useResource<BillingMetrics | null>(api.getBillingMetrics, null);
  const b = billing.data;
  return <section className="grid grid-cols-1 xl:grid-cols-4 gap-4">
    <StatCard title="MRR" value={b ? formatMoney(b.mrr) : null} sub="Net revenue" icon="dollar"/>
    <StatCard title="Failed Payments" value={b?.failedPayments ?? null} sub={b && b.failedPayments > 0 ? "Need follow-up" : null} icon="warning"/>
    <StatCard title="Trials" value={b?.trials ?? null} sub={b?.trialsAvgDaysLeft != null ? `${b.trialsAvgDaysLeft} days left avg` : null} icon="clock"/>
    <StatCard title="Churn Risk" value={b?.churnRisk ?? null} sub={b && b.churnRisk > 0 ? "Usage dropped" : null} icon="activity"/>
    <Card className="xl:col-span-4 p-5">
      <SectionHeader title="Billing Operations" subtitle="Manage subscriptions, invoices, credits, refunds, trials, and plan migrations." action={<Button>Export Revenue</Button>} />
      <div className="grid md:grid-cols-4 gap-3">
        <Button variant="outline">Extend Trial</Button>
        <Button variant="outline">Issue Credit</Button>
        <Button variant="outline">Retry Invoice</Button>
        <Button variant="outline">Cancel Subscription</Button>
      </div>
      {billing.error && <p className="text-sm text-red-600 mt-4">Failed to load billing metrics: {billing.error}</p>}
    </Card>
  </section>;
}

// C4.1 — Provider Health dashboard: cross-tenant rollup from the CRM
// Integration Hub (uptime, error rate, last sync, DLQ depth per provider).
function ProviderHealthDashboard() {
  const rollup = useResource<api.ProviderHealthRollupResponse>(
    api.getProviderHealthRollup,
    { providers: [], total: 0, generatedAt: "" },
  );
  const rows = sortRollup(rollup.data.providers);
  return (
    <Card className="xl:col-span-3 p-5">
      <SectionHeader
        title="Provider Health"
        subtitle={`Cross-tenant integration health over the last 24h${rollup.data.generatedAt ? ` — generated ${formatDate(rollup.data.generatedAt)}` : ""}.`}
      />
      <ResourceState
        loading={rollup.loading}
        error={rollup.error}
        empty={rows.length === 0}
        emptyTitle="No providers reported"
        emptyBody="Connected providers from GET /integrations/provider-health will appear here."
      />
      {!rollup.loading && !rollup.error && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3 font-medium">Provider</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Tenants</th>
                <th className="py-2 pr-3 font-medium">Events 24h</th>
                <th className="py-2 pr-3 font-medium">Error rate</th>
                <th className="py-2 pr-3 font-medium">Webhook fails</th>
                <th className="py-2 pr-3 font-medium">DLQ</th>
                <th className="py-2 pr-3 font-medium">Sync uptime</th>
                <th className="py-2 font-medium">Last sync</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const tone = rollupTone(p);
                return (
                  <tr key={p.provider} className="border-b border-zinc-100 last:border-0">
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <div className={`h-7 w-7 rounded-xl ${logoFor(p.provider).bg} flex items-center justify-center overflow-hidden`}>
                          <img
                            src={providerLogoUrl(p.provider)}
                            alt=""
                            className="h-4 w-4 object-contain"
                            loading="lazy"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                          />
                        </div>
                        <span className="font-semibold">{p.provider}</span>
                      </div>
                    </td>
                    <td className="py-2 pr-3"><Badge tone={tone}>{rollupLabel(tone)}</Badge></td>
                    <td className="py-2 pr-3">{p.connectedTenants}</td>
                    <td className="py-2 pr-3">{p.events24h}</td>
                    <td className="py-2 pr-3">{formatRate(p.errorRate24h)}</td>
                    <td className="py-2 pr-3">{p.webhookFailures24h}{p.webhooks24h > 0 ? ` / ${p.webhooks24h}` : ""}</td>
                    <td className="py-2 pr-3">{p.dlqDepth > 0 ? <Badge tone="bad">{p.dlqDepth}</Badge> : "0"}</td>
                    <td className="py-2 pr-3">{formatUptime(p.syncUptime24h)}</td>
                    <td className="py-2">{p.lastSyncAt ? formatDate(p.lastSyncAt) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function RetryFailedJobsButton() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const onClick = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.replayDlq();
      setResult(
        r.found === 0
          ? "No failed jobs to retry."
          : `Replayed ${r.replayed} of ${r.found}` +
            (r.skipped ? `, ${r.skipped} skipped` : "") +
            (r.failed ? `, ${r.failed} failed` : "") +
            (r.capped ? " (batch capped at 200)" : "") + ".",
      );
    } catch (e: any) {
      setResult(e?.message ? `Retry failed: ${e.message}` : "Retry failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      {result && <span className="text-sm text-zinc-500">{result}</span>}
      <Button onClick={onClick} disabled={busy}>{busy ? "Retrying…" : "Retry Failed Jobs"}</Button>
    </div>
  );
}

function IntegrationsSection() {
  const apps = useResource<Integration[]>(api.listIntegrations, []);
  const queues = useResource<QueueStat[]>(api.listQueues, []);
  return <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
    <ProviderHealthDashboard />
    <Card className="xl:col-span-2 p-5">
      <SectionHeader title="Integration Operations" subtitle="Monitor connected apps, failed jobs, OAuth tokens, webhooks, and SQS retries." action={<RetryFailedJobsButton />} />
      <ResourceState loading={apps.loading} error={apps.error} empty={apps.data.length === 0} emptyTitle="No integrations yet" emptyBody="Connected apps reported by GET /integrations will appear here." />
      {!apps.loading && !apps.error && apps.data.length > 0 && (
        <div className="grid md:grid-cols-2 gap-3">
          {apps.data.map((app) => {
            const tone = app.status === "healthy" ? "good" : app.status === "down" ? "bad" : "warn";
            const label = app.status === "healthy" ? "OK" : app.status === "down" ? "Down" : "Check";
            const subtext = app.status === "healthy" ? "Healthy" : app.status === "down" ? "Outage" : "Needs attention";
            return (
              <button
                key={app.id}
                type="button"
                onClick={() => { window.location.hash = `integrations/${encodeURIComponent(app.id)}`; }}
                className="rounded-2xl border border-zinc-200 p-4 flex items-center justify-between text-left transition hover:border-zinc-400 hover:bg-zinc-50"
              >
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-2xl ${logoFor(app.id).bg} flex items-center justify-center overflow-hidden`}>
                    <img
                      src={providerLogoUrl(app.id)}
                      alt={`${app.name} logo`}
                      className="h-6 w-6 object-contain"
                      loading="lazy"
                      onError={(e) => {
                        // Fall back to the generic plug icon if the CDN
                        // is unreachable — keeps the row visually intact.
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                  <div><p className="font-semibold">{app.name}</p><p className="text-sm text-zinc-500">{subtext}</p></div>
                </div>
                <Badge tone={tone}>{label}</Badge>
              </button>
            );
          })}
        </div>
      )}
    </Card>
    <Card className="p-5">
      <h3 className="text-lg font-bold">SQS Queues</h3>
      <div className="mt-4">
        <ResourceState loading={queues.loading} error={queues.error} empty={queues.data.length === 0} emptyTitle="No queues reported" emptyBody="Queue depths from GET /integrations/queues will appear here." />
        {!queues.loading && !queues.error && queues.data.length > 0 && (
          <div className="space-y-3">{queues.data.map((q) => <Queue key={q.name} name={q.name} count={String(q.count)} bad={q.isDeadLetter}/>)}</div>
        )}
      </div>
    </Card>
  </section>;
}
function Queue({ name, count, bad = false }) { return <div className="flex items-center justify-between rounded-2xl border border-zinc-200 p-3"><span className="text-sm font-medium">{name}</span><Badge tone={bad?"bad":"info"}>{count}</Badge></div>; }

const PROVIDER_CONSOLE_TABS = ["Health", "Configuration", "Diagnostics"] as const;
type ProviderConsoleTab = typeof PROVIDER_CONSOLE_TABS[number];

function ProviderConsole({
  providerId,
  currentUser,
}: {
  providerId: string;
  currentUser: api.CurrentUser | null;
}) {
  const healthFetcher = React.useCallback(() => api.getProviderHealth(providerId), [providerId]);
  const health = useResource<api.ProviderHealth | null>(healthFetcher, null, [providerId]);
  const [tab, setTab] = useState<ProviderConsoleTab>("Health");
  const [pauseSubmitting, setPauseSubmitting] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  const [pauseError, setPauseError] = useState<string | null>(null);
  const [testRun, setTestRun] = useState<api.ProviderTestResult | null>(null);
  const [testRunning, setTestRunning] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  const canPause = hasPerm(currentUser, "integration:disconnect");
  const canTest = hasPerm(currentUser, "integration:read");

  const togglePaused = async (next: boolean) => {
    setPauseSubmitting(true);
    setPauseError(null);
    try {
      await api.setProviderPaused(providerId, {
        paused: next,
        reason: next && pauseReason.trim() ? pauseReason.trim() : null,
      });
      setPauseReason("");
      health.refetch();
    } catch (err: any) {
      setPauseError(err?.message ?? "Failed to update paused state.");
    } finally {
      setPauseSubmitting(false);
    }
  };

  const runTest = async () => {
    setTestRunning(true);
    setTestError(null);
    try {
      const result = await api.testProviderConnection(providerId);
      setTestRun(result);
    } catch (err: any) {
      setTestError(err?.message ?? "Probe failed.");
    } finally {
      setTestRunning(false);
    }
  };

  const provider = health.data;
  const headerStatus = provider
    ? provider.paused
      ? { tone: "warn" as const, label: "Paused" }
      : provider.status === "healthy"
      ? { tone: "good" as const, label: "Healthy" }
      : provider.status === "down"
      ? { tone: "bad" as const, label: "Down" }
      : { tone: "warn" as const, label: "Needs attention" }
    : null;

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            type="button"
            onClick={() => { window.location.hash = "integrations"; }}
            className="text-sm text-zinc-500 hover:text-zinc-900"
          >
            ← Back to Integrations
          </button>
          <div className="mt-1 flex items-center gap-3">
            <div className={`h-11 w-11 rounded-2xl ${logoFor(providerId).bg} flex items-center justify-center overflow-hidden`}>
              <img
                src={providerLogoUrl(providerId)}
                alt={`${provider?.name ?? providerId} logo`}
                className="h-7 w-7 object-contain"
                loading="lazy"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{provider?.name ?? providerId}</h2>
              {provider && (
                <p className="text-sm text-zinc-500">
                  {provider.category} · {headerStatus && <Badge tone={headerStatus.tone}>{headerStatus.label}</Badge>}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <Card className="p-1">
        <div role="tablist" className="flex gap-1 p-1">
          {PROVIDER_CONSOLE_TABS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={`flex-1 rounded-xl px-3 py-2 text-sm font-medium transition ${
                tab === t
                  ? "bg-zinc-950 text-white"
                  : "text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </Card>

      <ResourceState
        loading={health.loading}
        error={health.error}
        empty={!health.loading && !health.error && !provider}
        emptyTitle="Provider not found"
        emptyBody="The id in the URL didn't match any platform-supported provider."
      />

      {provider && tab === "Health" && (
        <Card className="p-5 space-y-4">
          <SectionHeader
            title="Health"
            subtitle="Operational status across the platform — same view as customer-side integrations once Phase D wires per-tenant data."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Info label="Status" value={headerStatus?.label ?? "—"} />
            <Info label="Last sync" value={provider.lastSyncAt ? formatDate(provider.lastSyncAt) : "—"} />
            <Info
              label="Connected tenants"
              value={provider.connectedTenants == null ? "—" : String(provider.connectedTenants)}
            />
            <Info label="Failures last 24h" value={String(provider.failureCount24h)} />
            <Info label="Paused" value={provider.paused ? "Yes" : "No"} />
            {provider.paused && provider.pausedAt && (
              <Info label="Paused at" value={formatDate(provider.pausedAt)} />
            )}
            {provider.paused && provider.pausedBy && (
              <Info label="Paused by" value={provider.pausedBy} />
            )}
            {provider.paused && provider.pausedReason && (
              <Info label="Reason" value={provider.pausedReason} />
            )}
          </div>

          <div className="border-t border-zinc-100 pt-4">
            <h4 className="font-semibold text-zinc-900">Sync kill-switch</h4>
            <p className="text-sm text-zinc-500 mt-1">
              Stops every tenant's sync for this provider until resumed. Use when an
              upstream incident or rate-limit storm is affecting all tenants.
            </p>
            {!provider.paused && canPause && (
              <div className="mt-3 space-y-2">
                <input
                  type="text"
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  placeholder="Optional reason (visible in the audit log)"
                  className="w-full rounded-xl border border-zinc-200 bg-white p-2 text-sm outline-none focus:border-zinc-500"
                />
                <Button
                  onClick={() => togglePaused(true)}
                  disabled={pauseSubmitting}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {pauseSubmitting ? "Pausing…" : "Pause syncs"}
                </Button>
              </div>
            )}
            {provider.paused && canPause && (
              <div className="mt-3">
                <Button onClick={() => togglePaused(false)} disabled={pauseSubmitting}>
                  {pauseSubmitting ? "Resuming…" : "Resume syncs"}
                </Button>
              </div>
            )}
            {!canPause && (
              <p className="mt-3 text-xs text-zinc-500">
                You don't have <code>integration:disconnect</code> — pause is read-only for your role.
              </p>
            )}
            {pauseError && (
              <p className="mt-2 text-sm text-red-700">{pauseError}</p>
            )}
          </div>
        </Card>
      )}

      {provider && tab === "Configuration" && (
        <ProviderConfigurationTab providerId={providerId} canRotate={canPause} />
      )}

      {provider && tab === "Diagnostics" && (
        <Card className="p-5 space-y-4">
          <SectionHeader
            title="Diagnostics"
            subtitle="Run a live connectivity probe to confirm credentials and network reachability."
          />
          <div>
            {canTest ? (
              <Button onClick={runTest} disabled={testRunning}>
                {testRunning ? "Running…" : "Test connection"}
              </Button>
            ) : (
              <p className="text-xs text-zinc-500">
                You don't have <code>integration:read</code> — diagnostics are unavailable.
              </p>
            )}
          </div>
          {testError && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {testError}
            </div>
          )}
          {testRun && (
            <div
              className={`rounded-2xl border p-4 text-sm ${
                testRun.status === "ok"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : testRun.status === "warn"
                  ? "border-amber-200 bg-amber-50 text-amber-900"
                  : "border-red-200 bg-red-50 text-red-700"
              }`}
            >
              <p className="font-semibold">
                {testRun.status === "ok"
                  ? "Connection ok"
                  : testRun.status === "warn"
                  ? "Probe returned a warning"
                  : "Probe failed"}
              </p>
              <p className="mt-1">{testRun.message}</p>
              <p className="mt-2 text-xs opacity-75">Checked {formatDate(testRun.checkedAt)}</p>
            </div>
          )}
        </Card>
      )}
    </section>
  );
}

function ProviderConfigurationTab({
  providerId,
  canRotate,
}: {
  providerId: string;
  canRotate: boolean;
}) {
  const fetcher = React.useCallback(() => api.getProviderConfig(providerId), [providerId]);
  const cfg = useResource<api.ProviderConfig | null>(fetcher, null, [providerId]);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: "good" | "bad"; text: string } | null>(null);

  const data = cfg.data;

  return (
    <Card className="p-5 space-y-4">
      <SectionHeader
        title="Configuration"
        subtitle="OAuth client identity registered in the provider's developer console. Rotate the secret here when a credential leaks or expires."
      />
      <ResourceState
        loading={cfg.loading}
        error={cfg.error}
        empty={!cfg.loading && !cfg.error && !data}
        emptyTitle="No config returned"
        emptyBody="The /config endpoint returned no body — check admin-bff logs."
      />

      {data && !data.configured && (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50 p-5 text-sm text-zinc-600">
          <p className="font-medium text-zinc-900">Not configured</p>
          <p className="mt-1">{data.notConfiguredReason ?? "OAuth client is not yet registered for this provider."}</p>
        </div>
      )}

      {data && data.configured && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Info label={data.clientIdLabel} value={data.clientId ?? "—"} />
            <Info
              label="Last rotated"
              value={data.lastRotatedAt ? formatDate(data.lastRotatedAt) : "Never recorded"}
            />
            <Info label={data.secretLabel} value={data.hasSecret ? "•••••••• (hidden)" : "Not set"} />
          </div>

          {data.redirectUris.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Authorized redirect URIs
              </h4>
              <p className="mt-1 text-xs text-zinc-500">
                Read-only — managed in the provider's developer console. Update there first; this list documents what we expect.
              </p>
              <ul className="mt-2 space-y-1 text-sm font-mono text-zinc-700">
                {data.redirectUris.map((uri) => (
                  <li key={uri} className="rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2 break-all">{uri}</li>
                ))}
              </ul>
            </div>
          )}

          {data.webhookUrl && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Webhook URL</h4>
              <p className="mt-1 rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm font-mono text-zinc-700 break-all">
                {data.webhookUrl}
              </p>
            </div>
          )}

          <div className="border-t border-zinc-100 pt-4">
            <h4 className="font-semibold text-zinc-900">Rotate {data.secretLabel}</h4>
            <p className="text-sm text-zinc-500 mt-1">
              Generate a new secret in the provider's developer console, paste it as text below, and submit. The previous version stays in AWS Secrets Manager as <code>AWSPREVIOUS</code> for emergency rollback.
            </p>
            {canRotate ? (
              <div className="mt-3">
                <Button onClick={() => { setRotateOpen(true); setFeedback(null); }}>Rotate secret</Button>
              </div>
            ) : (
              <p className="mt-3 text-xs text-zinc-500">
                You don't have <code>integration:disconnect</code> — rotate is read-only for your role.
              </p>
            )}
          </div>
        </>
      )}

      {feedback && (
        <div className={`rounded-2xl border p-3 text-sm ${
          feedback.tone === "good"
            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
            : "border-red-200 bg-red-50 text-red-800"
        }`}>{feedback.text}</div>
      )}

      {rotateOpen && data && (
        <RotateSecretModal
          providerId={providerId}
          secretLabel={data.secretLabel}
          onClose={() => setRotateOpen(false)}
          onSuccess={(rotatedAt) => {
            setRotateOpen(false);
            setFeedback({
              tone: "good",
              text: `Rotated successfully at ${formatDate(rotatedAt)}. Customer integrations using this provider will pick up the new secret on their next sync.`,
            });
            cfg.refetch();
          }}
          onError={(msg) => setFeedback({ tone: "bad", text: msg })}
        />
      )}
    </Card>
  );
}

function RotateSecretModal({
  providerId,
  secretLabel,
  onClose,
  onSuccess,
  onError,
}: {
  providerId: string;
  secretLabel: string;
  onClose: () => void;
  onSuccess: (rotatedAt: string) => void;
  onError: (msg: string) => void;
}) {
  // Two-step confirmation: typed-input first, then explicit "Confirm" so
  // a fat-finger on the rotate button can't change a Prod credential.
  const [newSecret, setNewSecret] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const trimmed = newSecret.trim();
  const canSubmit = trimmed.length >= 8 && confirm === providerId && !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      const result = await api.rotateProviderSecret(providerId, trimmed);
      onSuccess(result.rotatedAt);
    } catch (e: any) {
      onError(e?.message ?? "Rotate failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <h3 className="text-lg font-bold">Rotate {secretLabel}</h3>
      <p className="mt-1 text-sm text-zinc-500">
        Paste the new secret as text from the provider's developer console — never OCR'd from a screenshot. Submitting writes a new version to AWS Secrets Manager and fresh service tasks pick it up on next boot.
      </p>
      <div className="mt-4 space-y-3">
        <div>
          <label className="block text-xs font-semibold text-zinc-700">New {secretLabel}</label>
          <input
            type="text"
            spellCheck={false}
            autoComplete="off"
            value={newSecret}
            onChange={(e) => setNewSecret(e.target.value)}
            className="w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm font-mono outline-none focus:border-zinc-500"
            placeholder="paste new secret here"
          />
          <p className="mt-1 text-xs text-zinc-500">Whitespace is trimmed. Length: {trimmed.length}.</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-zinc-700">
            Type <code>{providerId}</code> to confirm
          </label>
          <input
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm outline-none focus:border-zinc-500"
            placeholder={providerId}
          />
        </div>
      </div>
      <div className="mt-5 flex gap-2">
        <Button onClick={submit} disabled={!canSubmit} className="bg-amber-600 hover:bg-amber-700 text-white">
          {submitting ? "Rotating…" : "Rotate secret"}
        </Button>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
      </div>
    </ModalOverlay>
  );
}

function filterTicketsByQuery(tickets: Ticket[], query: string): Ticket[] {
  const q = query.trim().toLowerCase();
  if (!q) return tickets;
  return tickets.filter((t) =>
    t.customer.toLowerCase().includes(q) ||
    t.issue.toLowerCase().includes(q) ||
    (t.assignedTo ?? "").toLowerCase().includes(q)
  );
}

function SupportSection({ currentUser, searchQuery }: { currentUser: api.CurrentUser | null; searchQuery: string }) {
  const ticketsRes = useResource<Ticket[]>(api.listTickets, []);
  const teamRes = useResource<api.AdminTeamMember[]>(api.listAdminUsers, []);
  const macrosRes = useResource<api.SupportMacro[]>(api.listSupportMacros, []);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [queueFilter, setQueueFilter] = useState<typeof supportInboxTabs[number]>("All");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalationHistory, setEscalationHistory] = useState<api.EscalationEntry[]>([]);
  const [escalationHistoryLoading, setEscalationHistoryLoading] = useState(false);
  const [escalationHistoryError, setEscalationHistoryError] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [macrosOpen, setMacrosOpen] = useState(false);
  const [closeFeedback, setCloseFeedback] = useState<{ tone: "good" | "bad"; text: string } | null>(null);
  const canClose = hasPerm(currentUser, "support:close");
  const canAssign = hasPerm(currentUser, "support:assign");
  const filteredByQuery = useMemo(
    () => filterTicketsByQuery(ticketsRes.data, searchQuery),
    [ticketsRes.data, searchQuery],
  );
  const visibleTickets = filteredByQuery.filter((ticket) => {
    if (queueFilter === "All") return true;
    if (queueFilter === "Pending") return ticket.status === "Pending" || ticket.status === "Open";
    if (queueFilter === "Escalated") return ticket.status === "Escalated";
    // "Closed" — tickets resolved via the Close ticket button.
    return ticket.status === "Resolved";
  });
  // "Open" in the header counter == anything still actively needing
  // attention — explicitly excludes Resolved, otherwise the metric
  // never moves after closing a ticket.
  const openCount = ticketsRes.data.filter((ticket) => ticket.status !== "Resolved").length;
  const escalatedCount = ticketsRes.data.filter((ticket) => ticket.status === "Escalated").length;
  const closedCount = ticketsRes.data.filter((ticket) => ticket.status === "Resolved").length;
  // Resolved tickets keep their unread counter from before the close —
  // exclude them so the badge in the sidebar doesn't ring forever.
  const unreadCount = ticketsRes.data
    .filter((ticket) => ticket.status !== "Resolved")
    .reduce((sum, ticket) => sum + ticket.unread, 0);

  useEffect(() => {
    if (!selected && visibleTickets.length > 0) setSelected(visibleTickets[0]);
    if (selected && !visibleTickets.find((t) => t.id === selected.id)) {
      setSelected(visibleTickets[0] ?? null);
    }
  }, [selected, visibleTickets]);

  const messagesRes = useResource<ChatMessage[]>(
    () => (selected ? api.listTicketMessages(selected.id) : Promise.resolve([])),
    [],
    [selected?.id]
  );
  const contextRes = useResource<CustomerContext | null>(
    () => (selected ? api.getCustomerContext(selected.id) : Promise.resolve(null)),
    null,
    [selected?.id]
  );

  async function handleSend() {
    if (!selected || !draft.trim() || sending) return;
    setSending(true);
    try {
      await api.sendTicketMessage({ ticketId: selected.id, text: draft.trim() });
      setDraft("");
      messagesRes.refetch();
    } catch (e: any) {
      window.alert(`Failed to send: ${e?.message ?? e}`);
    } finally {
      setSending(false);
    }
  }

  function useSuggestedReply() {
    if (contextRes.data?.suggestedReply) setDraft(contextRes.data.suggestedReply);
  }

  async function handleClose() {
    if (!selected || closing) return;
    if (!confirm(`Close this ticket from ${selected.customer}? It will move to Resolved and the customer can reopen by replying.`)) return;
    setClosing(true);
    setCloseFeedback(null);
    try {
      await api.closeTicket(selected.id);
      setCloseFeedback({ tone: "good", text: "Ticket closed." });
      setSelected(null);
      ticketsRes.refetch();
    } catch (e: any) {
      setCloseFeedback({ tone: "bad", text: e?.message ?? "Failed to close ticket." });
    } finally {
      setClosing(false);
    }
  }

  async function handleOpenEscalate() {
    if (!selected) return;
    setEscalateOpen(true);
    setEscalationHistoryLoading(true);
    setEscalationHistoryError(null);
    try {
      const rows = await api.listEscalations(selected.id);
      setEscalationHistory(rows);
    } catch (e: any) {
      setEscalationHistoryError(e?.message ?? "Failed to load history");
      setEscalationHistory([]);
    } finally {
      setEscalationHistoryLoading(false);
    }
  }

  async function handleEscalateSubmit(args: {
    queue: api.EscalationQueue;
    teamId?: string;
    reason: string;
    internalNote: string;
  }) {
    if (!selected || escalating) return;
    setEscalating(true);
    setCloseFeedback(null);
    try {
      const result = await api.escalateTicket(selected.id, {
        queue: args.queue,
        teamId: args.teamId,
        reason: args.reason,
      });
      if (args.internalNote) {
        // Best-effort; the escalation itself is the durable action.
        await api.addInternalNote(selected.id, args.internalNote).catch(() => undefined);
      }
      setCloseFeedback({
        tone: "good",
        text: `Escalated to ${result.history.queue ?? args.queue}.`,
      });
      setEscalateOpen(false);
      ticketsRes.refetch();
    } catch (e: any) {
      setCloseFeedback({ tone: "bad", text: e?.message ?? "Failed to escalate." });
    } finally {
      setEscalating(false);
    }
  }

  async function handleAssign(ownerId: string) {
    if (!selected || assigning) return;
    setAssigning(true);
    setCloseFeedback(null);
    try {
      await api.assignTicketOwner(selected.id, ownerId);
      const owner = teamRes.data.find((m) => m.id === ownerId);
      setCloseFeedback({ tone: "good", text: `Assigned to ${owner?.fullName ?? owner?.email ?? "owner"}.` });
      setAssignOpen(false);
      ticketsRes.refetch();
    } catch (e: any) {
      setCloseFeedback({ tone: "bad", text: e?.message ?? "Failed to assign owner." });
    } finally {
      setAssigning(false);
    }
  }

  function pickMacro(macro: api.SupportMacro) {
    setDraft((prev) => (prev ? `${prev}\n\n${macro.body}` : macro.body));
    setMacrosOpen(false);
  }

  return <section className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-12 gap-6 min-h-[650px]">
    <Card className="xl:col-span-3 lg:col-span-1 p-4">
      <SectionHeader title="Support Inbox" subtitle="Ticket triage for bugs, billing issues, onboarding help, and escalations." />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Open</p>
          <p className="mt-2 text-lg font-bold text-zinc-950">{openCount}</p>
        </div>
        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Escalated</p>
          <p className="mt-2 text-lg font-bold text-zinc-950">{escalatedCount}</p>
        </div>
        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Unread</p>
          <p className="mt-2 text-lg font-bold text-zinc-950">{unreadCount}</p>
        </div>
        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Closed</p>
          <p className="mt-2 text-lg font-bold text-zinc-950">{closedCount}</p>
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        {supportInboxTabs.map((mode) => <button key={mode} onClick={() => setQueueFilter(mode)} className={`px-3 py-2 rounded-2xl text-xs font-semibold ${queueFilter===mode?"bg-zinc-950 text-white":"bg-zinc-100 text-zinc-600"}`}>{mode}</button>)}
      </div>
      <ResourceState loading={ticketsRes.loading} error={ticketsRes.error} empty={visibleTickets.length === 0} emptyTitle="Inbox is empty" emptyBody="Tickets from GET /support/tickets will appear here." />
      {!ticketsRes.loading && !ticketsRes.error && visibleTickets.length > 0 && (
        <div className="space-y-3">
          {visibleTickets.map((t)=><button key={t.id} onClick={()=>setSelected(t)} className={`w-full text-left p-4 rounded-2xl border transition ${selected?.id===t.id?"border-zinc-950 bg-zinc-50":"border-zinc-200 bg-white hover:bg-zinc-50"}`}>
            <div className="flex items-center justify-between"><p className="font-semibold">{t.customer}</p>{t.unread>0 && <Badge tone="bad">{t.unread}</Badge>}</div>
            <p className="text-sm text-zinc-500 mt-1">{t.issue}</p>
            <div className="flex gap-2 mt-3"><Badge tone={t.priority==="High"?"bad":t.priority==="Medium"?"warn":"default"}>{t.priority}</Badge><Badge tone={t.status==="Live Chat"?"info":"default"}>{t.status}</Badge></div>
          </button>)}
        </div>
      )}
    </Card>

    <Card className="xl:col-span-6 lg:col-span-1 p-0 overflow-hidden">
      {!selected && (
        <div className="h-full min-h-[500px] flex items-center justify-center p-8 text-center">
          <div><p className="font-medium text-zinc-700">No ticket selected</p><p className="text-sm text-zinc-500 mt-1">Select a ticket from the queue to start a conversation.</p></div>
        </div>
      )}
      {selected && (
        <>
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between bg-white">
            <div>
              <h3 className="font-bold">{selected.customer}</h3>
              <p className="text-sm text-zinc-500">{selected.assignedTo ? `Assigned to ${selected.assignedTo}` : "Unassigned"}</p>
            </div>
            <div className="flex gap-2"><Badge tone={selected.status === "Live Chat" ? "good" : "default"}>{selected.status === "Live Chat" ? "Customer Online" : selected.status}</Badge></div>
          </div>
          <div className="h-[320px] md:h-[430px] overflow-auto p-4 bg-zinc-50 space-y-3">
            {messagesRes.loading && <p className="text-sm text-zinc-500">Loading messages…</p>}
            {messagesRes.error && <p className="text-sm text-red-600">Failed to load messages: {messagesRes.error}</p>}
            {!messagesRes.loading && !messagesRes.error && messagesRes.data.length === 0 && <p className="text-sm text-zinc-500">No messages yet.</p>}
            {messagesRes.data.map((m)=><div key={m.id} className={`flex ${m.by==="admin"?"justify-end":"justify-start"}`}>
              <div className={`max-w-[78%] rounded-2xl p-3 text-sm ${m.by==="admin"?"bg-zinc-950 text-white":m.by==="system"?"bg-amber-50 text-amber-800 border border-amber-200":"bg-white border border-zinc-200 text-zinc-800"}`}>
                <p>{m.text}</p><p className={`text-[11px] mt-1 ${m.by==="admin"?"text-zinc-300":"text-zinc-400"}`}>{m.time}</p>
              </div>
            </div>)}
          </div>
          <div className="p-3 border-t border-zinc-200 bg-white">
            <div className="flex flex-wrap gap-2 mb-3">
              <button type="button" onClick={useSuggestedReply} disabled={!contextRes.data?.suggestedReply} className="text-xs px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 disabled:opacity-50">Use AI Reply</button>
              <button type="button" onClick={() => setMacrosOpen(true)} disabled={macrosRes.data.length === 0} className="text-xs px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 disabled:opacity-50" title={macrosRes.data.length === 0 ? "No macros configured yet" : "Insert a saved macro"}>Send Help Article</button>
              <button type="button" onClick={() => setAssignOpen(true)} disabled={!canAssign} className="text-xs px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 disabled:opacity-50" title={canAssign ? "Reassign this ticket to another admin" : "support:assign required"}>Assign Owner</button>
              <button type="button" onClick={handleOpenEscalate} disabled={!canAssign || escalating} className="text-xs px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 disabled:opacity-50">{escalating ? "Escalating…" : selected.status === "Escalated" ? "Re-route queue" : "Escalate…"}</button>
              {canClose && selected.status !== "Resolved" && (
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={closing}
                  className="text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {closing ? "Closing…" : "Close ticket"}
                </button>
              )}
              {selected.status === "Resolved" && (
                <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Resolved
                </span>
              )}
            </div>
            {closeFeedback && (
              <div className={`mb-3 rounded-2xl border p-2 text-xs ${
                closeFeedback.tone === "good"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-red-200 bg-red-50 text-red-800"
              }`}>{closeFeedback.text}</div>
            )}
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
              <input value={draft} onChange={(e) => setDraft(e.target.value)} className="flex-1 rounded-2xl border border-zinc-200 px-4 py-3 outline-none" placeholder="Type a reply to the customer..."/>
              <Button type="submit" disabled={sending || !draft.trim()}><Icon name="send" className="mr-2"/>{sending ? "Sending…" : "Send"}</Button>
            </form>
          </div>
        </>
      )}
    </Card>

    <Card className="xl:col-span-3 lg:col-span-2 p-5">
      <h3 className="text-lg font-bold">Customer 360 Context</h3>
      {!selected && <p className="text-sm text-zinc-500 mt-3">Select a ticket to view customer context.</p>}
      {selected && contextRes.loading && <p className="text-sm text-zinc-500 mt-3">Loading…</p>}
      {selected && contextRes.error && <p className="text-sm text-red-600 mt-3">Failed to load context: {contextRes.error}</p>}
      {selected && !contextRes.loading && !contextRes.error && !contextRes.data && <p className="text-sm text-zinc-500 mt-3">No customer context returned.</p>}
      {selected && contextRes.data && (
        <>
          <div className="space-y-3 mt-4 text-sm">
            <Info label="Plan" value={contextRes.data.plan}/>
            <Info label="Health" value={contextRes.data.health}/>
            <Info label="Last login" value={contextRes.data.lastLogin}/>
            <Info label="Open jobs" value={contextRes.data.openJobs}/>
            <Info label="Integration" value={contextRes.data.integration}/>
            <Info label="MRR" value={contextRes.data.mrr}/>
          </div>
          {contextRes.data.suggestedReply && (
            <div className="mt-5 rounded-2xl bg-zinc-50 border border-zinc-200 p-4">
              <div className="flex items-center gap-2 font-semibold text-sm"><Icon name="bot"/>AI Suggested Reply</div>
              <p className="text-sm text-zinc-600 mt-2">{contextRes.data.suggestedReply}</p>
            </div>
          )}
        </>
      )}
      <div className="grid gap-2 mt-4">
        <Button onClick={useSuggestedReply} disabled={!contextRes.data?.suggestedReply}>Use AI Reply</Button>
        <Button variant="outline" onClick={handleOpenEscalate} disabled={!canAssign || escalating || !selected}>{escalating ? "Escalating…" : selected?.status === "Escalated" ? "Re-route queue" : "Escalate to Queue"}</Button>
      </div>
    </Card>
    {assignOpen && selected && (
      <AssignOwnerModal
        members={teamRes.data}
        currentAssigneeId={selected.assignedTo ?? null}
        onPick={handleAssign}
        onClose={() => setAssignOpen(false)}
        busy={assigning}
      />
    )}
    {macrosOpen && (
      <MacroPickerModal
        macros={macrosRes.data}
        onPick={pickMacro}
        onClose={() => setMacrosOpen(false)}
      />
    )}
    {escalateOpen && selected && (
      <EscalationModal
        ticket={selected}
        history={escalationHistory}
        historyLoading={escalationHistoryLoading}
        historyError={escalationHistoryError}
        busy={escalating}
        onSubmit={handleEscalateSubmit}
        onClose={() => setEscalateOpen(false)}
      />
    )}
  </section>;
}

function ModalShell({ title, onClose, children, footer }: { title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode }) {
  // Sticky header + footer with a scrolling body keeps the action buttons
  // reachable on short viewports — without this, modals taller than the
  // viewport (e.g. EscalationModal with queue grid + history + note)
  // pushed the footer below the fold and the submit button became unclickable.
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3 shrink-0">
          <h3 className="font-semibold">{title}</h3>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900"><Icon name="x"/></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto grow">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-zinc-200 px-5 py-3 shrink-0">{footer}</div>}
      </div>
    </div>
  );
}

function AssignOwnerModal({ members, currentAssigneeId, onPick, onClose, busy }: {
  members: api.AdminTeamMember[];
  currentAssigneeId: string | null;
  onPick: (ownerId: string) => void;
  onClose: () => void;
  busy: boolean;
}) {
  const [filter, setFilter] = useState("");
  const filtered = members.filter((m) => {
    if (!m.active) return false;
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return m.fullName.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
  });
  return (
    <ModalShell title="Assign ticket owner" onClose={onClose}>
      <input
        autoFocus
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search admins by name or email…"
        className="mb-3 w-full rounded-2xl border border-zinc-200 px-3 py-2 text-sm outline-none"
      />
      <div className="max-h-72 overflow-auto divide-y divide-zinc-100">
        {filtered.length === 0 ? (
          <p className="text-sm text-zinc-500 py-6 text-center">No matching admins.</p>
        ) : filtered.map((m) => (
          <button
            key={m.id}
            type="button"
            disabled={busy}
            onClick={() => onPick(m.id)}
            className="w-full flex items-center justify-between py-3 text-left hover:bg-zinc-50 disabled:opacity-50 px-2"
          >
            <div>
              <p className="text-sm font-semibold">{m.fullName}</p>
              <p className="text-xs text-zinc-500">{m.email}</p>
            </div>
            {currentAssigneeId === m.id && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Current</span>
            )}
          </button>
        ))}
      </div>
    </ModalShell>
  );
}

function MacroPickerModal({ macros, onPick, onClose }: {
  macros: api.SupportMacro[];
  onPick: (macro: api.SupportMacro) => void;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState("");
  const filtered = macros.filter((m) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return m.name.toLowerCase().includes(q) || m.body.toLowerCase().includes(q);
  });
  return (
    <ModalShell title="Insert macro" onClose={onClose}>
      <input
        autoFocus
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search macros…"
        className="mb-3 w-full rounded-2xl border border-zinc-200 px-3 py-2 text-sm outline-none"
      />
      <div className="max-h-72 overflow-auto divide-y divide-zinc-100">
        {filtered.length === 0 ? (
          <p className="text-sm text-zinc-500 py-6 text-center">No macros configured. Manage them under Settings → Support Macros.</p>
        ) : filtered.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onPick(m)}
            className="w-full text-left py-3 px-2 hover:bg-zinc-50"
          >
            <p className="text-sm font-semibold">{m.name}</p>
            <p className="text-xs text-zinc-500 mt-1 line-clamp-2 whitespace-pre-wrap">{m.body}</p>
          </button>
        ))}
      </div>
    </ModalShell>
  );
}
// Mirrors the backend tickets.service.suggestQueueFromText regex set so
// the modal can show a "Suggested: legal" chip before the operator
// touches the queue picker. Pure client-side hint — submission still
// goes through the backend, so a stale frontend mapping is non-fatal.
function suggestQueueFromText(text: string): api.EscalationQueue | null {
  const lower = (text ?? "").toLowerCase();
  if (!lower.trim()) return null;
  if (/\b(legal|gdpr|compliance|contract|dpa|nda|lawsuit)\b/.test(lower)) return "legal";
  if (/\b(security|breach|vulnerab|cve|password|leak|csrf|xss|injection)\b/.test(lower)) return "security";
  if (/\b(billing|invoice|refund|charge|payment|subscription|plan)\b/.test(lower)) return "billing";
  if (/\b(integration|api|webhook|oauth|sync|hubspot|salesforce|pipedrive)\b/.test(lower)) return "integration";
  if (/\b(demo|pricing|sales|quote|trial|upgrade)\b/.test(lower)) return "sales";
  return null;
}

const QUEUE_LABEL: Record<api.EscalationQueue, string> = {
  legal: "Legal",
  billing: "Billing",
  security: "Security",
  engineering: "Engineering",
  sales: "Sales",
  integration: "Integration",
};

const QUEUE_DESCRIPTION: Record<api.EscalationQueue, string> = {
  legal: "GDPR / contract / compliance review",
  billing: "Refunds, invoicing, plan changes",
  security: "Breaches, vulnerabilities, account takeover",
  engineering: "Tier-2 bugs, root-cause analysis",
  sales: "Pricing, demos, upgrades",
  integration: "OAuth, webhooks, sync failures",
};

function EscalationModal({
  ticket,
  history,
  historyLoading,
  historyError,
  busy,
  onSubmit,
  onClose,
}: {
  ticket: Ticket;
  history: api.EscalationEntry[];
  historyLoading: boolean;
  historyError: string | null;
  busy: boolean;
  onSubmit: (args: {
    queue: api.EscalationQueue;
    teamId?: string;
    reason: string;
    internalNote: string;
  }) => void;
  onClose: () => void;
}) {
  const suggested = useMemo(
    () => suggestQueueFromText(ticket.issue ?? ""),
    [ticket.issue],
  );
  const [queue, setQueue] = useState<api.EscalationQueue>(suggested ?? "engineering");
  const [teamId, setTeamId] = useState("");
  const [reason, setReason] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const reasonValid = reason.trim().length >= 3;
  const lastEscalation = history[0];

  return (
    <ModalShell
      title={`Escalate ticket from ${ticket.customer}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm rounded-2xl bg-zinc-100 text-zinc-700 hover:bg-zinc-200 disabled:opacity-50"
          >Cancel</button>
          <button
            type="button"
            disabled={busy || !reasonValid}
            onClick={() => onSubmit({
              queue,
              teamId: teamId.trim() || undefined,
              reason: reason.trim(),
              internalNote: internalNote.trim(),
            })}
            className="px-4 py-2 text-sm rounded-2xl bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50"
          >{busy ? "Escalating…" : "Escalate"}</button>
        </>
      }
    >
      {suggested && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs text-amber-900">
          <Icon name="bot" />
          <span>Suggested queue: <span className="font-semibold">{QUEUE_LABEL[suggested]}</span> (from subject keywords)</span>
        </div>
      )}

      {lastEscalation && (
        <div className="mb-4 rounded-2xl border border-zinc-200 bg-zinc-50 p-3 text-xs">
          <p className="font-semibold text-zinc-700">Previously routed</p>
          <p className="mt-1 text-zinc-600">
            {lastEscalation.queue ? `→ ${QUEUE_LABEL[lastEscalation.queue as api.EscalationQueue] ?? lastEscalation.queue}` : ""}
            {lastEscalation.escalatedByEmail ? ` by ${lastEscalation.escalatedByEmail}` : ""}
            {lastEscalation.escalatedAt ? ` · ${new Date(lastEscalation.escalatedAt).toLocaleString()}` : ""}
          </p>
          {lastEscalation.reason && (
            <p className="mt-1 text-zinc-500 italic">"{lastEscalation.reason}"</p>
          )}
        </div>
      )}

      <fieldset>
        <legend className="text-sm font-semibold text-zinc-800">Queue</legend>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {api.ESCALATION_QUEUES.map((q) => (
            <label
              key={q}
              className={`cursor-pointer rounded-2xl border p-3 text-left text-sm transition ${
                queue === q
                  ? "border-zinc-950 bg-zinc-50"
                  : "border-zinc-200 bg-white hover:bg-zinc-50"
              }`}
            >
              <input
                type="radio"
                name="escalation-queue"
                value={q}
                checked={queue === q}
                onChange={() => setQueue(q)}
                className="sr-only"
              />
              <p className="font-semibold capitalize">{QUEUE_LABEL[q]}</p>
              <p className="text-xs text-zinc-500 mt-1">{QUEUE_DESCRIPTION[q]}</p>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="mt-4 block">
        <span className="text-sm font-semibold text-zinc-800">Team (optional)</span>
        <input
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
          placeholder="e.g. emea-legal, payments-tier2"
          className="mt-1 w-full rounded-2xl border border-zinc-200 px-3 py-2 text-sm outline-none"
        />
      </label>

      <label className="mt-4 block">
        <span className="text-sm font-semibold text-zinc-800">Reason</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Briefly explain why this needs the new queue (customer-visible)."
          rows={3}
          className="mt-1 w-full rounded-2xl border border-zinc-200 px-3 py-2 text-sm outline-none"
        />
        <p className="mt-1 text-xs text-zinc-500">
          The customer will see: <span className="italic">"Escalated to the {queue} queue. Reason: {reason || "…"}"</span>
        </p>
      </label>

      <label className="mt-4 block">
        <span className="text-sm font-semibold text-zinc-800">Internal note (optional)</span>
        <textarea
          value={internalNote}
          onChange={(e) => setInternalNote(e.target.value)}
          placeholder="Operator-only context — invisible to the customer. e.g. 'Tried steps 1-3 in the runbook, no luck.'"
          rows={3}
          className="mt-1 w-full rounded-2xl border border-zinc-200 px-3 py-2 text-sm outline-none"
        />
      </label>

      {historyLoading && (
        <p className="mt-3 text-xs text-zinc-500">Loading escalation history…</p>
      )}
      {historyError && (
        <p className="mt-3 text-xs text-red-600">Failed to load history: {historyError}</p>
      )}
    </ModalShell>
  );
}

function Info({ label, value }) { return <div className="flex items-center justify-between border-b border-zinc-100 pb-2"><span className="text-zinc-500">{label}</span><span className="font-medium text-right">{value}</span></div>; }

// Polling cadence: ticket-list still polls every 5s for cross-ticket
// awareness (cheap; doesn't need WS). Message stream is now socket-
// driven with MESSAGE_POLL_MS as the fallback when the chat namespace
// handshake fails repeatedly.
const TICKET_POLL_MS = 5_000;
const MESSAGE_POLL_MS = 2_000;

// Socket transport to support-service's chat namespace. Same path
// constants as LiveChatsPage / SupportWidget so all three consumers
// share an operations story when SOCKET_IO_REDIS is rotated.
const WS_NAMESPACE = "/chat";
const WS_PATH = "/api/support/socket.io";
const SOCKET_FAILURE_THRESHOLD = 2;

function LiveChatSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  // Poll the full live-chat feed so incoming requests + new customer
  // messages become visible without the operator hitting refresh.
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [ticketsErr, setTicketsErr] = useState<string | null>(null);
  const [team, setTeam] = useState<api.AdminTeamMember[]>([]);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesErr, setMessagesErr] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [actionFeedback, setActionFeedback] = useState<{ tone: "good" | "warn" | "bad"; text: string } | null>(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [escalationHistory, setEscalationHistory] = useState<api.EscalationEntry[]>([]);
  const [escalationHistoryLoading, setEscalationHistoryLoading] = useState(false);
  const [escalationHistoryError, setEscalationHistoryError] = useState<string | null>(null);
  const canAssign = hasPerm(currentUser, "support:assign");
  const canReply = hasPerm(currentUser, "support:reply");
  // Holds the chat-namespace socket for the currently-selected ticket.
  // Effect below (re)creates it on selection change and tears it down
  // on unmount or switch.
  const socketRef = useRef<Socket | null>(null);

  const buckets = useMemo(
    () => splitLiveChats(tickets, currentUser?.id ?? null),
    [tickets, currentUser?.id],
  );
  const reassignTargets = useMemo(
    () => (currentUser ? eligibleReassignTargets(team, currentUser.id) : []),
    [team, currentUser?.id],
  );

  const refetchTickets = async () => {
    try {
      const t = await api.listTickets();
      setTickets(t);
      setTicketsErr(null);
    } catch (e: any) {
      setTicketsErr(e?.message ?? "Failed to load tickets");
    }
  };

  // Poll tickets every 5s. visibilitychange refresh keeps the count
  // honest if the operator switches back from another tab.
  useEffect(() => {
    if (!api.isApiConfigured()) return;
    void refetchTickets();
    const id = window.setInterval(refetchTickets, TICKET_POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") void refetchTickets();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Team list for the reassign dropdown — fetched once on mount.
  useEffect(() => {
    if (!api.isApiConfigured()) return;
    api.listAdminUsers().then(setTeam).catch(() => setTeam([]));
  }, []);

  // Auto-select first own chat or first incoming when nothing is
  // currently selected, OR when the previously-selected chat
  // disappeared (e.g. claimed by someone else).
  useEffect(() => {
    if (selected && !buckets.mine.some((t) => t.id === selected.id) &&
        !buckets.incoming.some((t) => t.id === selected.id)) {
      setSelected(buckets.mine[0] ?? buckets.incoming[0] ?? null);
    } else if (!selected) {
      setSelected(buckets.mine[0] ?? buckets.incoming[0] ?? null);
    }
  }, [buckets, selected]);

  // Socket-driven message stream for the selected ticket. Replaces the
  // legacy 2s poll. Falls back to polling if the chat namespace
  // handshake fails twice in a row — preserves prior UX on networks
  // that strip WebSocket upgrades.
  //
  // Rooms joined with asAdmin=true:
  //   ticket:<id>     — message + claim + presence events
  //   ticket-ops:<id> — internal_note (operator-only; Slice G room split)
  //   tenant-seats:<tenantId> — ticket:claimed + ticket:escalated for
  //                              cross-seat awareness on this tenant
  useEffect(() => {
    if (!selected) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    const seen = new Set<string>();
    const socketFailures = { count: 0 };
    let pollTimer: number | null = null;
    let socket: Socket | null = null;

    const initialLoad = async () => {
      try {
        const ms = await api.listTicketMessages(selected.id);
        if (cancelled) return;
        for (const m of ms) {
          if (m.id) seen.add(m.id);
        }
        setMessages(ms);
        setMessagesErr(null);
      } catch (e: any) {
        if (!cancelled) setMessagesErr(e?.message ?? "Failed to load messages");
      }
    };

    const startPollingFallback = () => {
      if (pollTimer != null) return;
      const tick = async () => {
        try {
          const ms = await api.listTicketMessages(selected.id);
          if (!cancelled) {
            // Reseed the seen-set so socket-sourced ids stay de-duped
            // if WS reconnects later.
            seen.clear();
            for (const m of ms) {
              if (m.id) seen.add(m.id);
            }
            setMessages(ms);
            setMessagesErr(null);
          }
        } catch (e: any) {
          if (!cancelled) setMessagesErr(e?.message ?? "Failed to load messages");
        }
      };
      pollTimer = window.setInterval(tick, MESSAGE_POLL_MS);
    };

    const appendIfNew = (m: ChatMessage) => {
      if (!m?.id || seen.has(m.id)) return;
      seen.add(m.id);
      setMessages((prev) => [...prev, m]);
    };

    void initialLoad();
    // Fire markRead once on select; sidebar count drops on next list poll.
    api.markTicketRead(selected.id).catch(() => undefined);

    socket = io(WS_NAMESPACE, {
      path: WS_PATH,
      transports: ["websocket"],
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      socketFailures.count = 0;
      socket?.emit("join_ticket", {
        ticketId: selected.id,
        asAdmin: true,
        displayName: currentUser?.fullName || currentUser?.email || "Operator",
      });
      // Tenant-seats subscription is opt-in once Ticket carries tenantId
      // — admin operators handle multiple tenants so per-ticket-tenant
      // joins on selection change cover cross-seat awareness for the
      // active chat. List-level cross-ticket awareness keeps using the
      // 5s ticket poll.
    });

    socket.on("connect_error", () => {
      socketFailures.count += 1;
      if (socketFailures.count >= SOCKET_FAILURE_THRESHOLD && pollTimer == null) {
        // WS unavailable → tear down + fall back to polling. We don't
        // null out socketRef so an eventual reconnect still works.
        socket?.removeAllListeners();
        socket?.disconnect();
        startPollingFallback();
      }
    });

    socket.on("message", (m: ChatMessage) => appendIfNew(m));
    socket.on("internal_note", (m: ChatMessage) => {
      // Force the isInternal flag in case the server payload omits it.
      appendIfNew({ ...m, isInternal: true });
    });
    socket.on("claim", () => {
      void refetchTickets();
    });
    socket.on("ticket:claimed", () => {
      void refetchTickets();
    });
    socket.on("ticket:escalated", () => {
      void refetchTickets();
    });

    return () => {
      cancelled = true;
      if (pollTimer != null) window.clearInterval(pollTimer);
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
      }
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [selected?.id]);

  const onClaim = async (ticketId: string) => {
    setBusyAction(ticketId);
    setActionFeedback(null);
    try {
      const claimed = await api.claimTicket(ticketId);
      // Optimistically reflect the claim in our local list so the UI
      // doesn't flicker until the next poll lands.
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? { ...t, ...claimed } : t)));
      setSelected(claimed as Ticket);
      setActionFeedback({ tone: "good", text: "You're now connected — say hi!" });
      void refetchTickets();
    } catch (e: any) {
      // 409 = race-loss (another agent grabbed it). Refresh so the
      // claimed ticket disappears from this operator's queue.
      const msg = e?.status === 409 ? "Another agent already claimed this chat." : (e?.message ?? "Claim failed");
      setActionFeedback({ tone: "warn", text: msg });
      void refetchTickets();
    } finally {
      setBusyAction(null);
    }
  };

  const onReassign = async (newAgent: { id: string; firstName: string }) => {
    if (!selected) return;
    setBusyAction(selected.id);
    setActionFeedback(null);
    try {
      await api.reassignTicket(selected.id, newAgent.id, newAgent.firstName);
      setActionFeedback({ tone: "good", text: `Transferred to ${newAgent.firstName}.` });
      setReassignOpen(false);
      // Reload list — the reassigned chat will move to "other claimed"
      // and disappear from "mine".
      await refetchTickets();
      setSelected(null);
    } catch (e: any) {
      setActionFeedback({ tone: "bad", text: e?.message ?? "Reassign failed" });
    } finally {
      setBusyAction(null);
    }
  };

  async function handleSend() {
    if (!selected || !draft.trim() || sending) return;
    setSending(true);
    try {
      const sent = await api.sendTicketMessage({ ticketId: selected.id, text: draft.trim() });
      setMessages((prev) => [...prev, sent]);
      setDraft("");
    } catch (e: any) {
      setActionFeedback({ tone: "bad", text: `Failed to send: ${e?.message ?? e}` });
    } finally {
      setSending(false);
    }
  }

  async function handleOpenEscalate() {
    if (!selected) return;
    setEscalateOpen(true);
    setEscalationHistoryLoading(true);
    setEscalationHistoryError(null);
    try {
      const rows = await api.listEscalations(selected.id);
      setEscalationHistory(rows);
    } catch (e: any) {
      setEscalationHistoryError(e?.message ?? "Failed to load history");
      setEscalationHistory([]);
    } finally {
      setEscalationHistoryLoading(false);
    }
  }

  async function handleEscalateSubmit(args: {
    queue: api.EscalationQueue;
    teamId?: string;
    reason: string;
    internalNote: string;
  }) {
    if (!selected || escalating) return;
    setEscalating(true);
    setActionFeedback(null);
    try {
      const result = await api.escalateTicket(selected.id, {
        queue: args.queue,
        teamId: args.teamId,
        reason: args.reason,
      });
      if (args.internalNote) {
        // Best-effort: escalation is the durable action; a note that
        // fails should not roll it back.
        await api.addInternalNote(selected.id, args.internalNote).catch(() => undefined);
      }
      setActionFeedback({
        tone: "good",
        text: `Escalated to ${result.history.queue ?? args.queue}.`,
      });
      setEscalateOpen(false);
      await refetchTickets();
    } catch (e: any) {
      setActionFeedback({ tone: "bad", text: e?.message ?? "Failed to escalate." });
    } finally {
      setEscalating(false);
    }
  }

  async function handleSaveNote() {
    if (!selected || !noteDraft.trim() || savingNote) return;
    setSavingNote(true);
    setActionFeedback(null);
    try {
      const saved = await api.addInternalNote(selected.id, noteDraft.trim());
      setMessages((prev) => [...prev, saved]);
      setNoteDraft("");
      setActionFeedback({ tone: "good", text: "Internal note saved." });
    } catch (e: any) {
      setActionFeedback({ tone: "bad", text: e?.message ?? "Failed to save note." });
    } finally {
      setSavingNote(false);
    }
  }

  const isIncoming = selected ? !selected.assignedTo : false;
  const isMine = selected ? selected.assignedTo === currentUser?.id : false;

  return <>
    <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <StatCard title="My Live Chats" value={buckets.mine.length} sub="Conversations you're handling" icon="message" />
      <StatCard title="Incoming Requests" value={buckets.incoming.length} sub="Waiting for an operator to claim" icon="warning" />
      <StatCard title="Other Operators" value={buckets.otherClaimed.length} sub="Already handled by teammates" icon="users" />
    </section>

    {actionFeedback && (
      <div className={`mb-4 rounded-2xl border p-3 text-sm ${
        actionFeedback.tone === "good" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
        : actionFeedback.tone === "warn" ? "border-amber-200 bg-amber-50 text-amber-900"
        : "border-red-200 bg-red-50 text-red-800"
      }`}>{actionFeedback.text}</div>
    )}

    <section className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-12 gap-6 min-h-[650px]">
      {/* Left rail: Incoming + Mine */}
      <Card className="xl:col-span-3 lg:col-span-1 p-4 space-y-5">
        <div>
          <p className="text-xs font-semibold tracking-wider text-zinc-500 uppercase mb-2">
            Incoming Requests {buckets.incoming.length > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-bold bg-rose-500 text-white animate-pulse-fast">{buckets.incoming.length}</span>}
          </p>
          {buckets.incoming.length === 0 ? (
            <p className="text-xs text-zinc-400">No customers waiting.</p>
          ) : (
            <div className="space-y-2">
              {buckets.incoming.map((chat) => (
                <div key={chat.id} className="rounded-2xl border border-rose-200 bg-rose-50/60 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-zinc-950 truncate">{chat.customer}</p>
                    <Badge tone="bad">New</Badge>
                  </div>
                  <p className="mt-1 text-sm text-zinc-600 truncate">{chat.issue}</p>
                  <div className="mt-2 flex gap-2">
                    <Button onClick={() => onClaim(chat.id)} disabled={busyAction === chat.id} className="!py-1.5 !px-3 !text-xs">
                      {busyAction === chat.id ? "..." : "Accept"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold tracking-wider text-zinc-500 uppercase mb-2">My Live Chats</p>
          {buckets.mine.length === 0 ? (
            <p className="text-xs text-zinc-400">You aren't on any chats right now.</p>
          ) : (
            <div className="space-y-2">
              {buckets.mine.map((chat) => (
                <button key={chat.id} onClick={() => setSelected(chat)} className={`w-full rounded-2xl border p-3 text-left transition ${selected?.id === chat.id ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 bg-white hover:bg-zinc-50"}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-zinc-950 truncate">{chat.customer}</p>
                    {chat.unread > 0 && <Badge tone="bad">{chat.unread}</Badge>}
                  </div>
                  <p className="mt-1 text-xs text-zinc-500 truncate">{chat.issue}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {ticketsErr && <p className="text-xs text-red-600">{ticketsErr}</p>}
      </Card>

      {/* Center: chat panel */}
      <Card className="xl:col-span-6 lg:col-span-1 p-0 overflow-hidden">
        {!selected && (
          <div className="h-full min-h-[500px] flex items-center justify-center p-8 text-center">
            <div><p className="font-medium text-zinc-700">No live chat selected</p><p className="text-sm text-zinc-500 mt-1">Pick an incoming request to accept it, or open one of your live chats.</p></div>
          </div>
        )}
        {selected && (
          <>
            <div className="p-4 border-b border-zinc-200 flex items-center justify-between bg-white">
              <div>
                <h3 className="font-bold">{selected.customer}</h3>
                <p className="text-sm text-zinc-500">{
                  isMine ? "You're handling this chat"
                  : isIncoming ? "Pending — accept to start the conversation"
                  : `Handled by ${selected.assignedTo}`
                }</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge tone="good">Customer Online</Badge>
                <Badge tone={selected.priority === "High" ? "bad" : selected.priority === "Medium" ? "warn" : "info"}>{selected.priority}</Badge>
              </div>
            </div>
            <div className="h-[320px] md:h-[430px] overflow-auto p-4 bg-zinc-50 space-y-3">
              {messagesErr && <p className="text-sm text-red-600">Failed to load messages: {messagesErr}</p>}
              {!messagesErr && messages.length === 0 && <p className="text-sm text-zinc-500">No messages yet.</p>}
              {messages.map((message) => {
                const isInternal = message.isInternal === true;
                return (
                  <div key={message.id} className={`flex ${
                    isInternal ? "justify-end" :
                    message.by==="admin" ? "justify-end" :
                    message.by==="system" ? "justify-center" : "justify-start"
                  }`}>
                    <div className={`max-w-[78%] rounded-2xl p-3 text-sm ${
                      isInternal ? "bg-yellow-50 text-yellow-900 border border-yellow-300 border-dashed"
                      : message.by==="admin" ? "bg-zinc-950 text-white"
                      : message.by==="system" ? "bg-amber-50 text-amber-800 border border-amber-200 italic"
                      : "bg-white border border-zinc-200 text-zinc-800"
                    }`}>
                      {isInternal && (
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-yellow-800">
                          Internal note — operators only
                        </p>
                      )}
                      <p className="whitespace-pre-wrap">{message.text}</p>
                      <p className={`text-[11px] mt-1 ${isInternal ? "text-yellow-700" : message.by==="admin" ? "text-zinc-300" : "text-zinc-400"}`}>{message.time}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t border-zinc-200 bg-white">
              {isIncoming && (
                <div className="mb-3 rounded-2xl bg-rose-50 border border-rose-200 p-3 flex items-center justify-between">
                  <p className="text-sm text-rose-900">This conversation is unclaimed. Accept it to start replying.</p>
                  <Button onClick={() => onClaim(selected.id)} disabled={busyAction === selected.id}>
                    {busyAction === selected.id ? "..." : "Accept"}
                  </Button>
                </div>
              )}
              {isMine && (
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setReassignOpen((v) => !v)}
                    className="text-xs px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                  >
                    {reassignOpen ? "Cancel reassign" : "Reassign to teammate"}
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenEscalate}
                    disabled={!canAssign || escalating}
                    title={canAssign ? "Route this chat to a specialist queue" : "support:assign required"}
                    className="text-xs px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 hover:bg-zinc-200 disabled:opacity-50"
                  >
                    {escalating
                      ? "Escalating…"
                      : selected.status === "Escalated"
                        ? "Re-route queue"
                        : "Escalate to queue"}
                  </button>
                  {selected.queue && (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-900 border border-amber-200">
                      Queue: {selected.queue}
                    </span>
                  )}
                </div>
              )}
              {isMine && reassignOpen && (
                <div className="mb-3 rounded-2xl bg-zinc-50 border border-zinc-200 p-3">
                  <p className="text-xs font-semibold text-zinc-700 mb-2">Pick a teammate</p>
                  {reassignTargets.length === 0 ? (
                    <p className="text-xs text-zinc-500">No eligible teammates available.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {reassignTargets.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => onReassign(t)}
                          disabled={busyAction === selected.id}
                          className="text-xs px-3 py-1.5 rounded-full bg-white border border-zinc-200 text-zinc-800 hover:bg-zinc-100 disabled:opacity-50"
                        >
                          {t.fullName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {isMine && canReply && (
                <form
                  className="mb-3 rounded-2xl border border-yellow-300 border-dashed bg-yellow-50/60 p-2"
                  onSubmit={(e) => { e.preventDefault(); void handleSaveNote(); }}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-yellow-800 mb-1">
                    Internal note — operators only
                  </p>
                  <div className="flex gap-2">
                    <input
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      className="flex-1 rounded-2xl border border-yellow-200 bg-white px-3 py-2 text-sm outline-none"
                      placeholder="Drop a note for your teammates (customer never sees this)…"
                    />
                    <button
                      type="submit"
                      disabled={!noteDraft.trim() || savingNote}
                      className="px-4 py-2 text-sm rounded-2xl bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50"
                    >{savingNote ? "Saving…" : "Save note"}</button>
                  </div>
                </form>
              )}
              <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="flex-1 rounded-2xl border border-zinc-200 px-4 py-3 outline-none disabled:bg-zinc-50"
                  placeholder={isMine ? "Reply to the live customer..." : "Accept the chat to reply"}
                  disabled={!isMine}
                />
                <Button type="submit" disabled={!isMine || sending || !draft.trim()}>
                  <Icon name="send" className="mr-2"/>{sending ? "Sending…" : "Send"}
                </Button>
              </form>
            </div>
          </>
        )}
      </Card>

      {/* Right rail: policies + tips */}
      <Card className="xl:col-span-3 lg:col-span-2 p-5">
        <h3 className="text-lg font-bold">Live chat playbook</h3>
        <div className="mt-3 space-y-2 text-sm text-zinc-600">
          <p>• Click <span className="font-medium">Accept</span> to claim an incoming chat — first to click wins, others won't see it.</p>
          <p>• When you accept, the customer sees "You are now connected with {currentUser ? extractFirstName(currentUser.fullName) : "you"}."</p>
          <p>• Need help? Use <span className="font-medium">Reassign</span> to pass the chat to a teammate.</p>
          <p>• Route specialist work via <span className="font-medium">Escalate to queue</span> — legal, billing, security, engineering, sales, or integration.</p>
          <p>• Drop teammate context in the yellow <span className="font-medium">Internal note</span> bar — the customer never sees it.</p>
          <p>• First response target: under 60 seconds.</p>
        </div>
        <div className="mt-5 rounded-2xl border border-zinc-200 bg-zinc-50 p-4">
          <p className="text-xs text-zinc-500">Updates poll every 2 seconds while a chat is open.</p>
        </div>
      </Card>
    </section>
    {escalateOpen && selected && (
      <EscalationModal
        ticket={selected}
        history={escalationHistory}
        historyLoading={escalationHistoryLoading}
        historyError={escalationHistoryError}
        busy={escalating}
        onSubmit={handleEscalateSubmit}
        onClose={() => setEscalateOpen(false)}
      />
    )}
  </>;
}

function extractFirstName(fullName: string): string {
  const trimmed = (fullName ?? "").trim();
  if (!trimmed) return "your support agent";
  return trimmed.split(/\s+/)[0] || "your support agent";
}

const AI_COST_GROUPS: { key: AiCostGroupBy; label: string }[] = [
  { key: "tenant", label: "Tenant" },
  { key: "agent", label: "Agent" },
  { key: "model", label: "Model" },
  { key: "endpoint", label: "Endpoint" },
  { key: "day", label: "Day" },
];

/** micro-USD (BIGINT, possibly a string) → "$X.XX". */
function fmtMicroUsd(micro: string | number): string {
  const n = typeof micro === "string" ? Number(micro) : micro;
  if (!Number.isFinite(n)) return "$0.00";
  return "$" + (n / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// C2.3 — cross-tenant AI cost dashboard. Reads admin-bff /ai/costs (which proxies
// the agent's cost-governance data). Replaces the AI-cost work that originally
// landed in the now-archived ivorycom-admin SPA.
function AICostsView({ canEdit }: { canEdit: boolean }) {
  const [groupBy, setGroupBy] = useState<AiCostGroupBy>("tenant");
  const costs = useResource<{ costs: AiCostRow[] }>(
    () => api.getAiCosts({ group_by: groupBy }),
    { costs: [] },
    [groupBy],
  );
  const [editing, setEditing] = useState<string | null>(null);
  const [capDollars, setCapDollars] = useState("");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: "good" | "warn"; text: string } | null>(null);

  const rows = useMemo(
    () => [...costs.data.costs].sort((a, b) => Number(b.cost_micro_usd) - Number(a.cost_micro_usd)),
    [costs.data.costs],
  );
  const chartData = rows.slice(0, 12).map((r) => ({ key: r.key, usd: Number(r.cost_micro_usd) / 1_000_000 }));
  const totalMicro = rows.reduce((s, r) => s + Number(r.cost_micro_usd || 0), 0);
  const groupLabel = AI_COST_GROUPS.find((g) => g.key === groupBy)?.label ?? "Key";

  const submitCap = async (tenantId: string) => {
    setSaving(true);
    setFeedback(null);
    try {
      const dollars = capDollars.trim();
      const override = dollars === "" ? null : Math.round(parseFloat(dollars) * 1_000_000);
      if (override !== null && (!Number.isFinite(override) || override < 0)) {
        throw new Error("Enter a non-negative dollar amount (or blank to clear).");
      }
      await api.setTenantAiBudget(tenantId, { monthly_cost_cap_micro_usd_override: override });
      setFeedback({ tone: "good", text: dollars === "" ? "Budget override cleared." : `Cap set to $${dollars}/mo.` });
      setEditing(null);
      setCapDollars("");
    } catch (e: any) {
      setFeedback({ tone: "warn", text: e?.message ?? "Failed to set budget." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5">
      <SectionHeader
        title="AI Costs"
        subtitle="Cross-tenant Anthropic spend — by tenant, agent, model, endpoint, or day."
        action={<Button variant="outline" onClick={() => costs.refetch()}>Refresh</Button>}
      />
      <div className="flex flex-wrap items-center gap-2 mt-2">
        {AI_COST_GROUPS.map((g) => (
          <button
            key={g.key}
            type="button"
            onClick={() => setGroupBy(g.key)}
            className={`rounded-2xl px-3 py-1.5 text-sm font-medium border transition ${
              groupBy === g.key
                ? "bg-zinc-950 text-white border-zinc-950"
                : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
            }`}
          >
            {g.label}
          </button>
        ))}
        <span className="ml-auto text-sm text-zinc-500">
          Total: <span className="font-semibold text-zinc-900">{fmtMicroUsd(totalMicro)}</span>
        </span>
      </div>
      {feedback && (
        <div className={`mt-3 rounded-xl px-3 py-2 text-sm ${feedback.tone === "good" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          {feedback.text}
        </div>
      )}
      <div className="mt-4">
        <ResourceState
          loading={costs.loading}
          error={costs.error}
          empty={rows.length === 0}
          emptyTitle="No AI spend yet"
          emptyBody="Spend appears here once tenants use AI features."
        />
        {!costs.loading && !costs.error && rows.length > 0 && (
          <>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="key" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={52} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v: any) => `$${Number(v).toFixed(2)}`} />
                  <Bar dataKey="usd" fill="#18181b" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-zinc-200">
                    <th className="py-2 pr-3">{groupLabel}</th>
                    <th className="py-2 px-3 text-right">Cost</th>
                    <th className="py-2 px-3 text-right">BYO</th>
                    <th className="py-2 px-3 text-right">Overage</th>
                    <th className="py-2 px-3 text-right">In tok</th>
                    <th className="py-2 px-3 text-right">Out tok</th>
                    <th className="py-2 px-3 text-right">Calls</th>
                    {canEdit && groupBy === "tenant" && <th className="py-2 pl-3 text-right">Budget</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key} className="border-b border-zinc-100">
                      <td className="py-2 pr-3 font-medium text-zinc-900 max-w-[18rem] truncate">{r.key}</td>
                      <td className="py-2 px-3 text-right">{fmtMicroUsd(r.cost_micro_usd)}</td>
                      <td className="py-2 px-3 text-right text-zinc-500">{fmtMicroUsd(r.byo_cost_micro_usd)}</td>
                      <td className="py-2 px-3 text-right text-zinc-500">{fmtMicroUsd(r.overage_micro_usd)}</td>
                      <td className="py-2 px-3 text-right text-zinc-500">{r.input_tokens.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right text-zinc-500">{r.output_tokens.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right text-zinc-500">{r.api_calls.toLocaleString()}</td>
                      {canEdit && groupBy === "tenant" && (
                        <td className="py-2 pl-3 text-right">
                          {editing === r.key ? (
                            <span className="inline-flex items-center gap-1 justify-end">
                              <input
                                value={capDollars}
                                onChange={(e) => setCapDollars(e.target.value)}
                                placeholder="$/mo (blank=clear)"
                                className="w-28 rounded-lg border border-zinc-200 px-2 py-1 text-sm"
                              />
                              <Button className="px-2 py-1" onClick={() => submitCap(r.key)} disabled={saving}>Save</Button>
                              <Button variant="outline" className="px-2 py-1" onClick={() => { setEditing(null); setCapDollars(""); }}>✕</Button>
                            </span>
                          ) : (
                            <Button variant="outline" className="px-2 py-1" onClick={() => { setEditing(r.key); setCapDollars(""); }}>Set cap</Button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

function AIAgentSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const runs = useResource<AIRun[]>(api.listAIRuns, []);
  const canEdit = hasPerm(currentUser, "ai:override");
  return <section className="space-y-6">
    <AICostsView canEdit={canEdit} />
    <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <Card className="xl:col-span-2 p-5">
        <SectionHeader title="AI Agent Operations" subtitle="Monitor AI actions, failed tool calls, customer replies, automation decisions, and human approvals." action={<Button>Review Queue</Button>} />
        <ResourceState loading={runs.loading} error={runs.error} empty={runs.data.length === 0} emptyTitle="No AI runs yet" emptyBody="AI agent activity from GET /ai/runs will appear here." />
        {!runs.loading && !runs.error && runs.data.length > 0 && (
          <div className="space-y-3">{runs.data.map((r) => <Run key={r.id} title={r.title} status={r.status} warn={r.needsApproval}/>)}</div>
        )}
      </Card>
      <Card className="p-5">
        <h3 className="text-lg font-bold">Guardrails</h3>
        <div className="space-y-3 mt-4 text-sm text-zinc-600">
          <p>• No direct third-party API calls.</p>
          <p>• All actions go through admin-service.</p>
          <p>• Billing/refunds require approval.</p>
          <p>• Sensitive data masked.</p>
        </div>
      </Card>
    </section>
  </section>;
}
function Run({ title, status, warn = false }) { return <div className="rounded-2xl border border-zinc-200 p-4 flex items-center justify-between"><div><p className="font-semibold">{title}</p><p className="text-sm text-zinc-500">AI operation log</p></div><Badge tone={warn?"warn":"good"}>{status}</Badge></div>; }


// ─────────────────────────────────────────────────────────────────────────────
// Guardrail approvals — the human gate on CRITICAL destructive actions.
//
// Distinct from ApprovalsSection above, which is the internal business workflow
// (refunds, suspensions). These are PLATFORM guardrail decisions: a tenant purge
// destroys every trace of a customer across seven services, and it does not run
// until two distinct admins have each typed a phrase naming the tenant and
// re-proved their second factor.
//
// The typed phrase is displayed. It has to be — nobody could type it otherwise.
// Its purpose is not secrecy: it is that an operator cannot approve a purge
// without having READ which tenant they are about to destroy. The server
// re-checks it independently, so this input is a prompt, never the gate.
// ─────────────────────────────────────────────────────────────────────────────
function GuardrailApprovalsSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const [statusFilter, setStatusFilter] = useState<api.GuardrailStatus>("PENDING");
  const pendingRes = useResource<api.GuardrailPendingAction[]>(
    () => api.listGuardrailPending(statusFilter),
    [],
    [statusFilter],
  );
  const [openId, setOpenId] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const closeModal = () => {
    setOpenId(null);
    setTyped("");
    setMfaCode("");
    setError(null);
  };

  const open = pendingRes.data.find((p) => p.id === openId) ?? null;
  // Collapse whitespace exactly as the server does, so the button enables at the
  // same moment the server would accept — a button that looks ready and then
  // fails is worse than one that stays disabled.
  const normalise = (v: string) => v.trim().split(/\s+/).join(" ");
  const phraseMatches =
    !!open?.confirmation_phrase &&
    normalise(typed) === normalise(open.confirmation_phrase);
  const canSubmit = phraseMatches && mfaCode.trim().length >= 4 && !busy;

  const onApprove = async () => {
    if (!open || !canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.approveGuardrailPending(open.id, typed, mfaCode.trim());
      setNotice(
        res.status === "READY"
          ? `Approved — ${res.approval_count}/${res.required_approvals}. This action is now READY to execute.`
          : `Approved — ${res.approval_count}/${res.required_approvals}. It needs ${res.required_approvals - res.approval_count} more.`,
      );
      closeModal();
      pendingRes.refetch();
    } catch (e: any) {
      setError(e?.message ?? "approval failed");
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.revokeGuardrailPending(id, "revoked from the admin console");
      setNotice("Revoked. This action can no longer be approved or executed.");
      pendingRes.refetch();
    } catch (e: any) {
      setError(e?.message ?? "revoke failed");
    } finally {
      setBusy(false);
    }
  };

  const FILTERS: Array<{ key: api.GuardrailStatus; label: string }> = [
    { key: "PENDING", label: "Awaiting approval" },
    { key: "READY", label: "Ready to execute" },
    { key: "CONSUMED", label: "Executing" },
    { key: "REJECTED", label: "Revoked" },
    { key: "EXPIRED", label: "Expired" },
  ];

  return <section className="grid grid-cols-1 gap-6">
    <Card className="p-5">
      <SectionHeader
        title="Guardrail approvals"
        subtitle="Actions requiring human approval wait here. Review each action, its required approvals and confirmation phrase, then verify your second factor."
        action={<Button variant="outline" onClick={pendingRes.refetch}>Refresh</Button>}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
              statusFilter === f.key ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {notice && <div className="mb-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">{notice}</div>}
      {error && !openId && <div className="mb-3 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      <ResourceState
        loading={pendingRes.loading}
        error={pendingRes.error}
        empty={pendingRes.data.length === 0}
        emptyTitle={statusFilter === "PENDING" ? "Nothing awaiting approval" : "Nothing in this view"}
        emptyBody={statusFilter === "PENDING"
          ? "Actions appear here when policy requires approval. They remain held until their approval requirements are satisfied."
          : "Nothing to show."}
      />

      {!pendingRes.loading && !pendingRes.error && pendingRes.data.length > 0 && (
        <div className="space-y-3">
          {pendingRes.data.map((p) => {
            const expires = new Date(p.expires_at);
            const expired = expires.getTime() < Date.now();
            return (
              <div key={p.id} className="rounded-2xl border border-zinc-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-zinc-950">{p.action}</p>
                      <Badge tone="bad">{p.risk_level}</Badge>
                      <Badge tone={p.status === "READY" ? "good" : "warn"}>{p.status}</Badge>
                      <Badge tone="info">{p.approval_count}/{p.required_approvals} approved</Badge>
                    </div>
                    <p className="mt-1 text-sm text-zinc-600">
                      Tenant <span className="font-mono text-xs">{p.tenant_id}</span>
                      {" · requested by "}
                      <span className="font-medium">{p.requested_by_human || p.requested_by_id || p.requested_by_type}</span>
                    </p>
                    <p className="mt-1 text-[11px] text-zinc-500">
                      {expired ? "Expired " : "Expires "}{expires.toLocaleString()}
                      {p.pathway === "tenant_owner" && " · customer-initiated deletion"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => onRevoke(p.id)}
                      disabled={busy || p.status === "CONSUMED"}
                    >
                      Revoke
                    </Button>
                    <Button
                      onClick={() => { setOpenId(p.id); setNotice(null); }}
                      disabled={expired || p.status !== "PENDING"}
                    >
                      Review &amp; approve
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>

    {open && (
      <ActionModal
        title={`Approve ${open.action}?`}
        warning={open.action === "tenant.wipe"
          ? "Irreversible. This permanently destroys every record this tenant owns across all Ivorycom services. It cannot be undone, and there is no restore."
          : open.action === "auth.echo_workloads.register"
            ? "Register ECHO Engine, ECHO Voice and Support service identities for platform-wide use. Existing tenant enablement and permissions still control customer operations."
            : "Review the requested action and its scope before approving. Execution remains subject to policy and the required approvals."}
        submitLabel={busy ? "Approving..." : open.action === "tenant.wipe" ? "Approve this purge" : "Approve this action"}
        submitDisabled={!canSubmit}
        onSubmit={onApprove}
        onCancel={closeModal}
      >
        <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-3 text-xs text-zinc-700 space-y-1">
          <div><span className="text-zinc-500">Tenant</span> <span className="font-mono">{open.tenant_id}</span></div>
          <div><span className="text-zinc-500">Requested by</span> {open.requested_by_human || open.requested_by_id || open.requested_by_type}</div>
          <div><span className="text-zinc-500">Approvals</span> {open.approval_count} of {open.required_approvals} — you would be #{open.approval_count + 1}</div>
          {/* Naming the approver makes the two-person rule visible: the same
              person cannot be both signatures, and seeing whose approval this
              will be recorded as is the point of saying so. */}
          <div><span className="text-zinc-500">Approving as</span> {currentUser?.email ?? "your admin account"}</div>
        </div>

        {open.confirmation_phrase && (
          <>
            <label className="mt-3 block text-xs font-semibold text-zinc-700">
              Type <span className="font-mono text-zinc-950">{open.confirmation_phrase}</span> to confirm
            </label>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder={open.confirmation_phrase}
              className="mt-1 w-full rounded-xl border border-zinc-300 bg-white p-2 font-mono text-sm outline-none focus:border-zinc-500"
            />
            <p className="mt-1 text-[11px] text-zinc-500">
              {open.action === "tenant.wipe" ? "Read it before you type it. The phrase names the tenant you are about to destroy." : "Read the phrase before typing it to confirm the requested action."}
            </p>
          </>
        )}

        <label className="mt-3 block text-xs font-semibold text-zinc-700">Your authenticator code</label>
        <input
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9a-zA-Z-]/g, ""))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          className="mt-1 w-full rounded-xl border border-zinc-300 bg-white p-2 font-mono text-sm outline-none focus:border-zinc-500"
        />
        <p className="mt-1 text-[11px] text-zinc-500">
          Re-proving your second factor is what makes this approval yours: a leaked service
          token can reach this console, but it cannot produce your authenticator.
        </p>

        {error && <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-2 text-xs text-red-800">{error}</div>}
      </ActionModal>
    )}
  </section>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Platform approvals — the human gate on governed Tool Gateway changes.
//
// Registering a tool operation, an adapter or a risk rule decides what every
// AI agent on the platform is allowed to reach. The policy engine authorizes
// each change in the scope `platform:tool-gateway` and holds it here until one
// Ivorycom admin types the operation being authorized and re-proves their
// second factor. The engine verifies the signed assertion and the approver's
// role; this screen is the prompt, never the gate.
// ─────────────────────────────────────────────────────────────────────────────
function PlatformApprovalsSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const [stateFilter, setStateFilter] = useState<api.PlatformApprovalState>("PENDING");
  const res = useResource<api.PlatformApproval[]>(
    () => api.listPlatformApprovals(stateFilter),
    [],
    [stateFilter],
  );
  const [openId, setOpenId] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const closeModal = () => {
    setOpenId(null);
    setTyped("");
    setMfaCode("");
    setError(null);
  };

  const open = res.data.find((a) => a.approval_id === openId) ?? null;
  const normalise = (v: string) => v.trim().split(/\s+/).join(" ");
  const phrase = open ? api.platformApprovalPhrase(open) : "";
  const phraseMatches = !!open && normalise(typed) === phrase;
  const canSubmit = phraseMatches && mfaCode.trim().length >= 4 && !busy;

  const onApprove = async () => {
    if (!open || !canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const out = await api.approvePlatformApproval(open.approval_id, typed, mfaCode.trim());
      setNotice(`Granted ${api.platformApprovalPhrase(out)} — the requester can now complete it.`);
      closeModal();
      res.refetch();
    } catch (e: any) {
      setError(e?.message ?? "approval failed");
    } finally {
      setBusy(false);
    }
  };

  const onReject = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.rejectPlatformApproval(id, "rejected from the admin console");
      setNotice("Rejected. The requester must ask again with a new decision.");
      res.refetch();
    } catch (e: any) {
      setError(e?.message ?? "reject failed");
    } finally {
      setBusy(false);
    }
  };

  const FILTERS: Array<{ key: api.PlatformApprovalState; label: string }> = [
    { key: "PENDING", label: "Awaiting approval" },
    { key: "GRANTED", label: "Granted" },
    { key: "CONSUMED", label: "Completed" },
    { key: "REJECTED", label: "Rejected" },
    { key: "EXPIRED", label: "Expired" },
  ];

  return <section className="grid grid-cols-1 gap-6">
    <Card className="p-5">
      <SectionHeader
        title="Platform approvals"
        subtitle="Governed Tool Gateway changes — tool operations, adapters, risk rules — wait here. Each one needs one admin to type the operation being authorized and re-prove their second factor."
        action={<Button variant="outline" onClick={res.refetch}>Refresh</Button>}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStateFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
              stateFilter === f.key ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {notice && <div className="mb-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">{notice}</div>}
      {error && !openId && <div className="mb-3 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      <ResourceState
        loading={res.loading}
        error={res.error}
        empty={res.data.length === 0}
        emptyTitle={stateFilter === "PENDING" ? "Nothing awaiting approval" : "Nothing in this view"}
        emptyBody={stateFilter === "PENDING"
          ? "Governed platform changes appear here the moment an operator requests one. Until an admin grants it, the Tool Gateway does not change."
          : "Nothing to show."}
      />

      {!res.loading && !res.error && res.data.length > 0 && (
        <div className="space-y-3">
          {res.data.map((a) => {
            const expires = new Date(a.expires_at);
            const expired = expires.getTime() < Date.now();
            return (
              <div key={a.approval_id} className="rounded-2xl border border-zinc-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-zinc-950 font-mono">{api.platformApprovalPhrase(a)}</p>
                      <Badge tone="bad">{a.materiality}</Badge>
                      <Badge tone={a.state === "GRANTED" || a.state === "CONSUMED" ? "good" : a.state === "PENDING" ? "warn" : "info"}>{a.state}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-zinc-600">
                      Requested by <span className="font-medium">{a.requested_by_workload || a.requested_by_actor}</span>
                      {" · "}{new Date(a.requested_at).toLocaleString()}
                    </p>
                    <p className="mt-1 text-[11px] text-zinc-500 font-mono break-all">intent {a.bound_intent_hash}</p>
                    <p className="mt-1 text-[11px] text-zinc-500">
                      {expired ? "Expired " : "Expires "}{expires.toLocaleString()}
                      {a.approver_subject && ` · decided by ${a.approver_subject}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => onReject(a.approval_id)}
                      disabled={busy || a.state !== "PENDING"}
                    >
                      Reject
                    </Button>
                    <Button
                      onClick={() => { setOpenId(openId === a.approval_id ? null : a.approval_id); setNotice(null); }}
                      disabled={expired || a.state !== "PENDING"}
                    >
                      {openId === a.approval_id ? "Close" : "Review & grant"}
                    </Button>
                  </div>
                </div>
                {/* The grant panel opens right under the item that was clicked. With twenty items
                    on screen, a panel rendered after the whole list looked like a dead button. */}
                {open && open.approval_id === a.approval_id && (
                  <ActionModal
                    title={`Grant ${phrase}?`}
        warning="This authorizes a change to what every AI agent on the platform may reach through the Tool Gateway. The exact request is bound by its intent hash; nothing else can be completed with this grant."
        submitLabel={busy ? "Granting..." : "Grant this change"}
        submitDisabled={!canSubmit}
        onSubmit={onApprove}
        onCancel={closeModal}
      >
        <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-3 text-xs text-zinc-700 space-y-1">
          <div><span className="text-zinc-500">Scope</span> <span className="font-mono">{open.tenant_id}</span></div>
          <div><span className="text-zinc-500">Requested by</span> {open.requested_by_workload || open.requested_by_actor}</div>
          <div><span className="text-zinc-500">Intent hash</span> <span className="font-mono break-all">{open.bound_intent_hash}</span></div>
          <div><span className="text-zinc-500">Granting as</span> {currentUser?.email ?? "your admin account"}</div>
        </div>

        <label className="mt-3 block text-xs font-semibold text-zinc-700">
          Type <span className="font-mono text-zinc-950">{phrase}</span> to confirm
        </label>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder={phrase}
          className="mt-1 w-full rounded-xl border border-zinc-300 bg-white p-2 font-mono text-sm outline-none focus:border-zinc-500"
        />
        <p className="mt-1 text-[11px] text-zinc-500">
          Read it before you type it. The phrase names the platform operation you are authorizing.
        </p>

        <label className="mt-3 block text-xs font-semibold text-zinc-700">Your authenticator code</label>
        <input
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9a-zA-Z-]/g, ""))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          className="mt-1 w-full rounded-xl border border-zinc-300 bg-white p-2 font-mono text-sm outline-none focus:border-zinc-500"
        />
        <p className="mt-1 text-[11px] text-zinc-500">
          Re-proving your second factor is what makes this grant yours: a leaked service
          token can reach this console, but it cannot produce your authenticator.
        </p>

        {error && <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-2 text-xs text-red-800">{error}</div>}
                  </ActionModal>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  </section>;
}

function ApprovalsSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const [statusFilter, setStatusFilter] = useState<api.ApprovalStatus | "all">("pending");
  const approvalsRes = useResource<api.ApprovalRequest[]>(
    () => api.listApprovals(statusFilter === "all" ? undefined : statusFilter),
    [],
    [statusFilter],
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const onApprove = async (id: string) => {
    setBusyId(id);
    setActionError(null);
    try {
      await api.approveApproval(id);
      approvalsRes.refetch();
    } catch (e: any) {
      setActionError(e?.message ?? "approve failed");
    } finally {
      setBusyId(null);
    }
  };

  const onReject = async () => {
    if (!rejectingId || rejectReason.trim().length < 3) return;
    setBusyId(rejectingId);
    setActionError(null);
    try {
      await api.rejectApproval(rejectingId, rejectReason.trim());
      setRejectingId(null);
      setRejectReason("");
      approvalsRes.refetch();
    } catch (e: any) {
      setActionError(e?.message ?? "reject failed");
    } finally {
      setBusyId(null);
    }
  };

  const FILTERS: Array<{ key: api.ApprovalStatus | "all"; label: string }> = [
    { key: "pending", label: "Pending" },
    { key: "executed", label: "Executed" },
    { key: "rejected", label: "Rejected" },
    { key: "expired", label: "Expired" },
    { key: "all", label: "All" },
  ];

  return <section className="grid grid-cols-1 gap-6">
    <Card className="p-5">
      <SectionHeader
        title="Approvals"
        subtitle="High-risk operations (refunds, suspensions, terminations) wait here until an authorized admin signs off."
        action={<Button variant="outline" onClick={approvalsRes.refetch}>Refresh</Button>}
      />
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
              statusFilter === f.key
                ? "bg-zinc-950 text-white"
                : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {actionError && <div className="mb-3 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{actionError}</div>}
      <ResourceState
        loading={approvalsRes.loading}
        error={approvalsRes.error}
        empty={approvalsRes.data.length === 0}
        emptyTitle={statusFilter === "pending" ? "No pending approvals" : `No ${statusFilter} approvals`}
        emptyBody={statusFilter === "pending" ? "All caught up — high-risk actions will surface here when they need a second pair of eyes." : "Nothing to show in this view."}
      />
      {!approvalsRes.loading && !approvalsRes.error && approvalsRes.data.length > 0 && (
        <div className="space-y-3">
          {approvalsRes.data.map((req) => (
            <div key={req.id} className="rounded-2xl border border-zinc-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-zinc-950">{describeAction(req.action)}</p>
                    <Badge tone={statusTone(req.status)}>{req.status}</Badge>
                    {req.thresholdPolicy && <Badge tone="info">{req.thresholdPolicy}</Badge>}
                  </div>
                  <p className="mt-1 text-sm text-zinc-600">
                    Requested by <span className="font-medium">{req.requestedByEmail ?? req.requestedBy.slice(0, 8)}</span>
                    {' · '}
                    {timeAgo(req.requestedAt)}
                  </p>
                  <pre className="mt-2 max-h-40 overflow-auto rounded-xl bg-zinc-50 p-3 text-xs text-zinc-700">
{JSON.stringify(req.payload, null, 2)}
                  </pre>
                  {req.rejectedReason && (
                    <p className="mt-2 text-sm text-red-700">Rejection reason: {req.rejectedReason}</p>
                  )}
                  {req.approvedByEmail && (
                    <p className="mt-1 text-xs text-zinc-500">
                      Resolved by {req.approvedByEmail}
                      {req.resolvedAt ? ` · ${timeAgo(req.resolvedAt)}` : ""}
                    </p>
                  )}
                </div>
                {req.status === "pending" && rejectingId !== req.id && (
                  currentUser && req.requestedBy === currentUser.id ? (
                    // Two-person rule: requester can never approve their own
                    // request. Showing buttons here would just produce a 403
                    // when clicked. Surface the actual next step instead.
                    <div className="flex-shrink-0 max-w-xs rounded-xl bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
                      You requested this. Another admin (CFO or OWNER) must approve.
                    </div>
                  ) : (
                    <div className="flex flex-shrink-0 gap-2">
                      <Button
                        onClick={() => onApprove(req.id)}
                        disabled={busyId === req.id}
                      >{busyId === req.id ? "..." : "Approve"}</Button>
                      <Button
                        variant="outline"
                        onClick={() => { setRejectingId(req.id); setRejectReason(""); }}
                        disabled={busyId === req.id}
                      >Reject</Button>
                    </div>
                  )
                )}
              </div>
              {req.status === "pending" && rejectingId === req.id && (
                <div className="mt-3 rounded-2xl bg-zinc-50 p-3">
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">Reason for rejection (min 3 chars)</label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={2}
                    className="w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm outline-none focus:border-zinc-500"
                  />
                  <div className="mt-2 flex gap-2">
                    <Button onClick={onReject} disabled={busyId === req.id || rejectReason.trim().length < 3}>
                      {busyId === req.id ? "..." : "Confirm reject"}
                    </Button>
                    <Button variant="outline" onClick={() => setRejectingId(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  </section>;
}

const ACTION_LABELS: Record<string, string> = {
  tenant_suspend: "Suspend tenant",
  tenant_terminate: "Terminate tenant",
  billing_refund: "Issue refund",
  billing_credit: "Issue credit",
  mass_action: "Mass action",
  impersonation_long: "Long impersonation",
};

function describeAction(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

function statusTone(status: api.ApprovalStatus): "good" | "bad" | "warn" | "info" | "default" {
  switch (status) {
    case "executed": return "good";
    case "approved": return "info";
    case "rejected": return "bad";
    case "expired": return "warn";
    case "pending":
    default: return "default";
  }
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

function SecuritySection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const [filter, setFilter] = useState<api.AuditFilter>({});
  const fetcher = React.useCallback(() => api.listAuditEntries(filter), [filter]);
  const audit = useResource<AuditEntry[]>(fetcher, [], [JSON.stringify(filter)]);
  const canExport = hasPerm(currentUser, "security:audit_export");

  return <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
    <Card className="xl:col-span-2 p-5">
      <SectionHeader
        title="Security & Audit Logs"
        subtitle="Track every admin action, tenant access, impersonation, billing change, and permission update."
        action={
          canExport ? (
            <a
              href={api.auditExportUrl(filter)}
              className="inline-flex items-center justify-center rounded-2xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-medium text-zinc-900 hover:bg-zinc-50"
            >
              Export CSV
            </a>
          ) : null
        }
      />
      <AuditFilterBar filter={filter} onChange={setFilter} />
      <ResourceState loading={audit.loading} error={audit.error} empty={audit.data.length === 0} emptyTitle="No audit entries" emptyBody={Object.keys(filter).length > 0 ? "No entries match these filters." : "Audit events from GET /security/audit will appear here."} />
      {!audit.loading && !audit.error && audit.data.length > 0 && (
        <ul className="mt-2 space-y-2 text-sm">
          {audit.data.map((e) => (
            <li key={e.id} className="rounded-xl border border-zinc-200 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">{e.action}</p>
                  <p className="text-xs text-zinc-500">
                    {e.actor}
                    {e.resourceType && <> · <span className="font-mono">{e.resourceType}</span></>}
                    {e.resourceId && <>:{e.resourceId}</>}
                    {e.tenantId && <> · tenant <span className="font-mono">{e.tenantId.slice(0, 8)}…</span></>}
                  </p>
                </div>
                <div className="text-right">
                  {e.result && (
                    <Badge tone={e.result === "success" ? "good" : e.result === "denied" ? "warn" : "bad"}>{e.result}</Badge>
                  )}
                  <p className="text-[11px] text-zinc-400 mt-1">{e.timeAgo}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
    <MfaPanel />
  </section>;
}

// ----- A4.2 Policy Simulator -----
// Three panels: (a) Role Builder authors custom role definitions via
// POST/PUT /policy/definitions; (b) Simulator dry-runs an access decision
// via POST /policy/simulate and renders visible/masked/hidden fields;
// (c) History shows the policy-change timeline from GET /policy/history
// (sourced from the A4.1 audit ledger). Field-classification logic is the
// pure helper in policySimulator.ts (unit-tested).

const POLICY_ACTIONS: api.PolicyAction[] = ["create", "read", "update", "delete"];
const MASK_STRATEGIES = ["", "redact", "last4", "hash", "email_domain"] as const;

function emptyObjectRule(object = ""): api.PolicyObjectRule {
  return { object, create: false, read: true, update: false, delete: false, fields: [] };
}

function PolicySimulatorSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const canWrite = hasPerm(currentUser, "policy:write");
  return (
    <div className="space-y-6">
      <SectionHeader
        title="Policy Simulator"
        subtitle="Author custom roles, dry-run access decisions, and review the policy-change history."
      />
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <RoleBuilderPanel canWrite={canWrite} />
        <PolicyDryRunPanel />
      </section>
      <PolicyHistoryPanel />
    </div>
  );
}

function RoleBuilderPanel({ canWrite }: { canWrite: boolean }) {
  const [tenant, setTenant] = useState("");
  const [role, setRole] = useState("");
  const [description, setDescription] = useState("");
  const [objects, setObjects] = useState<api.PolicyObjectRule[]>([emptyObjectRule("Opportunity")]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updateObject = (i: number, patch: Partial<api.PolicyObjectRule>) =>
    setObjects((prev) => prev.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));

  const updateField = (oi: number, fi: number, patch: Partial<api.PolicyFieldRule>) =>
    setObjects((prev) =>
      prev.map((o, idx) =>
        idx === oi
          ? { ...o, fields: o.fields.map((f, j) => (j === fi ? { ...f, ...patch } : f)) }
          : o,
      ),
    );

  const addObject = () => setObjects((prev) => [...prev, emptyObjectRule()]);
  const removeObject = (i: number) => setObjects((prev) => prev.filter((_, idx) => idx !== i));
  const addField = (oi: number) =>
    setObjects((prev) =>
      prev.map((o, idx) =>
        idx === oi
          ? { ...o, fields: [...o.fields, { field: "", read: true, write: false, mask: null }] }
          : o,
      ),
    );
  const removeField = (oi: number, fi: number) =>
    setObjects((prev) =>
      prev.map((o, idx) => (idx === oi ? { ...o, fields: o.fields.filter((_, j) => j !== fi) } : o)),
    );

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      const def = await api.createPolicyDefinition({
        tenant: tenant.trim() || null,
        role: role.trim(),
        description: description.trim() || null,
        objects: objects
          .filter((o) => o.object.trim())
          .map((o) => ({ ...o, object: o.object.trim(), fields: o.fields.filter((f) => f.field.trim()) })),
      });
      setSaved(`Saved role "${def.role}" (${def.id}).`);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5">
      <SectionHeader title="Role Builder" subtitle="Create a custom role with per-object CRUD and per-field read/write/mask rules." />
      <div className="space-y-2 text-sm">
        <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role name (e.g. Read Only)" className="w-full rounded-xl border border-zinc-200 bg-white p-2 outline-none focus:border-zinc-500" />
        <input value={tenant} onChange={(e) => setTenant(e.target.value)} placeholder="Tenant ID (blank = platform-wide)" className="w-full rounded-xl border border-zinc-200 bg-white p-2 outline-none focus:border-zinc-500" />
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" className="w-full rounded-xl border border-zinc-200 bg-white p-2 outline-none focus:border-zinc-500" />
      </div>
      <div className="mt-4 space-y-3">
        {objects.map((o, oi) => (
          <div key={oi} className="rounded-xl border border-zinc-200 p-3">
            <div className="flex items-center gap-2">
              <input value={o.object} onChange={(e) => updateObject(oi, { object: e.target.value })} placeholder="Object (e.g. Opportunity)" className="flex-1 rounded-lg border border-zinc-200 bg-white p-1.5 text-sm outline-none focus:border-zinc-500" />
              <button type="button" onClick={() => removeObject(oi)} className="text-xs text-red-600 hover:underline">Remove</button>
            </div>
            <div className="mt-2 flex flex-wrap gap-3 text-xs">
              {(["create", "read", "update", "delete"] as const).map((op) => (
                <label key={op} className="inline-flex items-center gap-1 capitalize">
                  <input type="checkbox" checked={o[op]} onChange={(e) => updateObject(oi, { [op]: e.target.checked })} aria-label={`${o.object || "object"} ${op}`} />
                  {op}
                </label>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              {o.fields.map((f, fi) => (
                <div key={fi} className="flex flex-wrap items-center gap-2 text-xs">
                  <input value={f.field} onChange={(e) => updateField(oi, fi, { field: e.target.value })} placeholder="Field" className="rounded-lg border border-zinc-200 bg-white p-1.5 outline-none focus:border-zinc-500" />
                  <label className="inline-flex items-center gap-1"><input type="checkbox" checked={f.read} onChange={(e) => updateField(oi, fi, { read: e.target.checked })} />read</label>
                  <label className="inline-flex items-center gap-1"><input type="checkbox" checked={f.write} onChange={(e) => updateField(oi, fi, { write: e.target.checked })} />write</label>
                  <select value={f.mask ?? ""} onChange={(e) => updateField(oi, fi, { mask: e.target.value || null })} className="rounded-lg border border-zinc-200 bg-white p-1.5 outline-none focus:border-zinc-500" aria-label="mask strategy">
                    {MASK_STRATEGIES.map((m) => <option key={m} value={m}>{m === "" ? "no mask" : `mask: ${m}`}</option>)}
                  </select>
                  <button type="button" onClick={() => removeField(oi, fi)} className="text-red-600 hover:underline">×</button>
                </div>
              ))}
              <button type="button" onClick={() => addField(oi)} className="text-xs text-zinc-600 hover:underline">+ field</button>
            </div>
          </div>
        ))}
        <button type="button" onClick={addObject} className="text-sm text-zinc-600 hover:underline">+ object</button>
      </div>
      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      {saved && <p className="mt-3 text-sm text-emerald-700">{saved}</p>}
      <div className="mt-4">
        <Button onClick={save} disabled={!canWrite || saving || !role.trim()}>
          {saving ? "Saving…" : "Save role"}
        </Button>
        {!canWrite && <p className="mt-2 text-xs text-zinc-500">Requires policy:write.</p>}
      </div>
    </Card>
  );
}

function PolicyDryRunPanel() {
  const [tenant, setTenant] = useState("");
  const [role, setRole] = useState("");
  const [object, setObject] = useState("");
  const [action, setAction] = useState<api.PolicyAction>("read");
  const [result, setResult] = useState<api.SimulateResult | null>(null);
  const [rows, setRows] = useState<ClassifiedField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.simulatePolicy({
        tenant: tenant.trim(),
        role: role.trim(),
        object: object.trim(),
        action,
      });
      setResult(res);
      setRows(classifyFields(fieldsFromResult(res), res));
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setResult(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="xl:col-span-2 p-5">
      <SectionHeader title="Simulator" subtitle="Pick a tenant, role, object, and action to preview the effective access." />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
        <input value={tenant} onChange={(e) => setTenant(e.target.value)} placeholder="Tenant ID" className="rounded-xl border border-zinc-200 bg-white p-2 outline-none focus:border-zinc-500" />
        <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role" className="rounded-xl border border-zinc-200 bg-white p-2 outline-none focus:border-zinc-500" />
        <input value={object} onChange={(e) => setObject(e.target.value)} placeholder="Object" className="rounded-xl border border-zinc-200 bg-white p-2 outline-none focus:border-zinc-500" />
        <select value={action} onChange={(e) => setAction(e.target.value as api.PolicyAction)} className="rounded-xl border border-zinc-200 bg-white p-2 outline-none focus:border-zinc-500" aria-label="action">
          {POLICY_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      <div className="mt-3">
        <Button onClick={run} disabled={loading || !tenant.trim() || !role.trim() || !object.trim()}>
          {loading ? "Simulating…" : "Simulate"}
        </Button>
      </div>
      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      {result && (
        <div className="mt-4">
          <div className="flex items-center gap-2">
            <Badge tone={result.allow ? "good" : "bad"}>{result.allow ? "Allowed" : "Denied"}</Badge>
            <p className="text-sm text-zinc-600">{result.reason}</p>
          </div>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                <th className="py-1">Field</th>
                <th className="py-1">Visibility</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={2} className="py-2 text-zinc-500">No fields returned for this decision.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.field} className="border-t border-zinc-100">
                  <td className="py-1.5 font-mono">{r.field}</td>
                  <td className="py-1.5">
                    <Badge tone={visibilityTone(r.visibility)}>
                      {r.visibility === "masked" ? `Masked(${r.strategy})` : r.visibility === "visible" ? "Visible" : "Hidden"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function PolicyHistoryPanel() {
  const history = useResource<api.PolicyHistoryEntry[]>(() => api.listPolicyHistory(90), []);
  return (
    <Card className="p-5">
      <SectionHeader title="Policy Change History" subtitle="Policy edits over the last 90 days, sourced from the audit ledger." />
      <ResourceState
        loading={history.loading}
        error={history.error}
        empty={history.data.length === 0}
        emptyTitle="No policy changes"
        emptyBody="Policy authoring events from GET /policy/history will appear here."
      />
      {!history.loading && !history.error && history.data.length > 0 && (
        <ul className="mt-2 space-y-2 text-sm">
          {history.data.map((h) => (
            <li key={h.id} className="rounded-xl border border-zinc-200 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">{h.action}{h.role ? <> · <span className="font-mono">{h.role}</span></> : null}</p>
                  <p className="text-xs text-zinc-500">{h.summary}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-zinc-600">{h.actor}</p>
                  <p className="text-[11px] text-zinc-400 mt-1">{new Date(h.occurredAt).toLocaleString()}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function AuditFilterBar({
  filter,
  onChange,
}: {
  filter: api.AuditFilter;
  onChange: (next: api.AuditFilter) => void;
}) {
  const [actor, setActor] = useState(filter.actor ?? "");
  const [action, setAction] = useState(filter.action ?? "");
  const [tenantId, setTenantId] = useState(filter.tenantId ?? "");
  const [from, setFrom] = useState(filter.from ?? "");
  const [to, setTo] = useState(filter.to ?? "");

  const apply = () => {
    const next: api.AuditFilter = {};
    if (actor.trim()) next.actor = actor.trim();
    if (action.trim()) next.action = action.trim();
    if (tenantId.trim()) next.tenantId = tenantId.trim();
    if (from) next.from = new Date(from).toISOString();
    if (to) next.to = new Date(to).toISOString();
    onChange(next);
  };

  const reset = () => {
    setActor(""); setAction(""); setTenantId(""); setFrom(""); setTo("");
    onChange({});
  };

  return (
    <div className="mt-3 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2 text-xs">
      <input
        value={actor}
        onChange={(e) => setActor(e.target.value)}
        placeholder="Actor email"
        className="rounded-xl border border-zinc-200 bg-white p-2 outline-none focus:border-zinc-500"
      />
      <input
        value={action}
        onChange={(e) => setAction(e.target.value)}
        placeholder="Action (e.g. tenant.suspend)"
        className="rounded-xl border border-zinc-200 bg-white p-2 outline-none focus:border-zinc-500"
      />
      <input
        value={tenantId}
        onChange={(e) => setTenantId(e.target.value)}
        placeholder="Tenant ID"
        className="rounded-xl border border-zinc-200 bg-white p-2 outline-none focus:border-zinc-500"
      />
      <input
        type="datetime-local"
        value={from}
        onChange={(e) => setFrom(e.target.value)}
        className="rounded-xl border border-zinc-200 bg-white p-2 outline-none focus:border-zinc-500"
      />
      <input
        type="datetime-local"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        className="rounded-xl border border-zinc-200 bg-white p-2 outline-none focus:border-zinc-500"
      />
      <div className="md:col-span-5 flex gap-2">
        <Button onClick={apply}>Apply filter</Button>
        <Button variant="outline" onClick={reset}>Reset</Button>
      </div>
    </div>
  );
}

// MfaPanel — TOTP enrolment + status + disable, scoped to the current
// admin (the BFF reads the user from the admin_session cookie). Enable
// flow: GET /auth/mfa/enroll returns QR + recovery codes (shown ONCE),
// then POST /auth/mfa/confirm with the first 6-digit code flips
// mfa_enabled=true. Disable requires presenting a current code.
function MfaPanel() {
  const status = useResource<api.MfaStatus | null>(api.getMfaStatus, null);
  const [enrolment, setEnrolment] = useState<api.MfaEnrolment | null>(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: "good" | "warn" | "bad"; text: string } | null>(null);
  const [disableMode, setDisableMode] = useState(false);

  const beginEnrol = async () => {
    setFeedback(null);
    setSubmitting(true);
    try {
      const data = await api.enrollMfa();
      setEnrolment(data);
      setCode("");
    } catch (e: any) {
      setFeedback({ tone: "bad", text: e?.message ?? "Enrol failed." });
    } finally {
      setSubmitting(false);
    }
  };

  const confirm = async () => {
    if (code.trim().length < 6) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      await api.confirmMfa(code.trim());
      setFeedback({ tone: "good", text: "MFA enabled. Save your recovery codes — they won't be shown again." });
      status.refetch();
      setEnrolment(null);
      setCode("");
    } catch (e: any) {
      setFeedback({ tone: "bad", text: e?.message ?? "Invalid code. Try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const disable = async () => {
    if (code.trim().length < 6) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      await api.disableMfa(code.trim());
      setFeedback({ tone: "warn", text: "MFA disabled. Re-enrol from this panel any time." });
      status.refetch();
      setDisableMode(false);
      setCode("");
    } catch (e: any) {
      setFeedback({ tone: "bad", text: e?.message ?? "Disable failed." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-5">
      <h3 className="text-lg font-bold">Multi-factor auth</h3>
      <ResourceState loading={status.loading} error={status.error} empty={false} />
      {status.data && (
        <p className="mt-3 text-sm">
          {status.data.enabled
            ? <>Status: <Badge tone="good">Enabled</Badge> · {status.data.recoveryCodesRemaining} recovery codes remaining</>
            : <>Status: <Badge tone="warn">Not enabled</Badge> — required for non-Owner teammates.</>}
        </p>
      )}

      {feedback && (
        <div className={`mt-3 rounded-2xl border p-3 text-sm ${
          feedback.tone === "good" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : feedback.tone === "warn" ? "border-amber-200 bg-amber-50 text-amber-900"
          : "border-red-200 bg-red-50 text-red-800"
        }`}>{feedback.text}</div>
      )}

      {!status.data?.enabled && !enrolment && (
        <Button onClick={beginEnrol} disabled={submitting} className="mt-4 w-full">
          {submitting ? "..." : "Enable MFA"}
        </Button>
      )}

      {enrolment && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-zinc-600">
            Scan with Google Authenticator, 1Password, or Authy. Then enter the 6-digit code below.
          </p>
          <img src={enrolment.qrCodeDataUrl} alt="MFA QR code" className="rounded-xl border border-zinc-200" />
          <p className="text-[11px] text-zinc-500 break-all">
            Secret: <code>{enrolment.secret}</code>
          </p>
          <div>
            <p className="text-xs font-semibold text-zinc-700">Recovery codes (save these now)</p>
            <pre className="mt-1 rounded-xl border border-zinc-300 bg-zinc-50 p-3 text-[11px] font-mono whitespace-pre-wrap leading-5">
              {enrolment.recoveryCodes.join("\n")}
            </pre>
            <button
              type="button"
              className="text-xs text-zinc-700 hover:text-zinc-950 underline mt-1"
              onClick={() => downloadText("ivorycom-mfa-recovery-codes.txt", enrolment.recoveryCodes.join("\n"))}
            >
              Download as .txt
            </button>
          </div>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            inputMode="numeric"
            className="w-full rounded-xl border border-zinc-300 bg-white p-2 text-center text-lg tracking-widest outline-none focus:border-zinc-500"
          />
          <Button onClick={confirm} disabled={submitting || code.length < 6} className="w-full">
            {submitting ? "..." : "Confirm + enable"}
          </Button>
        </div>
      )}

      {status.data?.enabled && !disableMode && (
        <Button variant="outline" className="mt-4 w-full" onClick={() => { setDisableMode(true); setCode(""); setFeedback(null); }}>
          Disable MFA
        </Button>
      )}

      {disableMode && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-zinc-600">
            Enter a current 6-digit code or recovery code to confirm.
          </p>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456 or recovery code"
            className="w-full rounded-xl border border-zinc-300 bg-white p-2 text-sm outline-none focus:border-zinc-500"
          />
          <div className="flex gap-2">
            <Button onClick={disable} disabled={submitting || code.trim().length < 6}>
              {submitting ? "..." : "Confirm disable"}
            </Button>
            <Button variant="outline" onClick={() => setDisableMode(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function downloadText(filename: string, content: string): void {
  if (typeof document === "undefined") return;
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// MfaChallenge — full-screen TOTP/recovery prompt shown after Google
// SSO when the user has MFA enabled. The BFF set an admin_mfa_pending
// cookie during /auth/google/callback; this component POSTs the code
// to /auth/mfa/challenge which swaps it for the real admin_session.
// On success we trigger a window.location reload so AuthGate refetches
// /auth/me and renders the portal proper.
function MfaChallenge({ onSuccess }: { onSuccess: () => void }) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (code.trim().length < 6) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.challengeMfa(code.trim());
      onSuccess();
    } catch (e: any) {
      setError(e?.message ?? "Invalid code.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-100 p-4">
      <Card className="w-full max-w-md p-6">
        <h2 className="text-xl font-bold">Two-factor verification</h2>
        <p className="mt-2 text-sm text-zinc-500">
          Enter the 6-digit code from your authenticator app, or a recovery code.
        </p>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123456 or recovery code"
          className="mt-4 w-full rounded-xl border border-zinc-300 bg-white p-3 text-center text-lg tracking-widest outline-none focus:border-zinc-500"
          autoFocus
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        />
        {error && (
          <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}
        <Button onClick={submit} disabled={submitting || code.trim().length < 6} className="mt-4 w-full">
          {submitting ? "..." : "Verify + sign in"}
        </Button>
      </Card>
    </div>
  );
}
function Audit({ actor, action, timeAgo }: { actor: string; action: string; timeAgo: string }) { return <div className="rounded-2xl border border-zinc-200 p-4"><p className="font-semibold">{actor}</p><p className="text-sm text-zinc-500">{action} · {timeAgo}</p></div>; }

function Sparkline({ values, color = "#0ea5e9" }: { values: number[]; color?: string }) {
  if (!values || values.length === 0) {
    return <div className="h-8 w-24 rounded bg-zinc-100" />;
  }
  const data = values.map((v, i) => ({ i, v }));
  return (
    <div className="h-8 w-24">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function formatTimelineLabel(ts: string) {
  try {
    const d = new Date(ts);
    return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  } catch {
    return ts;
  }
}

function HealthTimelineChart({ points }: { points: HealthTimelinePoint[] }) {
  const data = points.map((p) => ({
    label: formatTimelineLabel(p.ts),
    apiLatencyMs: p.apiLatencyMs,
    dbLatencyMs: p.dbLatencyMs,
    servicesUp: p.servicesUp,
  }));
  const maxServices = points.reduce((m, p) => Math.max(m, p.servicesTotal ?? 0), 0) || undefined;
  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 12, right: 24, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} interval="preserveStartEnd" />
          <YAxis yAxisId="latency" tick={{ fontSize: 11, fill: "#71717a" }} width={48} />
          <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 11, fill: "#71717a" }} width={40} domain={[0, maxServices ?? "auto"]} allowDecimals={false} />
          <Tooltip
            contentStyle={{ borderRadius: 12, border: "1px solid #e4e4e7", fontSize: 12 }}
            labelStyle={{ color: "#52525b" }}
          />
          <Line yAxisId="latency" type="monotone" dataKey="apiLatencyMs" stroke="#0ea5e9" strokeWidth={2} dot={false} name="Service p95 latency (ms)" connectNulls />
          <Line yAxisId="latency" type="monotone" dataKey="dbLatencyMs" stroke="#10b981" strokeWidth={2} dot={false} name="Admin DB latency (ms)" connectNulls />
          <Line yAxisId="count" type="monotone" dataKey="servicesUp" stroke="#f59e0b" strokeWidth={2} dot={false} name="Services up" connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Live-refresh cadence for the System Health page. 60s matches the
// admin-bff in-memory cache TTL — going faster just hits the same
// cached payload without earning fresher data.
const HEALTH_POLL_MS = 60_000;

function HealthSection() {
  const health = useResource<HealthMetrics | null>(api.getHealthMetrics, null);
  const services = useResource<ServiceHealth[]>(api.getHealthServices, []);
  const timeline = useResource<HealthTimelinePoint[]>(api.getHealthTimeline, []);
  const [live, setLive] = useState(true);
  const [lastTickAt, setLastTickAt] = useState<Date | null>(null);
  const h = health.data;
  const stub = h?.source === "stub";

  // Auto-refresh while the tab is visible. Pauses on hide so a parked
  // tab doesn't re-probe the fleet for nothing.
  useEffect(() => {
    if (!live) return;
    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      health.refetch();
      services.refetch();
      timeline.refetch();
      setLastTickAt(new Date());
    };
    setLastTickAt(new Date());
    const id = window.setInterval(tick, HEALTH_POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  return <section className="grid grid-cols-1 gap-4">
    <div className="flex items-center justify-between gap-2 text-sm text-zinc-500">
      <div className="flex items-center gap-2">
        <span className={`inline-block w-2 h-2 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-zinc-400"}`} />
        <span>{live ? "Live — refreshes every 60s" : "Paused"}</span>
        {lastTickAt && live && (
          <span className="text-xs text-zinc-400">· last tick {lastTickAt.toLocaleTimeString()}</span>
        )}
      </div>
      <button
        type="button"
        onClick={() => setLive((v) => !v)}
        className="text-xs px-3 py-1.5 rounded-full bg-zinc-100 hover:bg-zinc-200"
      >
        {live ? "Pause live updates" : "Resume live updates"}
      </button>
    </div>
    {stub && (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        SERVICE_HEALTH_TARGETS isn't configured in this environment — no services are being probed.
      </div>
    )}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      <StatCard title="Services Up" value={h ? `${h.servicesUp}/${h.servicesTotal}` : null} sub={h ? (h.servicesUp === h.servicesTotal ? "All services healthy" : `${h.servicesTotal - h.servicesUp} down or degraded`) : null} icon="server"/>
      <StatCard title="Service Latency" value={h ? (h.apiLatencyMs != null ? `${h.apiLatencyMs}ms` : "—") : null} sub="p95 health probe, live" icon="activity"/>
      <StatCard title="Admin DB" value={h ? (h.dbLatencyMs != null ? `${h.dbLatencyMs}ms` : "—") : null} sub={h ? (h.dbLatencyMs != null ? "Round-trip latency" : "Unreachable") : null} icon="database"/>
      <StatCard title="Webhook Failures" value={h?.webhookFailures24h ?? null} sub="Delivery failures, last 24h" icon="plug"/>
    </div>
    <Card className="p-5">
      <SectionHeader title="System Health Timeline" subtitle="Service p95 latency, admin DB latency, and services up — last 24 hours, 5-minute samples." />
      {timeline.error ? (
        <div className="h-48 rounded-2xl bg-zinc-50 border border-zinc-200 flex items-center justify-center text-sm text-red-600">Failed to load timeline: {timeline.error}</div>
      ) : timeline.loading || timeline.data.length === 0 ? (
        <div className="h-48 rounded-2xl bg-zinc-50 border border-zinc-200 flex items-center justify-center text-zinc-500">{timeline.loading ? "Loading metrics…" : "No samples yet — the sampler records a point every 5 minutes."}</div>
      ) : (
        <HealthTimelineChart points={timeline.data} />
      )}
    </Card>
    <Card className="p-5">
      <SectionHeader title="Railway Services" subtitle="Live health probe of every service — status, response time, and the last two hours of latency." />
      {services.error ? (
        <p className="text-sm text-red-600">Failed to load services: {services.error}</p>
      ) : services.loading ? (
        <p className="text-sm text-zinc-500">Loading services…</p>
      ) : services.data.length === 0 ? (
        <p className="text-sm text-zinc-500">No services configured — set SERVICE_HEALTH_TARGETS on the admin backend.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-4 font-medium">Service</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Latency</th>
                <th className="py-2 pr-4 font-medium">Detail</th>
                <th className="py-2 pr-4 font-medium">Latency 2h</th>
              </tr>
            </thead>
            <tbody>
              {services.data.map((s) => {
                const tone = s.status === "healthy" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : s.status === "degraded" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200";
                const lat = s.latencyMs;
                const latTone = lat == null ? "text-zinc-400" : lat > 1500 ? "text-red-600" : lat > 600 ? "text-amber-600" : "text-emerald-600";
                return (
                  <tr key={s.serviceName} className="border-b border-zinc-100 last:border-0">
                    <td className="py-2 pr-4 font-mono text-xs">{s.serviceName}</td>
                    <td className="py-2 pr-4"><span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}>{s.status}</span></td>
                    <td className={`py-2 pr-4 font-semibold ${latTone}`}>{lat == null ? "—" : `${lat}ms`}</td>
                    <td className="py-2 pr-4 text-xs text-zinc-500">{s.detail ?? "—"}</td>
                    <td className="py-2 pr-4"><Sparkline values={s.latencySparkline} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  </section>;
}

function AnnouncementsSection() {
  const announcements = useResource<api.AnnouncementRow[]>(api.listAnnouncements, []);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [expiresAt, setExpiresAt] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: "good" | "bad"; text: string } | null>(null);

  const reset = () => {
    setTitle("");
    setBody("");
    setAudience("all");
    setExpiresAt("");
    setFeedback(null);
  };

  const publish = async () => {
    if (!title.trim() || !body.trim() || publishing) return;
    setPublishing(true);
    setFeedback(null);
    try {
      await api.createAnnouncement({
        title: title.trim(),
        body: body.trim(),
        audience: audience.trim() || "all",
        expiresAt: expiresAt || undefined,
      });
      setFeedback({ tone: "good", text: "Announcement published." });
      reset();
      announcements.refetch();
    } catch (err: any) {
      setFeedback({ tone: "bad", text: err?.message ?? "Failed to publish." });
    } finally {
      setPublishing(false);
    }
  };

  return <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
    <Card className="xl:col-span-2 p-5">
      <SectionHeader title="Customer Announcements" subtitle="Send product updates, outage notices, onboarding messages, and release notes to selected tenants." />
      <div className="space-y-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Announcement title…"
          className="w-full rounded-2xl border border-zinc-200 px-4 py-3 outline-none"
          maxLength={255}
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full h-44 rounded-2xl border border-zinc-200 p-4 outline-none"
          placeholder="Write announcement message…"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Audience</label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              className="mt-1 w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm bg-white"
            >
              <option value="all">All tenants</option>
              <option value="plan:starter">Plan: Starter</option>
              <option value="plan:pro">Plan: Pro</option>
              <option value="plan:business">Plan: Business</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Expires (optional)</label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="mt-1 w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button onClick={publish} disabled={publishing || !title.trim() || !body.trim()}>
            <Icon name="mail" className="mr-2"/>
            {publishing ? "Publishing…" : "Publish Announcement"}
          </Button>
          <Button variant="outline" onClick={reset} disabled={publishing}>Reset</Button>
          {feedback && (
            <span className={feedback.tone === "good" ? "text-sm text-emerald-700" : "text-sm text-red-700"}>{feedback.text}</span>
          )}
        </div>
      </div>
      <div className="mt-6">
        <h3 className="text-lg font-bold">Recent announcements</h3>
        <ResourceState loading={announcements.loading} error={announcements.error} empty={announcements.data.length === 0} emptyTitle="None yet" emptyBody="Published announcements will appear here." />
        {announcements.data.length > 0 && (
          <div className="mt-3 space-y-2">
            {announcements.data.map((a) => (
              <div key={a.id} className="rounded-xl border border-zinc-200 p-3">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm">{a.title}</p>
                  <span className="text-xs text-zinc-500">{a.audience}</span>
                </div>
                <p className="text-sm text-zinc-600 mt-1 whitespace-pre-wrap">{a.body}</p>
                <p className="text-xs text-zinc-400 mt-2">
                  Published {a.publishedAt ? new Date(a.publishedAt).toLocaleString() : "—"}
                  {a.expiresAt && ` · Expires ${new Date(a.expiresAt).toLocaleDateString()}`}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
    <Card className="p-5">
      <h3 className="text-lg font-bold">Targeting</h3>
      <p className="text-sm text-zinc-500 mt-2">
        Audience values map to backend filtering: <code className="text-xs">all</code>,{" "}
        <code className="text-xs">plan:&lt;tier&gt;</code>, or <code className="text-xs">tenant:&lt;id&gt;</code>.
      </p>
      <p className="text-sm text-zinc-500 mt-3">
        Email + in-app banner delivery is handled by the announcement worker once it picks up the row.
      </p>
    </Card>
  </section>;
}

function LogsErrorsSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const canSeeLogs = hasPerm(currentUser, "system:logs");
  const canSeeErrors = hasPerm(currentUser, "system:errors");
  const initialTab: "errors" | "logs" = canSeeErrors ? "errors" : "logs";
  const [tab, setTab] = useState<"errors" | "logs">(initialTab);
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");

  const errors = useResource<AuditEntry[]>(
    () => canSeeErrors ? api.listSecurityErrors({ actor: actor || undefined, action: action || undefined, limit: 200 }) : Promise.resolve([] as AuditEntry[]),
    [],
    [actor, action, tab],
  );
  const logs = useResource<AuditEntry[]>(
    () => canSeeLogs ? api.listSecurityLogs({ actor: actor || undefined, action: action || undefined, limit: 200 }) : Promise.resolve([] as AuditEntry[]),
    [],
    [actor, action, tab],
  );
  const data = tab === "errors" ? errors : logs;

  return (
    <section className="space-y-4">
      <Card className="p-5">
        <SectionHeader
          title="Logs & Errors"
          subtitle="Admin-action audit events. Application stdout/stderr logs live in Railway — open each service's deploy logs (or Grafana) for those."
        />
        <div className="flex flex-wrap gap-2 items-center mb-4">
          {canSeeErrors && (
            <button type="button" onClick={() => setTab("errors")} className={`px-3 py-2 rounded-2xl text-xs font-semibold ${tab === "errors" ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600"}`}>
              Errors{errors.data.length > 0 && tab !== "errors" ? ` (${errors.data.length})` : ""}
            </button>
          )}
          {canSeeLogs && (
            <button type="button" onClick={() => setTab("logs")} className={`px-3 py-2 rounded-2xl text-xs font-semibold ${tab === "logs" ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600"}`}>
              Logs
            </button>
          )}
          <div className="flex-1"/>
          <input
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            placeholder="Filter by actor email…"
            className="rounded-2xl border border-zinc-200 px-3 py-2 text-sm outline-none w-56"
          />
          <input
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="Filter by action…"
            className="rounded-2xl border border-zinc-200 px-3 py-2 text-sm outline-none w-56"
          />
        </div>
        <ResourceState loading={data.loading} error={data.error} empty={data.data.length === 0} emptyTitle={tab === "errors" ? "No errors" : "No log entries"} emptyBody="Either nothing happened in the window or the filters are too narrow." />
        {!data.loading && !data.error && data.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                  <th className="py-2 pr-4 font-medium">When</th>
                  <th className="py-2 pr-4 font-medium">Actor</th>
                  <th className="py-2 pr-4 font-medium">Action</th>
                  <th className="py-2 pr-4 font-medium">Resource</th>
                  <th className="py-2 pr-4 font-medium">Tenant</th>
                  <th className="py-2 pr-4 font-medium">Result</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((row) => (
                  <tr key={row.id} className="border-b border-zinc-100 last:border-0">
                    <td className="py-2 pr-4 text-zinc-500 whitespace-nowrap">{row.timeAgo}</td>
                    <td className="py-2 pr-4">{row.actor}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{row.action}</td>
                    <td className="py-2 pr-4 text-zinc-600">{row.resourceType ? `${row.resourceType}${row.resourceId ? `:${row.resourceId}` : ""}` : "—"}</td>
                    <td className="py-2 pr-4 text-zinc-600">{row.tenantId ?? "—"}</td>
                    <td className="py-2 pr-4">
                      <Badge tone={row.result === "success" ? "good" : row.result === "failure" ? "bad" : "warn"}>
                        {row.result}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </section>
  );
}

function FeatureFlagsSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const flags = useResource<api.FeatureFlagRow[]>(api.listFeatureFlags, []);
  const tenants = useResource<Tenant[]>(api.listTenants, []);
  const canWrite = hasPerm(currentUser, "feature_flag:write");
  const [tenantFilter, setTenantFilter] = useState<string>("");
  const [newKey, setNewKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: "good" | "bad"; text: string } | null>(null);

  const visibleFlags = flags.data.filter((f) => {
    if (!tenantFilter) return true;
    if (tenantFilter === "__global__") return f.tenantId == null;
    return f.tenantId === tenantFilter;
  });

  const toggle = async (flag: api.FeatureFlagRow, enabled: boolean) => {
    if (!canWrite) return;
    setSaving(true);
    setFeedback(null);
    try {
      await api.upsertFeatureFlag({ flagKey: flag.flagKey, tenantId: flag.tenantId ?? undefined, enabled });
      setFeedback({ tone: "good", text: `${flag.flagKey}: ${enabled ? "enabled" : "disabled"}.` });
      flags.refetch();
    } catch (err: any) {
      setFeedback({ tone: "bad", text: err?.message ?? "Failed to toggle flag." });
    } finally {
      setSaving(false);
    }
  };

  const create = async () => {
    if (!newKey.trim() || !canWrite) return;
    setSaving(true);
    setFeedback(null);
    try {
      await api.upsertFeatureFlag({
        flagKey: newKey.trim(),
        tenantId: tenantFilter && tenantFilter !== "__global__" ? tenantFilter : undefined,
        enabled: false,
      });
      setNewKey("");
      setFeedback({ tone: "good", text: "Flag created (disabled)." });
      flags.refetch();
    } catch (err: any) {
      setFeedback({ tone: "bad", text: err?.message ?? "Failed to create flag." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4">
      <Card className="p-5">
        <SectionHeader title="Feature flags" subtitle="Toggle flags globally or per tenant. Tenant-scoped rows override the global default." />
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Scope</label>
          <select
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            className="rounded-2xl border border-zinc-200 px-3 py-2 text-sm bg-white"
          >
            <option value="">All flags</option>
            <option value="__global__">Global only</option>
            {tenants.data.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {canWrite && (
            <>
              <input
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="New flag key (e.g. beta_dashboard)"
                className="rounded-2xl border border-zinc-200 px-3 py-2 text-sm outline-none w-72"
              />
              <Button onClick={create} disabled={saving || !newKey.trim()}>{saving ? "Saving…" : "Add flag"}</Button>
            </>
          )}
          {feedback && <span className={feedback.tone === "good" ? "text-sm text-emerald-700" : "text-sm text-red-700"}>{feedback.text}</span>}
        </div>
        <ResourceState loading={flags.loading} error={flags.error} empty={visibleFlags.length === 0} emptyTitle="No flags" emptyBody="No flags match the current scope." />
        {visibleFlags.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                  <th className="py-2 pr-4 font-medium">Flag</th>
                  <th className="py-2 pr-4 font-medium">Scope</th>
                  <th className="py-2 pr-4 font-medium">State</th>
                  <th className="py-2 pr-4 font-medium">Updated</th>
                  <th className="py-2 pr-4 font-medium">Toggle</th>
                </tr>
              </thead>
              <tbody>
                {visibleFlags.map((f) => {
                  const tenantName = f.tenantId
                    ? tenants.data.find((t) => t.id === f.tenantId)?.name ?? f.tenantId
                    : "Global default";
                  return (
                    <tr key={f.id} className="border-b border-zinc-100 last:border-0">
                      <td className="py-2 pr-4 font-mono text-xs">{f.flagKey}</td>
                      <td className="py-2 pr-4">{tenantName}</td>
                      <td className="py-2 pr-4">
                        <Badge tone={f.enabled ? "good" : "default"}>{f.enabled ? "Enabled" : "Disabled"}</Badge>
                      </td>
                      <td className="py-2 pr-4 text-zinc-500">{new Date(f.updatedAt).toLocaleString()}</td>
                      <td className="py-2 pr-4">
                        {canWrite ? (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => toggle(f, !f.enabled)}
                            className={`text-xs px-3 py-1.5 rounded-full disabled:opacity-50 ${f.enabled ? "bg-zinc-100 text-zinc-700" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
                          >
                            {f.enabled ? "Disable" : "Enable"}
                          </button>
                        ) : (
                          <span className="text-xs text-zinc-400">read-only</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </section>
  );
}

function SettingsSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const settings = useResource<AdminSettings | null>(api.getAdminSettings, null);
  const env = useResource<EnvironmentInfo | null>(api.getEnvironmentInfo, null);
  const e = env.data;
  return <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
    <Card className="xl:col-span-2 p-5">
      <SectionHeader title="Admin Portal Settings" subtitle="Configure support routing, notification rules, escalation paths, data retention, and internal branding." />
      {settings.loading && <p className="text-sm text-zinc-500">Loading settings…</p>}
      {settings.error && <p className="text-sm text-red-600">Failed to load settings: {settings.error}</p>}
      {!settings.loading && !settings.error && (
        <SystemSettingsEditor currentUser={currentUser} initial={settings.data} onSaved={() => settings.refetch()} />
      )}
      <SupportMacrosEditor currentUser={currentUser} />
      <ApprovalPoliciesEditor currentUser={currentUser} />
    </Card>
    <Card className="p-5">
      <h3 className="text-lg font-bold">Environment</h3>
      <div className="space-y-3 mt-4 text-sm">
        <Info label="Region" value={e?.region ?? "—"}/>
        <Info label="Mode" value={e?.mode ?? "—"}/>
        <Info label="Build" value={e?.build ?? "—"}/>
        <Info label="API URL" value={api.API_URL || "Not configured"}/>
      </div>
    </Card>
  </section>;
}
function Setting({ title, body }) { return <div className="rounded-2xl border border-zinc-200 p-4"><p className="font-semibold">{title}</p><p className="text-sm text-zinc-500 mt-1">{body}</p></div>; }

function SystemSettingsEditor({
  currentUser,
  initial,
  onSaved,
}: {
  currentUser: api.CurrentUser | null;
  initial: AdminSettings | null;
  onSaved: () => void;
}) {
  const canEdit = hasPerm(currentUser, "settings:write");
  const [draft, setDraft] = useState<AdminSettings>({
    supportSLA: initial?.supportSLA ?? "",
    chatRouting: initial?.chatRouting ?? "",
    dataRetention: initial?.dataRetention ?? "",
    featureFlags: initial?.featureFlags ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: "good" | "bad"; text: string } | null>(null);

  useEffect(() => {
    if (initial) setDraft({ ...initial });
  }, [initial]);

  if (!hasPerm(currentUser, "settings:read")) return null;

  const dirty = initial != null && (
    draft.supportSLA !== (initial.supportSLA ?? "") ||
    draft.chatRouting !== (initial.chatRouting ?? "") ||
    draft.dataRetention !== (initial.dataRetention ?? "") ||
    draft.featureFlags !== (initial.featureFlags ?? "")
  );

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    setFeedback(null);
    try {
      await api.updateAdminSettings(draft);
      setFeedback({ tone: "good", text: "Settings saved." });
      onSaved();
    } catch (err: any) {
      setFeedback({ tone: "bad", text: err?.message ?? "Save failed." });
    } finally {
      setSaving(false);
    }
  };

  const fields: { key: keyof AdminSettings; title: string; placeholder: string }[] = [
    { key: "supportSLA", title: "Support SLA", placeholder: "e.g. 24h response · 4h chat" },
    { key: "chatRouting", title: "Chat routing", placeholder: "e.g. round-robin" },
    { key: "dataRetention", title: "Data retention", placeholder: "e.g. 90d hot, 1y cold" },
    { key: "featureFlags", title: "Feature flags summary", placeholder: "e.g. managed per-tenant" },
  ];

  return (
    <div className="grid md:grid-cols-2 gap-3">
      {fields.map(({ key, title, placeholder }) => (
        <div key={key} className="rounded-2xl border border-zinc-200 p-4">
          <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wide" htmlFor={`setting-${key}`}>{title}</label>
          <input
            id={`setting-${key}`}
            value={draft[key]}
            onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            placeholder={placeholder}
            disabled={!canEdit}
            className="mt-2 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm outline-none focus:border-zinc-500 disabled:bg-zinc-50 disabled:text-zinc-500"
          />
        </div>
      ))}
      {canEdit && (
        <div className="md:col-span-2 flex items-center gap-3">
          <Button onClick={save} disabled={saving || !dirty}>{saving ? "Saving…" : "Save settings"}</Button>
          {feedback && <span className={feedback.tone === "good" ? "text-sm text-emerald-700" : "text-sm text-red-700"}>{feedback.text}</span>}
        </div>
      )}
    </div>
  );
}

function SupportMacrosEditor({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const macros = useResource<api.SupportMacro[]>(api.listSupportMacros, []);
  const canManage = hasPerm(currentUser, "support:macro_manage");
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editBody, setEditBody] = useState("");

  if (!hasPerm(currentUser, "support:read")) return null;

  const reset = () => { setName(""); setBody(""); setError(null); };
  const startEdit = (m: api.SupportMacro) => { setEditingId(m.id); setEditName(m.name); setEditBody(m.body); };
  const cancelEdit = () => { setEditingId(null); setEditName(""); setEditBody(""); };

  const create = async () => {
    if (!name.trim() || !body.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.createSupportMacro({ name: name.trim(), body: body.trim() });
      reset();
      macros.refetch();
    } catch (e: any) {
      setError(e?.message ?? "Failed to create macro.");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateSupportMacro(editingId, { name: editName.trim(), body: editBody.trim() });
      cancelEdit();
      macros.refetch();
    } catch (e: any) {
      setError(e?.message ?? "Failed to update macro.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this macro? Operators using it will lose the saved text.")) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteSupportMacro(id);
      macros.refetch();
    } catch (e: any) {
      setError(e?.message ?? "Failed to delete macro.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-zinc-200 p-4">
      <div>
        <h4 className="font-semibold">Support macros</h4>
        <p className="text-xs text-zinc-500 mt-1">
          Canned responses operators can insert from the Support Inbox compose row.
          {!canManage && " Read-only — `support:macro_manage` required to edit."}
        </p>
      </div>
      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      <ResourceState loading={macros.loading} error={macros.error} empty={false} />
      {macros.data.length === 0 && !macros.loading && (
        <p className="mt-3 text-sm text-zinc-500">No macros yet.</p>
      )}
      {macros.data.length > 0 && (
        <div className="mt-3 space-y-2">
          {macros.data.map((m) => (
            <div key={m.id} className="rounded-xl border border-zinc-200 p-3">
              {editingId === m.id ? (
                <div className="space-y-2">
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm"
                  />
                  <textarea
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    rows={3}
                    className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <Button onClick={saveEdit} disabled={saving || !editName.trim() || !editBody.trim()}>{saving ? "Saving…" : "Save"}</Button>
                    <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{m.name}</p>
                    <p className="text-xs text-zinc-500 mt-1 whitespace-pre-wrap">{m.body}</p>
                  </div>
                  {canManage && (
                    <div className="flex gap-1 shrink-0">
                      <button type="button" onClick={() => startEdit(m)} className="text-xs px-2 py-1 rounded-md bg-zinc-100 hover:bg-zinc-200">Edit</button>
                      <button type="button" onClick={() => remove(m.id)} className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100">Delete</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {canManage && (
        <div className="mt-4 rounded-xl border border-dashed border-zinc-300 p-3">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Create new macro</p>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (e.g. Refund issued)"
            className="mt-2 w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder="Macro body — supports plain text. Operators can edit before sending."
            className="mt-2 w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm"
          />
          <div className="mt-2 flex gap-2">
            <Button onClick={create} disabled={saving || !name.trim() || !body.trim()}>{saving ? "Saving…" : "Add macro"}</Button>
            <Button variant="outline" onClick={reset}>Reset</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ApprovalPoliciesEditor — formfor admin_settings['approval.policies'].
// Lets OWNER/CFO tune refund/credit/terminate/suspend thresholds without
// hand-editing JSON in the database. Loads current policy via
// GET /settings/approval-policies, edits in-place, PUT on Save. The
// backend re-validates and invalidates the PolicyProvider cache.
function ApprovalPoliciesEditor({
  currentUser,
}: {
  currentUser: api.CurrentUser | null;
}) {
  const policies = useResource<api.ApprovalPolicies | null>(api.getApprovalPolicies, null);
  const [draft, setDraft] = useState<api.ApprovalPolicies | null>(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: "good" | "bad"; text: string } | null>(null);

  const canEdit = hasPerm(currentUser, "settings:write");

  // Initialise the draft once the data lands.
  useEffect(() => {
    if (policies.data && !draft) {
      setDraft(structuredClone(policies.data));
    }
  }, [policies.data, draft]);

  if (!hasPerm(currentUser, "settings:read")) return null;

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setFeedback(null);
    try {
      await api.updateApprovalPolicies(draft);
      setFeedback({ tone: "good", text: "Approval policies saved. Cache invalidated — next request uses these thresholds." });
      policies.refetch();
    } catch (e: any) {
      setFeedback({ tone: "bad", text: e?.message ?? "Save failed." });
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (policies.data) setDraft(structuredClone(policies.data));
    setFeedback(null);
  };

  return (
    <div className="mt-6 rounded-2xl border border-zinc-200 p-4">
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-semibold">Approval policies</h4>
          <p className="text-xs text-zinc-500 mt-1">
            Tier ladder for high-risk admin actions. Below the tier's role, the action holds for approval.
          </p>
        </div>
      </div>
      <ResourceState loading={policies.loading} error={policies.error} empty={!policies.data} />
      {draft && (
        <div className="mt-4 space-y-5">
          <AmountTierEditor
            name="Refund tiers"
            tiers={draft.refund ?? []}
            onChange={(refund) => setDraft({ ...draft, refund })}
            disabled={!canEdit}
          />
          <AmountTierEditor
            name="Credit tiers"
            tiers={draft.credit ?? []}
            onChange={(credit) => setDraft({ ...draft, credit })}
            disabled={!canEdit}
          />
          <SimpleTierEditor
            name="Tenant suspend"
            tiers={draft.tenant_suspend ?? []}
            onChange={(tenant_suspend) => setDraft({ ...draft, tenant_suspend })}
            disabled={!canEdit}
          />
          <SimpleTierEditor
            name="Tenant terminate"
            tiers={draft.tenant_terminate ?? []}
            onChange={(tenant_terminate) => setDraft({ ...draft, tenant_terminate })}
            disabled={!canEdit}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <NumberSetting
              label="Mass-action min tenants"
              value={draft.mass_action_min_tenants ?? 5}
              onChange={(mass_action_min_tenants) => setDraft({ ...draft, mass_action_min_tenants })}
              disabled={!canEdit}
            />
            <NumberSetting
              label="Impersonation max minutes"
              value={draft.impersonation_max_minutes ?? 30}
              onChange={(impersonation_max_minutes) => setDraft({ ...draft, impersonation_max_minutes })}
              disabled={!canEdit}
            />
          </div>
          {feedback && (
            <div className={`rounded-2xl border p-3 text-sm ${
              feedback.tone === "good" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-red-200 bg-red-50 text-red-800"
            }`}>{feedback.text}</div>
          )}
          {canEdit && (
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving}>{saving ? "..." : "Save"}</Button>
              <Button variant="outline" onClick={reset} disabled={saving}>Reset to saved</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const POLICY_ROLES = [
  "SUPPORT_AGENT", "SUPPORT_LEAD", "ENGINEER", "MARKETING", "SALES",
  "CFO", "CEO", "OWNER", "READ_ONLY", "CFO+OWNER",
];

function AmountTierEditor({
  name,
  tiers,
  onChange,
  disabled,
}: {
  name: string;
  tiers: api.PolicyTier[];
  onChange: (next: api.PolicyTier[]) => void;
  disabled: boolean;
}) {
  const update = (i: number, patch: Partial<api.PolicyTier>) => {
    onChange(tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  };
  const remove = (i: number) => onChange(tiers.filter((_, idx) => idx !== i));
  const add = () => onChange([...tiers, { max_cents: 0, role: "SUPPORT_AGENT" }]);

  return (
    <div>
      <p className="text-xs font-semibold text-zinc-700">{name}</p>
      <p className="text-[11px] text-zinc-500">First tier matched by max_cents wins. Last tier should have max_cents=null (catch-all).</p>
      <div className="mt-2 space-y-2">
        {tiers.map((tier, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <label className="text-zinc-500 shrink-0">Up to ($)</label>
            <input
              type="number"
              min={0}
              step="0.01"
              disabled={disabled}
              value={tier.max_cents == null ? "" : tier.max_cents / 100}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") return update(i, { max_cents: null });
                const dollars = parseFloat(raw);
                if (Number.isFinite(dollars)) update(i, { max_cents: Math.round(dollars * 100) });
              }}
              placeholder="(blank = no cap)"
              className="w-32 rounded-lg border border-zinc-200 bg-white p-1.5 outline-none focus:border-zinc-500 disabled:bg-zinc-50"
            />
            <label className="text-zinc-500">→ role</label>
            <select
              value={tier.role}
              disabled={disabled}
              onChange={(e) => update(i, { role: e.target.value })}
              className="rounded-lg border border-zinc-200 bg-white p-1.5 outline-none focus:border-zinc-500 disabled:bg-zinc-50"
            >
              {POLICY_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            {!disabled && (
              <button type="button" onClick={() => remove(i)} className="text-red-700 hover:text-red-900 ml-auto">
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
      {!disabled && (
        <button
          type="button"
          onClick={add}
          className="mt-2 text-xs text-zinc-700 hover:text-zinc-950 underline"
        >
          + Add tier
        </button>
      )}
    </div>
  );
}

function SimpleTierEditor({
  name,
  tiers,
  onChange,
  disabled,
}: {
  name: string;
  tiers: api.PolicyTier[];
  onChange: (next: api.PolicyTier[]) => void;
  disabled: boolean;
}) {
  const role = tiers[0]?.role ?? "SUPPORT_LEAD";
  return (
    <div className="flex items-center gap-3 text-xs">
      <p className="text-xs font-semibold text-zinc-700 w-40">{name}</p>
      <label className="text-zinc-500">Required role</label>
      <select
        value={role}
        disabled={disabled}
        onChange={(e) => onChange([{ role: e.target.value }])}
        className="rounded-lg border border-zinc-200 bg-white p-1.5 outline-none focus:border-zinc-500 disabled:bg-zinc-50"
      >
        {POLICY_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
    </div>
  );
}

function NumberSetting({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
  disabled: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-zinc-700">{label}</label>
      <input
        type="number"
        min={1}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          if (Number.isFinite(v) && v >= 1) onChange(v);
        }}
        className="w-32 rounded-lg border border-zinc-200 bg-white p-1.5 outline-none focus:border-zinc-500 disabled:bg-zinc-50"
      />
    </div>
  );
}

// ─── Slice F: role-scoped dashboards ───────────────────────────────────
// One section per role (Legal / Tech Support / Live Agent / Billing
// Ops / Technical). Legal is the editorial centerpiece — it renders
// inside `.legal-journal` so the OGC palette + Fraunces typography
// scope to this section only (see index.css `.legal-journal` block).

function legalUrgencyLabel(u: api.LegalMatterUrgency): string {
  if (u === "classified_72hr") return "Classified · 72-hr";
  if (u === "standard") return "Standard";
  return "Low";
}
function legalUrgencyStamp(u: api.LegalMatterUrgency): string {
  if (u === "classified_72hr") return "stamp--escalated";
  if (u === "standard") return "stamp--filed";
  return "stamp--awaiting";
}
function legalStatusStamp(s: api.LegalMatterStatus): string {
  if (s === "escalated" || s === "classified") return "stamp--escalated";
  if (s === "awaiting_counsel" || s === "awaiting_signature" || s === "awaiting_sign_off") return "stamp--awaiting";
  if (s === "privileged_review") return "stamp--privileged";
  return "stamp--filed";
}
function legalStatusLabel(s: api.LegalMatterStatus): string {
  return s.replace(/_/g, " ");
}
function legalAge(ageMs: number): string {
  const days = Math.floor(ageMs / 86_400_000);
  const hrs = Math.floor((ageMs % 86_400_000) / 3_600_000);
  if (days > 0) return `${days}d ${hrs}h`;
  const mins = Math.floor((ageMs % 3_600_000) / 60_000);
  return `${hrs}h ${mins}m`;
}
function legalDeadline(deadline: string | null, now = Date.now()): string {
  if (!deadline) return "—";
  const ms = new Date(deadline).getTime() - now;
  if (ms < 0) return "overdue";
  const days = Math.floor(ms / 86_400_000);
  const hrs = Math.floor((ms % 86_400_000) / 3_600_000);
  if (days > 0) return `${days}d ${hrs}h left`;
  return `${hrs}h left`;
}

function LegalSection() {
  const summary = useResource<api.LegalSummary | null>(api.getLegalSummary, null);
  const matters = useResource<api.LegalMatter[]>(() => api.listLegalMatters({ limit: 50 }), []);
  const audit = useResource<{ days: number; entries: api.LegalAuditEntry[] } | null>(() => api.listLegalAudit(7, 25), null);
  const [selected, setSelected] = React.useState<api.LegalMatter | null>(null);
  const s = summary.data;
  const auditEntries = audit.data?.entries ?? [];

  return (
    <>
    <section className="legal-journal">
      <header className="lj-masthead">
        <div className="lj-meta">
          <div><span className="lj-dot" /><b>Office of General Counsel</b></div>
          <div>Records retention · 7 yr · SOC 2 / FedRAMP</div>
        </div>
        <h1 className="lj-title">The Legal <em>Journal</em></h1>
        <div className="lj-meta lj-meta-right">
          <div>{new Date().toUTCString().slice(0, 22)} UTC</div>
          <div>Federal review window: <b>OPEN</b></div>
        </div>
      </header>
      <div className="lj-subhead-rule"><span /><span>Filed under privilege · For internal counsel use only</span><span /></div>
      <div className="lj-standing">
        <div className="lj-standing-cell">
          <span className="lj-label">Active matters</span>
          <span className="lj-standing-value">{s?.activeMatters ?? "—"}<sup>open</sup></span>
          <span className="lj-standing-meta">{s ? `${s.awaitingCounsel} awaiting counsel · ${s.escalated} escalated` : ""}</span>
        </div>
        <div className="lj-standing-cell">
          <span className="lj-label">GDPR data deletion</span>
          <span className="lj-standing-value">{s?.gdprDeletionRequests ?? "—"}<sup>req</sup></span>
          <span className={`lj-standing-meta ${s && s.gdprInside72HrClock > 0 ? "warn" : ""}`}>{s && s.gdprInside72HrClock > 0 ? <>{s.gdprInside72HrClock} inside <b>72-hr clock</b></> : "Clock clear"}</span>
        </div>
        <div className="lj-standing-cell">
          <span className="lj-label">DPA / contract review</span>
          <span className="lj-standing-value">{s?.dpaQueue ?? "—"}<sup>queue</sup></span>
          <span className="lj-standing-meta">{s?.dpaMedianAgeDays != null ? <>median age <b>{s.dpaMedianAgeDays}d</b> · oldest {s.dpaOldestAgeDays}d</> : "Queue clear"}</span>
        </div>
        <div className="lj-standing-cell">
          <span className="lj-label">Compliance posture</span>
          <span className="lj-standing-value">{s?.compliancePosture.tier ?? "—"}<sup>tier</sup></span>
          <span className="lj-standing-meta">{s?.compliancePosture.note ?? ""}</span>
        </div>
      </div>
      <ResourceState loading={matters.loading || summary.loading} error={matters.error ?? summary.error} empty={!matters.loading && matters.data.length === 0} emptyTitle="No active matters" emptyBody="Counsel's desk is clear. New filings appear here as they're routed in." />
      {matters.data.length > 0 && (
        <div className="lj-section-head">
          <span className="lj-ord">III.</span>
          <h2>Active <em>matters</em></h2>
          <span className="lj-label">{matters.data.length} open · sorted by urgency</span>
        </div>
      )}
      <div className="lj-dossiers">
        {matters.data.map((m) => (
          <article key={m.id} className="lj-dossier" tabIndex={0} onClick={() => setSelected(m)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(m); } }} aria-label={`Open dossier ${m.caseId}`}>
            <div className="lj-dossier-case">
              FILE
              <b>{m.caseId}</b>
              Opened {new Date(m.openedAt).toUTCString().slice(5, 16)}
              <div className="lj-dossier-age">Age · {legalAge(m.ageMs)}</div>
            </div>
            <div className="lj-dossier-body">
              <span className="lj-dossier-tag">{m.type.replace(/_/g, " ")}</span>
              <h3 className="lj-dossier-subject" dangerouslySetInnerHTML={{ __html: m.subject.replace(/`([^`]+)`|\b([\w.-]+\.(?:io|com|fr|de|es|net|org))\b/g, (_, a, b) => `<em>${a ?? b}</em>`) }} />
              {m.body && <p className="lj-dossier-meta">{m.body}</p>}
            </div>
            <span className={`lj-stamp ${legalUrgencyStamp(m.urgency)}`}>{legalUrgencyLabel(m.urgency).toLowerCase()}</span>
          </article>
        ))}
      </div>
      <div className="lj-section-head" style={{ marginTop: 64 }}>
        <span className="lj-ord">IV.</span>
        <h2>Audit · <em>actions of legal interest</em></h2>
        <span className="lj-label">{audit.data?.days ?? 7} days · {auditEntries.length} entries</span>
      </div>
      <table className="lj-audit-table" aria-label="Audit log filtered to legal-relevant actions">
        <thead>
          <tr>
            <th></th>
            <th>When (UTC)</th>
            <th>Actor</th>
            <th>Action</th>
            <th>Outcome</th>
          </tr>
        </thead>
        <tbody>
          {auditEntries.map((e, i) => (
            <tr key={e.id}>
              <td className="n">{i + 1}.</td>
              <td className="when">{new Date(e.occurredAt).toUTCString().slice(5, 22)}</td>
              <td className="who"><b>{e.actorEmail ?? "system"}</b></td>
              <td className="what">{e.action}{e.tenantId ? <em> · {e.tenantId}</em> : null}<span className="ref">{e.resourceType ?? ""}</span></td>
              <td className={`outcome outcome--${e.result === "success" ? "filed" : e.result === "denied" ? "denied" : "noted"}`}>{e.result}</td>
            </tr>
          ))}
          {audit.loading && <tr><td colSpan={5} style={{ padding: 12 }}>Loading audit feed…</td></tr>}
          {!audit.loading && auditEntries.length === 0 && <tr><td colSpan={5} style={{ padding: 12 }}>No legal-relevant audit entries in the past 7 days.</td></tr>}
        </tbody>
      </table>
      {selected && <LegalDossierDrawer matter={selected} onClose={() => setSelected(null)} onUpdated={(m) => { setSelected(m); matters.refetch(); summary.refetch(); audit.refetch(); }} />}
    </section>
    <section className="grid grid-cols-1 xl:grid-cols-4 gap-4 mt-6">
      <TeamMemberOverlay title="By team member" columns={["actions", "approvals", "pending", "lastActive"]} />
    </section>
    </>
  );
}

function LegalDossierDrawer({ matter, onClose, onUpdated }: { matter: api.LegalMatter; onClose: () => void; onUpdated: (m: api.LegalMatter) => void }) {
  const [busy, setBusy] = React.useState<null | "execute" | "extend" | "forward">(null);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const execute = async () => {
    setBusy("execute"); setErr(null);
    try {
      const updated = await api.updateLegalMatter(matter.id, { status: "closed" });
      onUpdated(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="lj-drawer-veil open" onClick={onClose} aria-hidden />
      <aside className="lj-drawer open" role="dialog" aria-modal="true" aria-labelledby="lj-drawer-title">
        <button className="lj-drawer-close" onClick={onClose} aria-label="Close dossier">Close</button>
        <header className="lj-drawer-head">
          <div className="lj-drawer-case">
            <span>FILE · <b>{matter.caseId}</b></span>
            <span className={`lj-stamp ${legalStatusStamp(matter.status)}`}>{legalStatusLabel(matter.status)}</span>
          </div>
          <h2 id="lj-drawer-title">{matter.subject}</h2>
        </header>
        <div className="lj-drawer-body">
          {matter.body && <p className="lj-pull">{matter.body}</p>}
          <section>
            <h3>Particulars</h3>
            <dl className="lj-kv">
              <dt>Tenant</dt><dd>{matter.tenantId ?? "—"}</dd>
              {matter.particulars?.principals && (<><dt>Principals</dt><dd>{matter.particulars.principals.join(" · ")}</dd></>)}
              {matter.particulars?.lawful_basis && (<><dt>Lawful basis</dt><dd>{matter.particulars.lawful_basis}</dd></>)}
              {matter.particulars?.retention_test && (<><dt>Retention test</dt><dd>{matter.particulars.retention_test}</dd></>)}
              <dt>Clock</dt><dd>{matter.deadlineAt ? `${legalDeadline(matter.deadlineAt)} · ends ${new Date(matter.deadlineAt).toUTCString().slice(0, 22)}` : "—"}</dd>
              <dt>Opened</dt><dd>{new Date(matter.openedAt).toUTCString().slice(0, 22)}</dd>
              {matter.routedBy && (<><dt>Routed by</dt><dd>{matter.routedBy}</dd></>)}
              {matter.assignedCounsel && (<><dt>Counsel</dt><dd>{matter.assignedCounsel}</dd></>)}
            </dl>
          </section>
          {matter.counselNotes && (
            <section>
              <h3>Counsel notes</h3>
              <p>{matter.counselNotes}</p>
            </section>
          )}
          <section>
            <h3>Actions</h3>
            {err && <p className="lj-error">{err}</p>}
            <div className="lj-drawer-actions">
              <button className="lj-btn lj-btn-primary" onClick={execute} disabled={busy !== null || matter.status === "closed"}>
                {busy === "execute" ? "Signing…" : matter.status === "closed" ? "Already filed" : "Sign & execute"}
              </button>
              <button className="lj-btn" disabled>Request 24-hr extension</button>
              <button className="lj-btn lj-btn-ghost" disabled>Forward to outside counsel</button>
            </div>
          </section>
        </div>
      </aside>
    </>
  );
}

function TechSupportSection() {
  const queue = useResource<api.TechSupportQueue | null>(api.getTechSupportQueue, null);
  const sla = useResource<api.TechSupportSla | null>(api.getTechSupportSla, null);
  const q = queue.data;
  const sl = sla.data;
  return (
    <section className="grid grid-cols-1 xl:grid-cols-4 gap-4">
      <StatCard title="Open Tickets" value={q?.totalOpen ?? null} sub={q ? `${q.unassignedCount} unassigned` : null} icon="support" />
      <StatCard title="Oldest Open" value={q?.oldestOpenAgeMinutes != null ? `${q.oldestOpenAgeMinutes}m` : "—"} sub="In queue" icon="clock" />
      <StatCard title="SLA Breaches" value={sl?.breachedTotal ?? null} sub={sl ? `high ${sl.breachedHigh} · med ${sl.breachedMedium} · low ${sl.breachedLow}` : null} icon="warning" />
      <StatCard title="High Priority" value={q?.byPriority.high ?? null} sub="Open + escalated" icon="activity" />
      <Card className="xl:col-span-2 p-5">
        <SectionHeader title="Queue depth by status" subtitle="Open / pending / escalated tickets aggregated from support-service." />
        <ResourceState loading={queue.loading} error={queue.error} empty={!queue.loading && (!q || q.totalOpen === 0)} emptyTitle="No open tickets" emptyBody="Inbox is clear." />
        {q && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(q.byStatus).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded-2xl border border-zinc-200 p-3"><span className="text-sm font-medium capitalize">{k.replace(/_/g, " ")}</span><Badge tone={k === "escalated" ? "bad" : k === "pending" ? "warn" : "info"}>{v}</Badge></div>
            ))}
          </div>
        )}
      </Card>
      <Card className="xl:col-span-2 p-5">
        <SectionHeader title="SLA thresholds" subtitle={sl ? `high ≤ ${sl.thresholdsMinutes.high}m · medium ≤ ${sl.thresholdsMinutes.medium}m · low ≤ ${sl.thresholdsMinutes.low}m` : "loading"} />
        <ResourceState loading={sla.loading} error={sla.error} empty={!sla.loading && sl?.breachedTotal === 0} emptyTitle="SLA clean" emptyBody="Every open ticket is within first-response threshold." />
        {sl && sl.breachedTotal > 0 && (
          <div className="grid md:grid-cols-3 gap-3">
            <div className="rounded-2xl border border-red-200 bg-red-50 p-3"><div className="text-xs text-zinc-500">High</div><div className="text-2xl font-bold">{sl.breachedHigh}</div></div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3"><div className="text-xs text-zinc-500">Medium</div><div className="text-2xl font-bold">{sl.breachedMedium}</div></div>
            <div className="rounded-2xl border border-zinc-200 p-3"><div className="text-xs text-zinc-500">Low</div><div className="text-2xl font-bold">{sl.breachedLow}</div></div>
          </div>
        )}
      </Card>
      <TeamMemberOverlay title="By team member" columns={["tickets", "live", "resolved", "lastActive"]} />
    </section>
  );
}

function LiveAgentSection() {
  const sessions = useResource<api.SessionsSnapshot | null>(api.getLiveAgentSessions, null);
  const seats = useResource<api.SeatsSnapshot | null>(api.getLiveAgentSeats, null);
  const s = sessions.data;
  const st = seats.data;
  return (
    <section className="grid grid-cols-1 xl:grid-cols-4 gap-4">
      <StatCard title="Active Sessions" value={s?.totalActive ?? null} sub={s != null && s.unassigned > 0 ? `${s.unassigned} unassigned` : null} icon="message" />
      <StatCard title="Avg Age" value={s?.avgAgeMinutes != null ? `${s.avgAgeMinutes}m` : "—"} sub="Across active live-chat" icon="clock" />
      <StatCard title="Agents Online" value={st?.online.length ?? null} sub={st ? `${st.totalActive} sessions claimed` : null} icon="users" />
      <StatCard title="Unclaimed" value={st?.unassignedConversations ?? null} sub="Need pickup" icon="warning" />
      <Card className="xl:col-span-2 p-5">
        <SectionHeader title="Per-agent presence" subtitle="Aggregated from live_chat ticket assignments." />
        <ResourceState loading={seats.loading} error={seats.error} empty={!seats.loading && (!st || st.online.length === 0)} emptyTitle="No agents claimed" emptyBody="Every live chat is unclaimed." />
        {st && st.online.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-zinc-500"><tr><th className="py-2 text-left">Agent</th><th className="text-right">Sessions</th><th className="text-right">Unread</th><th className="text-right">Longest</th></tr></thead>
            <tbody>{st.online.map((seat) => (<tr key={seat.agentId} className="border-t border-zinc-100"><td className="py-2">{seat.agentId}</td><td className="text-right">{seat.activeSessions}</td><td className="text-right">{seat.unreadAcrossSessions}</td><td className="text-right">{seat.longestAgeMinutes}m</td></tr>))}</tbody>
          </table>
        )}
      </Card>
      <Card className="xl:col-span-2 p-5">
        <SectionHeader title="Active sessions" subtitle="Live-chat tickets currently in handoff." />
        <ResourceState loading={sessions.loading} error={sessions.error} empty={!sessions.loading && (!s || s.totalActive === 0)} emptyTitle="No live chats" emptyBody="No customers are currently in a live-chat handoff." />
        {s && s.active.length > 0 && (
          <ul className="space-y-2">
            {s.active.map((sess) => (
              <li key={sess.ticketId} className="rounded-2xl border border-zinc-200 p-3 text-sm">
                <div className="flex justify-between"><span className="font-medium">{sess.customerName}</span><span className="text-xs text-zinc-500">{sess.ageMinutes}m</span></div>
                <div className="text-xs text-zinc-600">{sess.subject}</div>
                <div className="text-xs text-zinc-500 mt-1">{sess.assignedTo ? `Assigned · ${sess.assignedTo}` : <Badge tone="warn">Unclaimed</Badge>} · {sess.tenantId}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <TeamMemberOverlay title="By team member" columns={["live", "tickets", "resolved", "lastActive"]} />
    </section>
  );
}

function BillingOpsSection() {
  const overview = useResource<api.BillingAdminOverview | null>(api.getBillingAdminOverview, null);
  const overrides = useResource<api.BillingPlanOverride[]>(api.listBillingPlanOverrides, []);
  const o = overview.data;
  return (
    <section className="grid grid-cols-1 xl:grid-cols-4 gap-4">
      <StatCard title="MRR" value={o ? formatMoney(o.mrrCents / 100) : null} sub="Net revenue" icon="dollar" />
      <StatCard title="Failed Payments" value={o?.failedPayments ?? null} sub="Need follow-up" icon="warning" />
      <StatCard title="Active Trials" value={o?.trialsActive ?? null} sub={o?.trialsAvgDaysLeft != null ? `${o.trialsAvgDaysLeft}d avg left` : null} icon="clock" />
      <StatCard title="Plan Overrides" value={overrides.data.length} sub="Currently active" icon="card" />
      <Card className="xl:col-span-4 p-5">
        <SectionHeader title="Active plan overrides" subtitle="Tenants flying on a manual plan-tier override (Slice D). Authority resolves via auth-service tenants.plan_override; reason is enriched from the most recent audit entry." />
        <ResourceState loading={overrides.loading} error={overrides.error} empty={!overrides.loading && overrides.data.length === 0} emptyTitle="No active overrides" emptyBody="Every tenant is on their billed plan." />
        {overrides.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-zinc-500"><tr><th className="py-2 text-left">Tenant</th><th className="text-left">Plan</th><th className="text-left">Status</th><th className="text-left">Applied by</th><th className="text-left">When</th><th className="text-left">Reason</th></tr></thead>
            <tbody>{overrides.data.map((row) => (<tr key={row.tenantId} className="border-t border-zinc-100"><td className="py-2">{row.tenantName}</td><td><Badge tone="info">{row.plan}</Badge></td><td>{row.status}</td><td className="text-xs">{row.appliedBy ?? "—"}</td><td className="text-xs">{row.appliedAt ? new Date(row.appliedAt).toUTCString().slice(5, 22) : "—"}</td><td className="text-xs text-zinc-600">{row.reason ?? "—"}</td></tr>))}</tbody>
          </table>
        )}
      </Card>
      <TeamMemberOverlay title="By team member" columns={["actions", "approvals", "pending", "lastActive"]} />
    </section>
  );
}

function TechnicalSection() {
  const health = useResource<{ services: api.TechnicalServiceHealth[] } | null>(api.getTechnicalInfraHealth, null);
  const alerts = useResource<{ recent: api.TechnicalAlert[]; sinceUtc: string } | null>(api.getTechnicalAlerts, null);
  const services = health.data?.services ?? [];
  const alertsList = alerts.data?.recent ?? [];
  const healthyCount = services.filter((s) => s.status === "healthy").length;
  const degradedCount = services.filter((s) => s.status === "degraded").length;
  const unreachableCount = services.filter((s) => s.status === "unreachable").length;
  return (
    <section className="grid grid-cols-1 xl:grid-cols-4 gap-4">
      <StatCard title="Services Healthy" value={`${healthyCount}/${services.length || 0}`} sub="Probed every load" icon="server" />
      <StatCard title="Degraded" value={degradedCount} sub={degradedCount > 0 ? "Returning non-200" : null} icon="warning" />
      <StatCard title="Unreachable" value={unreachableCount} sub={unreachableCount > 0 ? "No /healthz response" : null} icon="activity" />
      <StatCard title="Alerts (24h)" value={alertsList.length} sub="System audit events" icon="warning" />
      <Card className="xl:col-span-4 p-5">
        <SectionHeader title="Sibling service health" subtitle="Best-effort /healthz probe across the platform's HTTP services. 1s timeout per probe." />
        <ResourceState loading={health.loading} error={health.error} empty={!health.loading && services.length === 0} emptyTitle="No sibling services" emptyBody="Service URLs aren't configured in this environment." />
        {services.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-zinc-500"><tr><th className="py-2 text-left">Service</th><th className="text-left">Status</th><th className="text-left">Latency</th><th className="text-left">Detail</th></tr></thead>
            <tbody>{services.map((svc) => (<tr key={svc.name} className="border-t border-zinc-100"><td className="py-2">{svc.name}</td><td><Badge tone={svc.status === "healthy" ? "good" : svc.status === "degraded" ? "warn" : "bad"}>{svc.status}</Badge></td><td className="text-xs">{svc.latencyMs != null ? `${svc.latencyMs}ms` : "—"}</td><td className="text-xs text-zinc-600">{svc.detail ?? svc.url}</td></tr>))}</tbody>
          </table>
        )}
      </Card>
      <Card className="xl:col-span-4 p-5">
        <SectionHeader title="Recent system alerts" subtitle={alerts.data ? `Since ${new Date(alerts.data.sinceUtc).toUTCString()}` : "loading"} />
        <ResourceState loading={alerts.loading} error={alerts.error} empty={!alerts.loading && alertsList.length === 0} emptyTitle="No alerts" emptyBody="No system_alert / integration / webhook failures in the past 24 hours." />
        {alertsList.length > 0 && (
          <ul className="space-y-2">{alertsList.map((a) => (<li key={a.id} className="rounded-2xl border border-zinc-200 p-3 text-sm"><div className="flex justify-between"><span className="font-medium">{a.action}</span><span className="text-xs text-zinc-500">{new Date(a.occurredAt).toUTCString().slice(5, 22)}</span></div><div className="text-xs text-zinc-600">{a.resourceType ?? ""}{a.resourceType && a.resourceId ? "·" : ""}{a.resourceId ?? ""}</div></li>))}</ul>
        )}
      </Card>
      <TeamMemberOverlay title="By team member" columns={["actions", "approvals", "pending", "lastActive"]} />
    </section>
  );
}

// Pillar 5 — coupons. Stripe is the source of truth; admin-bff proxies to the
// billing service. Visible only with `billing:coupons` (OWNER/CEO/CFO).
const COUPON_PLAN_OPTIONS = ["starter", "growth", "business", "enterprise"] as const;

// Where the customer-facing pricing page lives. A campaign promo link drops
// the prospect straight onto /pricing with the code pre-applied (the SPA reads
// ?promo= and passes it to Stripe checkout). Slice 3 of Pillar 5.
const CUSTOMER_APP_URL = "https://app.ivorycomcrm.com";
function couponPromoLink(code: string): string {
  return `${CUSTOMER_APP_URL}/pricing?promo=${encodeURIComponent(code)}`;
}

function formatCouponDiscount(c: api.Coupon): string {
  if (c.percentOff != null) return `${c.percentOff}% off`;
  if (c.amountOffCents != null) {
    const cur = (c.currency || "usd").toUpperCase();
    return `${formatMoney(c.amountOffCents / 100)} ${cur} off`;
  }
  return "—";
}

function formatCouponDuration(c: api.Coupon): string {
  if (c.duration === "repeating") return `${c.durationMonths ?? "?"} months`;
  if (c.duration === "forever") return "Forever";
  return "Once";
}

function CouponsSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const coupons = useResource<api.Coupon[]>(api.listCoupons, []);
  const [showCreate, setShowCreate] = useState(false);
  const [banner, setBanner] = useState<{ tone: "good" | "bad"; text: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const canManage = hasPerm(currentUser, "billing:coupons");

  const copyLink = async (c: api.Coupon) => {
    const link = couponPromoLink(c.code);
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Clipboard API can be blocked (insecure context / permissions) — fall
      // back to a prompt so the admin can still grab the link.
      window.prompt("Copy this campaign link:", link);
    }
    setCopiedId(c.promotionCodeId);
    window.setTimeout(() => setCopiedId((id) => (id === c.promotionCodeId ? null : id)), 1800);
  };

  const list = coupons.data;
  const activeCount = list.filter((c) => c.active).length;
  const totalRedemptions = list.reduce((sum, c) => sum + (c.timesRedeemed || 0), 0);

  const deactivate = async (c: api.Coupon) => {
    if (!window.confirm(`Deactivate coupon "${c.code}"? It can no longer be redeemed.`)) return;
    setBusyId(c.promotionCodeId);
    setBanner(null);
    try {
      await api.deactivateCoupon(c.promotionCodeId);
      setBanner({ tone: "good", text: `Coupon "${c.code}" deactivated.` });
      coupons.refetch();
    } catch (e: any) {
      setBanner({ tone: "bad", text: e?.message ?? "Deactivation failed." });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="space-y-4">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <StatCard title="Coupons" value={list.length} sub={`${activeCount} active`} icon="tag" />
        <StatCard title="Total Redemptions" value={totalRedemptions} sub="Across all coupons" icon="check" />
        <StatCard title="Active" value={activeCount} sub="Currently redeemable" icon="dollar" />
      </div>

      {banner && (
        <div className={`rounded-2xl border p-3 text-sm ${banner.tone === "good" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"}`}>
          {banner.text}
        </div>
      )}

      <Card className="p-5">
        <SectionHeader
          title="Discount coupons"
          subtitle="Stripe-native coupons + promotion codes. Redeemable at checkout, or share a campaign link that pre-applies the code on the pricing page. Source of truth is Stripe — redemption counts are live."
          action={canManage ? (
            <Button onClick={() => { setBanner(null); setShowCreate(true); }}>
              <Icon name="tag" size={16} className="mr-2" /> New coupon
            </Button>
          ) : null}
        />
        <ResourceState
          loading={coupons.loading}
          error={coupons.error}
          empty={!coupons.loading && list.length === 0}
          emptyTitle="No coupons yet"
          emptyBody={canManage ? "Create your first discount coupon to share a promo code with customers." : "No coupons have been created."}
        />
        {list.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-zinc-500">
                <tr>
                  <th className="py-2 text-left">Code</th>
                  <th className="text-left">Discount</th>
                  <th className="text-left">Duration</th>
                  <th className="text-left">Campaign</th>
                  <th className="text-left">Eligible plans</th>
                  <th className="text-left">Redemptions</th>
                  <th className="text-left">Expires</th>
                  <th className="text-left">Status</th>
                  {canManage && <th className="text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.promotionCodeId} className="border-t border-zinc-100">
                    <td className="py-2 font-mono font-medium">{c.code}</td>
                    <td>{formatCouponDiscount(c)}</td>
                    <td className="text-xs">{formatCouponDuration(c)}</td>
                    <td className="text-xs text-zinc-600">{c.campaignName ? <Badge tone="info">{c.campaignName}</Badge> : "—"}</td>
                    <td className="text-xs text-zinc-600">{c.eligiblePlans?.length ? c.eligiblePlans.join(", ") : "All plans"}</td>
                    <td className="text-xs">{c.timesRedeemed}{c.maxRedemptions ? ` / ${c.maxRedemptions}` : ""}</td>
                    <td className="text-xs">{c.expiresAtUnix ? new Date(c.expiresAtUnix * 1000).toUTCString().slice(5, 16) : "—"}</td>
                    <td><Badge tone={c.active ? "good" : "default"}>{c.active ? "Active" : "Inactive"}</Badge></td>
                    {canManage && (
                      <td className="text-right whitespace-nowrap">
                        {c.active ? (
                          <div className="inline-flex gap-2">
                            <Button variant="outline" className="px-3 py-1.5 text-xs" onClick={() => copyLink(c)} title={couponPromoLink(c.code)}>
                              {copiedId === c.promotionCodeId ? "Copied!" : "Copy link"}
                            </Button>
                            <Button variant="outline" className="px-3 py-1.5 text-xs" disabled={busyId === c.promotionCodeId} onClick={() => deactivate(c)}>
                              {busyId === c.promotionCodeId ? "…" : "Deactivate"}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-zinc-400">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <CreateCouponModal
          onClose={() => setShowCreate(false)}
          onSuccess={(code) => {
            setShowCreate(false);
            setBanner({ tone: "good", text: `Coupon "${code}" created.` });
            coupons.refetch();
          }}
        />
      )}
    </section>
  );
}

function CreateCouponModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: (code: string) => void }) {
  const [code, setCode] = useState("");
  const [discountType, setDiscountType] = useState<"percent" | "amount">("percent");
  const [percentOff, setPercentOff] = useState("20");
  const [amountOff, setAmountOff] = useState("10");
  const [currency, setCurrency] = useState("usd");
  const [duration, setDuration] = useState<api.CouponDuration>("once");
  const [durationMonths, setDurationMonths] = useState("3");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [plans, setPlans] = useState<string[]>([]);
  const [campaignName, setCampaignName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const togglePlan = (p: string) =>
    setPlans((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const fieldCls = "w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm bg-white outline-none";
  const labelCls = "block text-sm";
  const hintCls = "text-zinc-500";

  const submit = async () => {
    setError(null);
    const trimmed = code.trim();
    if (trimmed.length < 3) { setError("Code must be at least 3 characters."); return; }
    const input: api.CreateCouponInput = { code: trimmed, duration };
    if (discountType === "percent") {
      const v = Number(percentOff);
      if (!(v >= 1 && v <= 100)) { setError("Percent off must be between 1 and 100."); return; }
      input.percentOff = v;
    } else {
      const v = Math.round(Number(amountOff) * 100);
      if (!(v >= 1)) { setError("Amount off must be greater than 0."); return; }
      input.amountOffCents = v;
      input.currency = currency.trim().toLowerCase() || "usd";
    }
    if (duration === "repeating") {
      const m = Number(durationMonths);
      if (!(m >= 1)) { setError("Repeating coupons need a duration in months (≥ 1)."); return; }
      input.durationMonths = m;
    }
    if (maxRedemptions.trim() !== "") {
      const m = Number(maxRedemptions);
      if (!(m >= 0)) { setError("Max redemptions can't be negative."); return; }
      if (m > 0) input.maxRedemptions = m;
    }
    if (expiresAt.trim() !== "") {
      const ts = Math.floor(new Date(expiresAt).getTime() / 1000);
      if (!Number.isFinite(ts)) { setError("Invalid expiry date."); return; }
      input.expiresAtUnix = ts;
    }
    if (plans.length > 0) input.eligiblePlans = plans;
    if (campaignName.trim() !== "") input.campaignName = campaignName.trim();

    setSubmitting(true);
    try {
      await api.createCoupon(input);
      onSuccess(trimmed);
    } catch (e: any) {
      setError(e?.message ?? "Failed to create coupon.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="New discount coupon"
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>{submitting ? "Creating…" : "Create coupon"}</Button>
        </>
      }
    >
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <div className="space-y-4">
        <label className={labelCls}>
          <span className={hintCls}>Promo code</span>
          <input className={`${fieldCls} font-mono uppercase`} value={code} placeholder="LAUNCH20" maxLength={40}
            onChange={(e) => setCode(e.target.value.toUpperCase())} autoFocus />
          <span className="mt-1 block text-xs text-zinc-400">What customers type at checkout. Letters & numbers.</span>
        </label>

        <div>
          <span className={hintCls}>Discount</span>
          <div className="mt-1 flex gap-2">
            <button type="button" onClick={() => setDiscountType("percent")}
              className={`flex-1 rounded-xl border px-3 py-2 text-sm ${discountType === "percent" ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white"}`}>Percent off</button>
            <button type="button" onClick={() => setDiscountType("amount")}
              className={`flex-1 rounded-xl border px-3 py-2 text-sm ${discountType === "amount" ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white"}`}>Fixed amount</button>
          </div>
          {discountType === "percent" ? (
            <div className="mt-2 flex items-center gap-2">
              <input type="number" min={1} max={100} className={fieldCls} value={percentOff} onChange={(e) => setPercentOff(e.target.value)} />
              <span className="text-sm text-zinc-500">%</span>
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2">
              <input type="number" min={0} step="0.01" className={fieldCls} value={amountOff} onChange={(e) => setAmountOff(e.target.value)} />
              <input className="w-24 rounded-xl border border-zinc-200 px-3 py-2 text-sm uppercase" value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value)} />
            </div>
          )}
        </div>

        <label className={labelCls}>
          <span className={hintCls}>Duration</span>
          <select className={fieldCls} value={duration} onChange={(e) => setDuration(e.target.value as api.CouponDuration)}>
            <option value="once">Once — one invoice</option>
            <option value="repeating">Repeating — N months</option>
            <option value="forever">Forever — every invoice</option>
          </select>
        </label>
        {duration === "repeating" && (
          <label className={labelCls}>
            <span className={hintCls}>Repeats for (months)</span>
            <input type="number" min={1} max={36} className={fieldCls} value={durationMonths} onChange={(e) => setDurationMonths(e.target.value)} />
          </label>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className={labelCls}>
            <span className={hintCls}>Max redemptions</span>
            <input type="number" min={0} className={fieldCls} value={maxRedemptions} placeholder="Unlimited" onChange={(e) => setMaxRedemptions(e.target.value)} />
          </label>
          <label className={labelCls}>
            <span className={hintCls}>Expires (optional)</span>
            <input type="date" className={fieldCls} value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </label>
        </div>

        <div>
          <span className={hintCls}>Eligible plans</span>
          <span className="ml-2 text-xs text-zinc-400">leave empty for all plans</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {COUPON_PLAN_OPTIONS.map((p) => (
              <button key={p} type="button" onClick={() => togglePlan(p)}
                className={`rounded-full border px-3 py-1.5 text-xs capitalize ${plans.includes(p) ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white text-zinc-700"}`}>
                {p}
              </button>
            ))}
          </div>
        </div>

        <label className={labelCls}>
          <span className={hintCls}>Campaign name</span>
          <span className="ml-2 text-xs text-zinc-400">optional — for attribution</span>
          <input className={fieldCls} value={campaignName} placeholder="e.g. Summer Launch 2026" maxLength={120}
            onChange={(e) => setCampaignName(e.target.value)} />
          <span className="mt-1 block text-xs text-zinc-400">
            Tag this coupon to a campaign. Redemptions attribute back to it. Share this link in your email:
          </span>
          <code className="mt-1 block break-all rounded-lg bg-zinc-50 border border-zinc-200 px-2 py-1 text-xs text-zinc-600">
            {couponPromoLink(code.trim() || "CODE")}
          </code>
        </label>
      </div>
    </ModalShell>
  );
}

// ----- Pillar 4: App Review desk -----

function priorityTone(p: string): "bad" | "info" | "default" {
  return p === "high" ? "bad" : p === "standard" ? "info" : "default";
}

function ReviewDetailView({ versionId, onBack, onDone }: { versionId: string; onBack: () => void; onDone: () => void }) {
  const detail = useResource<api.ReviewDetail | null>(() => api.getReviewDetail(versionId), null, [versionId]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const d = detail.data;
  const packet = (d?.reviewPacket ?? {}) as any;
  const sec = packet.security ?? {};
  const appq = packet.app ?? {};

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); onDone(); } catch (e: any) { setErr(e?.message ?? String(e)); } finally { setBusy(false); }
  };

  return (
    <section className="space-y-6">
      <button type="button" onClick={onBack} className="text-sm text-zinc-600 hover:underline">← Back to queue</button>
      <ResourceState loading={detail.loading} error={detail.error} empty={false} />
      {d && (
        <>
          <Card className="p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-xl font-bold">{d.name || d.slug || "—"} <span className="text-zinc-400 text-base">v{d.version}</span></h3>
                <p className="text-sm text-zinc-500">{d.slug} · submitted {formatDate(d.submittedAt)}{d.submittedByEmail ? ` · ${d.submittedByEmail}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge tone={priorityTone(d.priority)}>{d.priority}</Badge>
                <Badge tone={d.certificationPassed ? "good" : "bad"}>{d.certificationPassed ? "cert passed" : "cert failed"}</Badge>
                <Badge tone="info">{d.status}</Badge>
              </div>
            </div>
            {d.assignedReviewerEmail && <p className="text-xs text-zinc-500 mt-2">Assigned to {d.assignedReviewerEmail}</p>}
          </Card>

          <Card className="p-5">
            <SectionHeader title="Security & app review packet" subtitle="Submitted by the developer." />
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div className="rounded-2xl border border-zinc-200 p-3">
                <div className="text-xs text-zinc-500 mb-1">Demo video</div>
                {packet.demoVideoUrl ? <a href={packet.demoVideoUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">▶ {packet.demoVideoUrl}</a> : <span className="text-zinc-400">—</span>}
              </div>
              <div className="rounded-2xl border border-zinc-200 p-3">
                <div className="text-xs text-zinc-500 mb-1">Security</div>
                <div className="text-zinc-700">stores platform data: {sec.storesPlatformData ? "yes" : "no"} · encryption transit/rest: {sec.dataEncryptedInTransit ? "✓" : "✗"}/{sec.dataEncryptedAtRest ? "✓" : "✗"}</div>
                <div className="text-zinc-700">certs: {(sec.complianceCertifications ?? []).join(", ") || "—"}</div>
              </div>
            </div>
            {(appq.purpose || appq.dataFlows || appq.testInstructions) && (
              <div className="mt-3 space-y-2 text-sm">
                {appq.purpose && <p><span className="text-zinc-400">Purpose:</span> {appq.purpose}</p>}
                {appq.dataFlows && <p><span className="text-zinc-400">Data flows:</span> {appq.dataFlows}</p>}
                {appq.testInstructions && <p><span className="text-zinc-400">Test instructions:</span> {appq.testInstructions}</p>}
              </div>
            )}
          </Card>

          {err && <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>}
          <Card className="p-5">
            <SectionHeader title="Decision" subtitle="Publish requires a passing certification. Rejection requires notes (emailed to the developer)." />
            <div className="flex flex-wrap items-center gap-3">
              <select
                className="rounded-xl border border-zinc-200 px-3 py-2 text-sm"
                value={d.priority}
                disabled={busy}
                onChange={(e) => act(() => api.setReviewPriority(versionId, e.target.value))}
              >
                <option value="high">high</option>
                <option value="standard">standard</option>
                <option value="low">low</option>
              </select>
              <Button disabled={busy || !d.certificationPassed} onClick={() => act(() => api.publishReview(versionId, undefined))}>Publish</Button>
              <Button variant="outline" disabled={busy} onClick={() => {
                const notes = window.prompt("Rejection notes for the developer:");
                if (notes && notes.trim()) act(() => api.rejectReview(versionId, notes.trim()));
              }}>Reject…</Button>
            </div>
          </Card>
        </>
      )}
    </section>
  );
}

function SlaSettingsCard() {
  const sla = useResource<api.SlaThresholds | null>(api.getReviewSla, null);
  const [draft, setDraft] = useState<api.SlaThresholds | null>(null);
  const [saving, setSaving] = useState(false);
  const v = draft ?? sla.data;
  const set = (k: keyof api.SlaThresholds, val: number) => v && setDraft({ ...v, [k]: val });
  return (
    <Card className="p-5">
      <SectionHeader title="SLA thresholds" subtitle="Hours-in-queue before a submission breaches SLA, per priority." />
      <ResourceState loading={sla.loading} error={sla.error} empty={false} />
      {v && (
        <div className="flex flex-wrap items-end gap-3">
          {(["high", "standard", "low"] as const).map((k) => (
            <label key={k} className="text-sm">
              <div className="text-xs text-zinc-500 capitalize mb-1">{k} (h)</div>
              <input type="number" min={1} className="w-24 rounded-xl border border-zinc-200 px-3 py-2" value={v[k]} onChange={(e) => set(k, Number(e.target.value))} />
            </label>
          ))}
          <Button disabled={saving || !draft} onClick={async () => { setSaving(true); try { await api.setReviewSla(v); sla.refetch(); setDraft(null); } finally { setSaving(false); } }}>Save</Button>
        </div>
      )}
    </Card>
  );
}

function PublishedAppsCard() {
  const apps = useResource<{ apps: api.PublishedApp[] } | null>(api.getPublishedApps, null);
  const [busy, setBusy] = useState<string | null>(null);
  const rows = apps.data?.apps ?? [];
  const run = async (id: string, fn: () => Promise<unknown>) => { setBusy(id); try { await fn(); apps.refetch(); } finally { setBusy(null); } };
  return (
    <Card className="p-5">
      <SectionHeader title="Published apps" subtitle="Catalog hygiene — delist or delete (delete only with zero live installs)." />
      <ResourceState loading={apps.loading} error={apps.error} empty={!apps.loading && rows.length === 0} emptyTitle="No published apps" />
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500"><tr><th className="text-left p-3">App</th><th className="text-left p-3">Publisher</th><th className="text-right p-3">Installs</th><th className="text-right p-3">Actions</th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-t border-zinc-100">
                  <td className="p-3 font-medium">{a.name}<div className="text-xs text-zinc-500">{a.slug} · v{a.currentVersion ?? "—"}</div></td>
                  <td className="p-3 text-zinc-700">{a.publisher}</td>
                  <td className="p-3 text-right tabular-nums">{a.installCount}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button type="button" disabled={busy === a.id} className="text-amber-700 hover:underline mr-3" onClick={() => run(a.id, () => api.delistApp(a.id))}>Delist</button>
                    <button type="button" disabled={busy === a.id} className="text-red-700 hover:underline" onClick={() => { if (window.confirm(`Delete ${a.name}? Only works with zero live installs.`)) run(a.id, () => api.deleteMarketplaceApp(a.id)); }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function AppReviewSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  void currentUser;
  const queue = useResource<api.ReviewQueueResponse | null>(api.getReviewQueue, null);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const rows = queue.data?.queue ?? [];

  if (selected) {
    return <ReviewDetailView versionId={selected} onBack={() => setSelected(null)} onDone={() => { setSelected(null); queue.refetch(); }} />;
  }

  const claim = async (id: string) => { setBusy(id); try { await api.assignReview(id, {}); queue.refetch(); } finally { setBusy(null); } };

  return (
    <section className="space-y-6">
      <Card className="p-5">
        <SectionHeader title="App Review" subtitle="Marketplace submissions awaiting review. Assign, set priority, publish or reject — every decision is audited." />
        <ResourceState
          loading={queue.loading}
          error={queue.error}
          empty={!queue.loading && !queue.error && rows.length === 0}
          emptyTitle="Queue clear"
          emptyBody="No pending submissions."
        />
        {rows.length > 0 && (
          <div className="overflow-x-auto rounded-2xl border border-zinc-200">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-zinc-50 text-zinc-500">
                <tr>
                  <th className="text-left p-3">App</th>
                  <th className="text-left p-3">Priority</th>
                  <th className="text-left p-3">Cert</th>
                  <th className="text-left p-3">In queue</th>
                  <th className="text-left p-3">SLA</th>
                  <th className="text-left p-3">Assignee</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.versionId} className="border-t border-zinc-100">
                    <td className="p-3 font-medium">{v.name}<div className="text-xs text-zinc-500">{v.slug} · v{v.version}</div></td>
                    <td className="p-3"><Badge tone={priorityTone(v.priority)}>{v.priority}</Badge></td>
                    <td className="p-3"><Badge tone={v.certificationPassed ? "good" : "bad"}>{v.certificationPassed ? "passed" : "failed"}</Badge></td>
                    <td className="p-3 text-zinc-600">{v.ageHours}h</td>
                    <td className="p-3"><Badge tone={v.slaBreached ? "bad" : "good"}>{v.slaBreached ? "SLA breach" : "on track"}</Badge></td>
                    <td className="p-3 text-zinc-600">{v.assignedReviewerEmail ?? <span className="text-zinc-400">Unassigned</span>}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {!v.assignedReviewerEmail && <button type="button" disabled={busy === v.versionId} className="text-zinc-900 hover:underline mr-3" onClick={() => claim(v.versionId)}>Claim</button>}
                      <button type="button" className="text-blue-600 hover:underline" onClick={() => setSelected(v.versionId)}>Review</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SlaSettingsCard />
        <PublishedAppsCard />
      </div>
    </section>
  );
}

// ----- Pillar 6A: People / Employee OS -----

function lifecycleTone(s: string): "good" | "info" | "warn" | "default" {
  return s === "active" ? "good" : s === "onboarding" ? "info" : s === "offboarding" ? "warn" : "default";
}

function EmployeeProfileView({ userId, canManage, onBack, onChanged }: { userId: string; canManage: boolean; onBack: () => void; onChanged: () => void }) {
  const prof = useResource<api.EmployeeProfile | null>(() => api.getEmployeeProfile(userId), null, [userId]);
  const templates = useResource<api.OnboardingTemplate[]>(api.getOnboardingTemplates, []);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const p = prof.data;

  const startEdit = () => {
    if (!p) return;
    setDraft({
      title: p.title ?? "", employmentType: p.employmentType ?? "", workLocation: p.workLocation ?? "",
      startDate: p.startDate ? String(p.startDate).slice(0, 10) : "", phone: p.phone ?? "", bio: p.bio ?? "",
    });
    setEditing(true);
  };
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body: Record<string, unknown> = {};
      for (const k of ["title", "employmentType", "workLocation", "startDate", "phone", "bio"]) {
        const v = draft[k]; if (v !== undefined) body[k] = v === "" ? null : v;
      }
      await api.updateEmployeeProfile(userId, body);
      setEditing(false); prof.refetch(); onChanged();
    } catch (e: any) { setErr(e?.message ?? String(e)); } finally { setBusy(false); }
  };
  const act = async (fn: () => Promise<unknown>) => { setBusy(true); setErr(null); try { await fn(); prof.refetch(); onChanged(); } catch (e: any) { setErr(e?.message ?? String(e)); } finally { setBusy(false); } };

  return (
    <section className="space-y-6">
      <button type="button" onClick={onBack} className="text-sm text-zinc-600 hover:underline">← Back to directory</button>
      <ResourceState loading={prof.loading} error={prof.error} empty={false} />
      {p && (
        <>
          <Card className="p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-xl font-bold">{p.fullName}</h3>
                <p className="text-sm text-zinc-500">{[p.title, p.department].filter(Boolean).join(" · ") || p.email}</p>
                <p className="text-xs text-zinc-500 mt-1">{p.email}{p.managerName ? ` · reports to ${p.managerName}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge tone={lifecycleTone(p.lifecycleStatus)}>{p.lifecycleStatus}</Badge>
                <Badge tone={p.active ? "good" : "warn"}>{p.active ? "active" : "inactive"}</Badge>
                {canManage && !editing && <Button variant="outline" onClick={startEdit}>Edit</Button>}
              </div>
            </div>
          </Card>

          {err && <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>}

          <Card className="p-5">
            <SectionHeader title="Profile" subtitle="Employment details." />
            {!editing ? (
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <p><span className="text-zinc-400">Employment type:</span> {p.employmentType ?? "—"}</p>
                <p><span className="text-zinc-400">Location:</span> {p.workLocation ?? "—"}</p>
                <p><span className="text-zinc-400">Start date:</span> {p.startDate ? String(p.startDate).slice(0, 10) : "—"}</p>
                <p><span className="text-zinc-400">Phone:</span> {p.phone ?? "—"}</p>
                <p className="md:col-span-2"><span className="text-zinc-400">Bio:</span> {p.bio ?? "—"}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <label>Title<input className="w-full mt-1 rounded-xl border border-zinc-200 px-3 py-2" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></label>
                <label>Employment type
                  <select className="w-full mt-1 rounded-xl border border-zinc-200 px-3 py-2" value={draft.employmentType} onChange={(e) => setDraft({ ...draft, employmentType: e.target.value })}>
                    <option value="">—</option><option value="full_time">full_time</option><option value="part_time">part_time</option><option value="contractor">contractor</option><option value="intern">intern</option>
                  </select>
                </label>
                <label>Location<input className="w-full mt-1 rounded-xl border border-zinc-200 px-3 py-2" value={draft.workLocation} onChange={(e) => setDraft({ ...draft, workLocation: e.target.value })} /></label>
                <label>Start date<input type="date" className="w-full mt-1 rounded-xl border border-zinc-200 px-3 py-2" value={draft.startDate} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} /></label>
                <label>Phone<input className="w-full mt-1 rounded-xl border border-zinc-200 px-3 py-2" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></label>
                <label className="md:col-span-2">Bio<textarea className="w-full mt-1 rounded-xl border border-zinc-200 px-3 py-2" rows={2} value={draft.bio} onChange={(e) => setDraft({ ...draft, bio: e.target.value })} /></label>
                <div className="md:col-span-2 flex gap-2"><Button disabled={busy} onClick={save}>Save</Button><Button variant="outline" disabled={busy} onClick={() => setEditing(false)}>Cancel</Button></div>
              </div>
            )}
          </Card>

          <Card className="p-5">
            <SectionHeader
              title="Onboarding / Offboarding"
              subtitle="Checklist progress."
              action={canManage ? (
                <select className="rounded-xl border border-zinc-200 px-3 py-2 text-sm" defaultValue="" disabled={busy}
                  onChange={(e) => { const tid = e.target.value; e.target.value = ""; if (tid) act(() => api.startEmployeeChecklist(userId, tid)); }}>
                  <option value="">Start from template…</option>
                  {templates.data.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.kind})</option>)}
                </select>
              ) : null}
            />
            {p.checklists.length === 0 && <p className="text-sm text-zinc-500">No checklists yet.</p>}
            <div className="space-y-4">
              {p.checklists.map((cl) => {
                const done = cl.items.filter((i) => i.status === "done").length;
                return (
                  <div key={cl.id} className="rounded-2xl border border-zinc-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium capitalize">{cl.kind}</span>
                      <Badge tone={cl.status === "complete" ? "good" : "info"}>{done}/{cl.items.length} {cl.status === "complete" ? "complete" : "done"}</Badge>
                    </div>
                    <ul className="space-y-1">
                      {cl.items.map((it) => (
                        <li key={it.id} className="flex items-start gap-2 text-sm">
                          <input type="checkbox" className="mt-1" checked={it.status === "done"} disabled={!canManage || busy}
                            onChange={(e) => act(() => api.toggleChecklistItem(cl.id, it.id, e.target.checked))} />
                          <span className={it.status === "done" ? "line-through text-zinc-400" : ""}>
                            <span className="font-medium">{it.title}</span>{it.description ? <span className="text-zinc-500"> — {it.description}</span> : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </section>
  );
}

function TemplatesPanel() {
  const templates = useResource<api.OnboardingTemplate[]>(api.getOnboardingTemplates, []);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("onboarding");
  const [itemsText, setItemsText] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    const items = itemsText.split("\n").map((s) => s.trim()).filter(Boolean).map((title) => ({ title }));
    if (!name.trim() || items.length === 0) return;
    setBusy(true);
    try { await api.createOnboardingTemplate({ name: name.trim(), kind, items }); setName(""); setItemsText(""); templates.refetch(); } finally { setBusy(false); }
  };
  const remove = async (id: string) => { if (!window.confirm("Delete this template?")) return; setBusy(true); try { await api.deleteOnboardingTemplate(id); templates.refetch(); } finally { setBusy(false); } };
  return (
    <Card className="p-5">
      <SectionHeader title="Checklist templates" subtitle="Reusable onboarding/offboarding checklists." />
      <ResourceState loading={templates.loading} error={templates.error} empty={false} />
      <div className="space-y-3 mb-5">
        {templates.data.map((t) => (
          <div key={t.id} className="rounded-2xl border border-zinc-200 p-3">
            <div className="flex items-center justify-between">
              <span className="font-medium">{t.name} <Badge tone="info">{t.kind}</Badge></span>
              <button type="button" disabled={busy} className="text-red-700 hover:underline text-sm" onClick={() => remove(t.id)}>Delete</button>
            </div>
            <ul className="mt-1 text-xs text-zinc-500 list-disc pl-5">{t.items.map((i) => <li key={i.id}>{i.title}</li>)}</ul>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-3 text-sm border-t border-zinc-100 pt-4">
        <label>Name<input className="w-full mt-1 rounded-xl border border-zinc-200 px-3 py-2" value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label>Kind<select className="w-full mt-1 rounded-xl border border-zinc-200 px-3 py-2" value={kind} onChange={(e) => setKind(e.target.value)}><option value="onboarding">onboarding</option><option value="offboarding">offboarding</option></select></label>
        <label className="md:col-span-2">Items (one per line)<textarea className="w-full mt-1 rounded-xl border border-zinc-200 px-3 py-2" rows={4} value={itemsText} onChange={(e) => setItemsText(e.target.value)} /></label>
        <div><Button disabled={busy} onClick={create}>Create template</Button></div>
      </div>
    </Card>
  );
}

function PeopleSection({ currentUser }: { currentUser: api.CurrentUser | null }) {
  const canManage = currentUser?.permissions?.includes("employee:manage") ?? false;
  const [status, setStatus] = useState("all");
  const [view, setView] = useState<"directory" | "templates">("directory");
  const [selected, setSelected] = useState<string | null>(null);
  const employees = useResource<api.EmployeeRow[]>(() => api.getEmployees(status === "all" ? {} : { status }), [], [status]);
  const rows = employees.data;

  if (selected) return <EmployeeProfileView userId={selected} canManage={canManage} onBack={() => setSelected(null)} onChanged={() => employees.refetch()} />;

  return (
    <section className="space-y-6">
      <Card className="p-5">
        <SectionHeader
          title="People"
          subtitle="Employee directory, profiles, onboarding & offboarding."
          action={
            <div className="flex items-center gap-2">
              <select className="rounded-xl border border-zinc-200 px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="all">All statuses</option><option value="onboarding">Onboarding</option><option value="active">Active</option><option value="offboarding">Offboarding</option><option value="departed">Departed</option>
              </select>
              {canManage && (
                <div className="inline-flex rounded-xl border border-zinc-200 bg-white p-0.5 text-xs font-medium">
                  <button type="button" onClick={() => setView("directory")} className={`px-3 py-1.5 rounded-lg ${view === "directory" ? "bg-zinc-950 text-white" : "text-zinc-600"}`}>Directory</button>
                  <button type="button" onClick={() => setView("templates")} className={`px-3 py-1.5 rounded-lg ${view === "templates" ? "bg-zinc-950 text-white" : "text-zinc-600"}`}>Templates</button>
                </div>
              )}
            </div>
          }
        />
        {view === "directory" && (
          <>
            <ResourceState loading={employees.loading} error={employees.error} empty={!employees.loading && rows.length === 0} emptyTitle="No employees" emptyBody="No employees match this filter." />
            {rows.length > 0 && (
              <div className="overflow-x-auto rounded-2xl border border-zinc-200">
                <table className="w-full text-sm min-w-[820px]">
                  <thead className="bg-zinc-50 text-zinc-500">
                    <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Title</th><th className="text-left p-3">Department</th><th className="text-left p-3">Type</th><th className="text-left p-3">Location</th><th className="text-left p-3">Start</th><th className="text-left p-3">Status</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((e) => (
                      <tr key={e.id} className="border-t border-zinc-100">
                        <td className="p-3 font-medium"><button type="button" className="hover:underline" onClick={() => setSelected(e.id)}>{e.fullName || e.email}</button><div className="text-xs text-zinc-500">{e.email}</div></td>
                        <td className="p-3 text-zinc-700">{e.title ?? "—"}</td>
                        <td className="p-3 text-zinc-700">{e.department ?? "—"}</td>
                        <td className="p-3 text-zinc-700">{e.employmentType ?? "—"}</td>
                        <td className="p-3 text-zinc-700">{e.workLocation ?? "—"}</td>
                        <td className="p-3 text-zinc-600">{e.startDate ? String(e.startDate).slice(0, 10) : "—"}</td>
                        <td className="p-3"><Badge tone={lifecycleTone(e.lifecycleStatus)}>{e.lifecycleStatus}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        {view === "templates" && canManage && <div className="mt-2"><TemplatesPanel /></div>}
      </Card>
    </section>
  );
}

function renderSection(
  active: string,
  currentUser: api.CurrentUser | null,
  tenantDetailId: string | null,
  providerDetailId: string | null,
  searchQuery: string,
) {
  if (tenantDetailId) {
    return <TenantDetailSection tenantId={tenantDetailId} currentUser={currentUser} />;
  }
  if (providerDetailId) {
    return <ProviderConsole providerId={providerDetailId} currentUser={currentUser} />;
  }
  switch (active) {
    case "Tenants": return <TenantsSection currentUser={currentUser} searchQuery={searchQuery} />;
    case "Users & Teams": return <UsersSection currentUser={currentUser} searchQuery={searchQuery} />;
    case "My Team": return <MyTeamSection currentUser={currentUser} />;
    case "Billing": return <BillingSection />;
    case "Coupons": return <CouponsSection currentUser={currentUser} />;
    case "Integrations": return <IntegrationsSection />;
    case "Support Inbox": return <SupportSection currentUser={currentUser} searchQuery={searchQuery} />;
    case "Live Chat": return <LiveChatSection currentUser={currentUser} />;
    case "AI Agent Ops": return <AIAgentSection currentUser={currentUser} />;
    case "Approvals": return <ApprovalsSection currentUser={currentUser} />;
    case "Guardrail Approvals": return <>
      <GuardrailApprovalsSection currentUser={currentUser} />
      <div className="mt-6"><PlatformApprovalsSection currentUser={currentUser} /></div>
    </>;
    case "Security": return <SecuritySection currentUser={currentUser} />;
    case "Policy Simulator": return <PolicySimulatorSection currentUser={currentUser} />;
    case "System Health": return <HealthSection />;
    case "Logs & Errors": return <LogsErrorsSection currentUser={currentUser} />;
    case "Feature Flags": return <FeatureFlagsSection currentUser={currentUser} />;
    case "Announcements": return <AnnouncementsSection />;
    case "Settings": return <SettingsSection currentUser={currentUser} />;
    case "Legal": return <LegalSection />;
    case "Tech Support": return <TechSupportSection />;
    case "Live Agent": return <LiveAgentSection />;
    case "Billing Ops": return <BillingOpsSection />;
    case "Technical": return <TechnicalSection />;
    case "App Review": return <AppReviewSection currentUser={currentUser} />;
    case "People": return <PeopleSection currentUser={currentUser} />;
    default: return <CommandCenter currentUser={currentUser} />;
  }
}

function AdminSecurityFooter({
  currentUser,
  onLogout,
}: {
  currentUser?: api.CurrentUser | null;
  onLogout?: () => void;
}) {
  const auth = useResource<AdminAuth | null>(api.getAdminAuth, null);
  const a = auth.data;
  const summary = a
    ? `${a.mfaEnabled ? "MFA enabled" : "MFA disabled"} · ${a.auditLoggingEnabled ? "Audit logging active" : "Audit logging off"}`
    : auth.loading
    ? "Loading…"
    : "Status unavailable";
  return (
    <div className="mt-auto rounded-2xl bg-zinc-900 p-4 border border-zinc-800 space-y-3">
      {currentUser ? (
        <div>
          <p className="text-sm font-medium truncate">{currentUser.fullName || currentUser.email}</p>
          <p className="text-xs text-zinc-400 truncate">{currentUser.email}</p>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">
            {currentUser.roles.join(" · ")}
          </p>
        </div>
      ) : null}
      <div className="border-t border-zinc-800 pt-3">
        <p className="text-sm font-medium">Admin Security</p>
        <p className="text-xs text-zinc-400 mt-1">{summary}</p>
      </div>
      {onLogout ? (
        <button
          type="button"
          onClick={onLogout}
          className="w-full text-xs font-medium rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-3 py-2 transition"
        >
          Sign out
        </button>
      ) : null}
    </div>
  );
}

// Background ticker for sidebar counts. Polls every 5s and surfaces:
//   - liveChat:   distinct tickets with status === "Live Chat"
//                 AND unread > 0 (customer waiting OR new message)
//   - unread:     tickets with unread > 0 (admin hasn't seen replies)
//   - approvals:  pending approval requests
// We DON'T artificially zero the count for the active section — the
// real fix is server-side markRead when the admin opens an individual
// chat. The flash stops naturally as backend unread counts drop to 0.
const UNREAD_POLL_MS = 5_000;

function useSidebarCounts() {
  const [counts, setCounts] = useState<{ liveChat: number; unread: number; approvals: number }>({
    liveChat: 0,
    unread: 0,
    approvals: 0,
  });

  useEffect(() => {
    if (!api.isApiConfigured()) return;
    let cancelled = false;

    const tick = async () => {
      const ticketsP = api.listTickets().catch(() => [] as Ticket[]);
      const approvalsP = api
        .getPendingApprovalsCount()
        .catch(() => null);
      const [tickets, approvalsRes] = await Promise.all([ticketsP, approvalsP]);
      if (cancelled) return;
      // Distinct unread live-chat conversations — exactly one count per
      // ticket regardless of how many messages are unread inside it.
      const liveChat = tickets.filter(
        (t) => t.status === "Live Chat" && Number(t.unread ?? 0) > 0,
      ).length;
      const unread = tickets.filter((t) => Number(t.unread ?? 0) > 0).length;
      setCounts((prev) => ({
        liveChat,
        unread,
        approvals: approvalsRes ? approvalsRes.count : prev.approvals,
      }));
    };

    void tick();
    const id = window.setInterval(tick, UNREAD_POLL_MS);
    // Refresh immediately when the tab comes back to focus so the count
    // is fresh for the operator without a 5s wait.
    const onVis = () => {
      if (document.visibilityState === "visible") void tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return {
    supportInbox: counts.unread,
    liveChat: counts.liveChat,
    approvals: counts.approvals,
  };
}

function SidebarNav({
  active,
  onSelect,
  navItems,
}: {
  active: string;
  onSelect: (name: string) => void;
  navItems: NavItem[];
}) {
  const counts = useSidebarCounts();
  return (
    <nav className="space-y-1">
      {navItems.map((item) => {
        const selected = active === item.name;
        const flashCount =
          item.name === "Support Inbox"
            ? counts.supportInbox
            : item.name === "Live Chat"
              ? counts.liveChat
              : item.name === "Approvals"
                ? counts.approvals
                : 0;
        const flashing = flashCount > 0 && !selected;
        return (
          <button
            key={item.name}
            onClick={() => onSelect(item.name)}
            className={`relative w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-sm transition ${
              selected ? "bg-white text-zinc-950" : "text-zinc-300 hover:bg-zinc-800"
            }`}
            aria-current={selected ? "page" : undefined}
            aria-label={
              flashCount > 0
                ? `${item.name} (${flashCount} waiting)`
                : item.name
            }
          >
            <Icon name={item.icon} size={18} />
            {item.name}
            {flashCount > 0 && (
              <span
                className={`ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                  selected
                    ? "bg-zinc-900 text-white"
                    : "bg-rose-500 text-white"
                } ${flashing ? "animate-pulse-fast" : ""}`}
              >
                {flashCount}
              </span>
            )}
            {flashing && (
              <span
                className="absolute right-2 top-2 h-2 w-2 rounded-full bg-rose-500 animate-ping"
                aria-hidden="true"
              />
            )}
          </button>
        );
      })}
    </nav>
  );
}

function ConnectionStatusCard({ compact = false }: { compact?: boolean }) {
  const configured = api.isApiConfigured();
  const shellClassName = compact
    ? `inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-medium ${
        configured ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-900"
      }`
    : `rounded-2xl border px-4 py-4 ${
        configured ? "border-emerald-200 bg-emerald-50/80 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"
      }`;

  if (compact) {
    return (
      <div className={shellClassName}>
        <span className={`h-2.5 w-2.5 rounded-full ${configured ? "bg-emerald-500" : "bg-amber-500"}`} />
        {configured ? "Live backend connected" : "Preview mode"}
      </div>
    );
  }

  return (
    <div className={shellClassName}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-semibold">{configured ? "Backend connected" : "Preview mode active"}</p>
          <p className="mt-1 text-sm opacity-80">
            {configured
              ? `Requests are being sent to ${api.API_URL}.`
              : "Set API_URL to fetch live platform data. Until then, use this shell to validate layout, navigation, and content structure."}
          </p>
        </div>
        <code className={`rounded-xl px-3 py-2 text-xs ${configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>
          {configured ? api.API_URL : "API_URL not configured"}
        </code>
      </div>
    </div>
  );
}

export default function IvorycomAdminPortal() {
  // ── Auth gate ────────────────────────────────────────────────────────
  // Single source of truth for "am I logged in?". Renders Login until
  // /auth/me succeeds, then renders the command center. Logout clears
  // the cookie server-side and resets state here.
  const [authState, setAuthState] = useState<"loading" | "authed" | "unauth">("loading");
  // Captured once at mount: the BFF's post-SSO redirect names #mfa-challenge and
  // nothing else may rewrite it before the challenge screen renders.
  const [mfaChallengePending] = useState(
    () => typeof window !== "undefined" && window.location.hash === "#mfa-challenge",
  );
  // Likewise captured once: the emailed sign-in link lands on #signin-link=<token>.
  const [signinLinkToken] = useState(
    () => (typeof window !== "undefined" ? signinLinkTokenFromHash(window.location.hash) : null),
  );
  const [currentUser, setCurrentUser] = useState<api.CurrentUser | null>(null);
  const [authError, setAuthError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    api
      .getCurrentUser()
      .then((user) => {
        if (cancelled) return;
        setCurrentUser(user);
        setAuthState("authed");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const isApiError = err && typeof err === "object" && "status" in err;
        const status = isApiError ? (err as { status: number }).status : 0;
        if (status === 401 || status === 403) {
          setAuthState("unauth");
        } else if (status === 0) {
          setAuthError("Backend is not reachable. Check API configuration.");
          setAuthState("unauth");
        } else {
          setAuthError(`Failed to verify session (HTTP ${status}).`);
          setAuthState("unauth");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch {
      // ignore — even if the server-side clear fails, drop client state.
    }
    setCurrentUser(null);
    setAuthState("unauth");
  };

  // Sidebar onSelect must clear any open drill-in (tenant 360 or provider
  // console) — otherwise renderSection's drill-in early-returns keep
  // showing the previous detail page while the sidebar highlights a
  // different section, and the URL-sync effect skips because a detail
  // id is still set.
  const selectSection = (name: string) => {
    setActive(name);
    setTenantDetailId(null);
    setProviderDetailId(null);
  };

  // Tenant 360° drill-in — when the URL hash is `tenant/<id>`, render
  // the detail page instead of the active section. Tracked separately
  // so the sidebar still highlights "Tenants" while the detail is open.
  const [tenantDetailId, setTenantDetailId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const hash = window.location.hash.replace(/^#/, "");
    return hash.startsWith("tenant/") ? hash.slice("tenant/".length) : null;
  });

  // Provider Operations Console drill-in — same pattern as tenant detail.
  // `#integrations/<providerId>` opens the drill-in while keeping
  // "Integrations" highlighted in the sidebar.
  const [providerDetailId, setProviderDetailId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const hash = window.location.hash.replace(/^#/, "");
    return hash.startsWith("integrations/")
      ? hash.slice("integrations/".length) || null
      : null;
  });

  const [active, setActive] = useState(() => {
    if (typeof window === "undefined") return "Command Center";
    const hash = window.location.hash.replace(/^#/, "");
    if (hash.startsWith("tenant/")) return "Tenants";
    if (hash.startsWith("integrations/")) return "Integrations";
    return sectionFromHashMap[hash] ?? "Command Center";
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Topbar search query — filters Tenants + Users & Teams sections
  // client-side. Server-side filtering is a follow-up (P0 #4 roadmap)
  // when tenant counts cross ~200; today the data fits one page.
  const [searchQuery, setSearchQuery] = useState("");

  // Persona-aware nav. Sourced from currentUser.permissions (from
  // /auth/me). Sections without the right perms drop out of the
  // sidebar AND any deep link to them re-routes to the first
  // visible section. OWNER holds every permission so sees everything.
  const visibleNav = useMemo(
    () => filterNav(nav, currentUser?.permissions ?? []),
    [currentUser?.permissions],
  );

  useEffect(() => {
    // Wait for /auth/me to populate visibleNav before resolving — otherwise
    // a deep-link load (e.g. /#integrations/facebook or /#tenant/<id>)
    // gets demoted to Command Center on the first render because the
    // empty-perm filter has dropped every sidebar item.
    if (visibleNav.length === 0) return;
    const next = resolveActiveSection(active, visibleNav);
    if (next !== active) setActive(next);
  }, [visibleNav, active]);

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURN. The previous version
  // placed these three useEffects AFTER the `authState === "loading"|"unauth"`
  // early returns, so the first (loading) render registered zero post-return
  // hooks but later (authed) renders registered three — React detected the
  // mismatch and threw error #310 ("Rendered more hooks than during the
  // previous render"), blanking out the admin portal after a successful SSO.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [active]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Don't overwrite a tenant/<id> or integrations/<id> hash with the
    // section hash — the detail page owns the URL while it's open.
    if (tenantDetailId || providerDetailId) return;
    // Never touch the URL before the session is known: after Google SSO the BFF
    // lands an MFA-enrolled admin on #mfa-challenge, and rewriting that hash to
    // the section hash here (on the very first render, while /auth/me is still
    // in flight) hid the challenge screen and bounced the admin back to
    // "Sign in with Google" forever — while admins without MFA signed in fine.
    if (authState !== "authed") return;
    const hash = sectionHashMap[active];
    if (!hash) return;
    if (window.location.hash !== `#${hash}`) {
      window.history.replaceState(null, "", `#${hash}`);
    }
  }, [active, tenantDetailId, providerDetailId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const syncFromHash = () => {
      const hash = window.location.hash.replace(/^#/, "");
      if (hash.startsWith("tenant/")) {
        setTenantDetailId(hash.slice("tenant/".length) || null);
        setProviderDetailId(null);
        setActive("Tenants");
        return;
      }
      if (hash.startsWith("integrations/")) {
        setProviderDetailId(hash.slice("integrations/".length) || null);
        setTenantDetailId(null);
        setActive("Integrations");
        return;
      }
      setTenantDetailId(null);
      setProviderDetailId(null);
      const nextSection = sectionFromHashMap[hash];
      if (nextSection) setActive(nextSection);
    };
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, []);

  if (authState === "loading") {
    return <LoginLoading />;
  }
  if (authState === "unauth") {
    // After Google SSO with MFA enabled, the BFF sets admin_mfa_pending
    // and bounces here with #mfa-challenge. /auth/me 401s (no session
    // yet), so we land in unauth — but we should show the MFA prompt
    // rather than ask the user to "Sign in with Google" again.
    if (mfaChallengePending) {
      return (
        <MfaChallenge
          onSuccess={() => {
            window.location.hash = "";
            window.location.reload();
          }}
        />
      );
    }
    if (signinLinkToken) {
      return <SigninLinkRedeem token={signinLinkToken} />;
    }
    return <Login error={authError} />;
  }
  // From here on the user is authenticated. currentUser + handleLogout are
  // available to any nested component via the props passed to AdminSecurityCard.

  // h-screen + overflow-hidden locks the shell to the viewport: the sidebar
  // and each section page scroll INDEPENDENTLY (owner report 2026-08-19 —
  // the sidebar used to scroll away with long pages).
  return <div className="h-screen overflow-hidden bg-zinc-100 text-zinc-950 flex font-sans">
    {mobileNavOpen && <div className="fixed inset-0 z-40 bg-zinc-950/55 backdrop-blur-sm lg:hidden" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />}

    <aside className={`fixed inset-y-0 left-0 z-50 w-[86vw] max-w-72 bg-zinc-950 p-5 text-white transition-transform duration-200 lg:hidden ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}>
      <div className="flex h-full flex-col">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={logoUrl} alt="Ivorycom" className="h-11 w-11 rounded-2xl object-cover bg-zinc-900" />
            <div>
              <h1 className="font-semibold text-lg">Ivorycom Admin</h1>
              <p className="text-xs text-zinc-400">Business Command Center</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            className="rounded-2xl border border-zinc-800 p-2 text-zinc-300 hover:bg-zinc-800"
            aria-label="Close navigation"
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
          <SidebarNav active={active} onSelect={selectSection} navItems={visibleNav} />
        </div>
        <AdminSecurityFooter currentUser={currentUser} onLogout={handleLogout} />
      </div>
    </aside>

    <aside className="w-72 bg-zinc-950 text-white p-5 hidden lg:flex flex-col h-full shrink-0">
      <div className="flex items-center gap-3 mb-8"><img src={logoUrl} alt="Ivorycom" className="h-11 w-11 rounded-2xl object-cover bg-zinc-900" /><div><h1 className="font-semibold text-lg">Ivorycom Admin</h1><p className="text-xs text-zinc-400">Business Command Center</p></div></div>
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
        <SidebarNav active={active} onSelect={selectSection} navItems={visibleNav} />
      </div>
      <AdminSecurityFooter currentUser={currentUser} onLogout={handleLogout} />
    </aside>

    <main className="flex-1 h-full min-w-0 p-4 lg:p-8 overflow-y-auto">
      <header className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="mt-1 inline-flex rounded-2xl border border-zinc-200 bg-white p-2 text-zinc-700 shadow-sm lg:hidden"
            aria-label="Open navigation"
          >
            <Icon name="menu" size={18} />
          </button>
          <div>
            <h2 className="text-3xl font-bold">{active}</h2>
            <p className="text-zinc-500 mt-1">Manage customers, billing, support, integrations, AI agents, and platform health.</p>
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <ConnectionStatusCard compact />
          {SEARCHABLE_SECTIONS.has(active) && (
            <div className="relative w-full md:w-auto">
              <Icon name="search" className="absolute left-3 top-3 text-zinc-400" size={17} />
              <input
                className="pl-10 pr-4 py-2.5 rounded-2xl border border-zinc-200 w-full md:w-72 outline-none bg-white"
                placeholder={
                  active === "Users & Teams"
                    ? "Search admins by name, email, or role…"
                    : active === "Support Inbox"
                      ? "Search tickets by customer, subject, or owner…"
                      : "Search tenants by name, owner, or plan…"
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          )}
          <Button><Icon name="bell" size={17} className="mr-2"/>Alerts</Button>
        </div>
      </header>

      {!api.isApiConfigured() && (
        <div className="mb-6">
          <ConnectionStatusCard />
        </div>
      )}

      <SectionErrorBoundary
        sectionLabel={
          providerDetailId
            ? "Provider console"
            : tenantDetailId
            ? "Tenant detail"
            : active
        }
      >
        {renderSection(active, currentUser, tenantDetailId, providerDetailId, searchQuery)}
      </SectionErrorBoundary>
    </main>
  </div>;
}

// Wraps the active section so a render-time crash (e.g. a missing field on
// a backend response) shows a recoverable error card instead of unmounting
// the whole admin portal — exactly the failure mode that turned
// /#users-teams into a black screen on Prod.
class SectionErrorBoundary extends React.Component<
  { sectionLabel: string; children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidUpdate(prev: { sectionLabel: string }) {
    if (prev.sectionLabel !== this.props.sectionLabel && this.state.error) {
      this.setState({ error: null });
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800">
          <p className="font-semibold">Something went wrong loading {this.props.sectionLabel}.</p>
          <p className="mt-1 text-red-700">{this.state.error.message}</p>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="mt-3 inline-flex items-center rounded-2xl border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-800 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
